/*
 * Fork of assets/babyset-builder.js, md5 c34e6a592a8075dd666559787bdf803d (v1 after KARLO-DECISIONS §9, its fix rounds 1–2 and §11–§12, as on the dev live theme #205350502748 at 2026-10-03T01:12Z). Babyset builder v2 (work/ux-v2/V2-SPEC.md); work/v2/BUILD-LOG.md says which build steps are in.
 */
/*
 * Babyset builder v2 ("Složi svoj baby set" – korak po korak). Pairs with sections/babyset-builder-v2.liquid; the custom
 * element babyset-builder-v2 lives next to v1 (babyset-builder, assets/babyset-builder.js), never on the same page.
 *
 * The flow (V2-SPEC §0, §2): the start screen (the baby's age = the set size, who picks the colours, "Za koga je set?")
 * → one part at a time ("Korak N od M", the stepper, the bar's step button, one history entry per step: #korak-N,
 * #pregled) → Pregled → v1's add. One button per card, "Dodaj u set": the piece goes in at once when nothing is missing,
 * otherwise the piece window (a <dialog>: a bottom sheet on phones, a centred dialog on desktop) asks for what is
 * missing with v1's controls, which the window holds (each card's config block is moved into it). Nothing is
 * preselected where the customer has a real choice (SD3); only the window's button adds (SD4).
 *
 * The rules of the set are v1's, in v1's methods kept verbatim (the model, sizes, "Boje bira Šigureca", the gift,
 * the totals, the photos and the cart: cartItems is the payload, identical to v1's). v1's layouts, presets,
 * formula, palette button and "Dodaj u set asks for a colour" path are removed. The method lists are in V2-SPEC §1.5;
 * work/v2/BUILD-LOG.md says what each build step has done.
 *
 * v1's own description follows (its rules still hold where the methods are kept).
 */
/*
 * Babyset builder ("Složi svoj baby set" – Vi birate, ja slažem).
 * Pairs with sections/babyset-builder.liquid. No dependencies; works with Dawn's markup/classes.
 *
 * Her rules: exactly one dekica (puffy or muslin) is required; every other part is optional and can hold several
 * pieces (the part's "Najviše komada"). Above the gift threshold (80 € by default) the scrunchie for mum is free:
 * the customer picks its colour and it is saved on the set's dekica line ("Poklon za mamu: Gumica – Mint") plus a
 * cart attribute. assets/babyset-cart.js (window.BabysetCart) then keeps the cart honest: a set that drops to
 * the threshold or below in the cart loses the gift, and the attribute lists the gifts of the sets in the cart.
 *
 * Products of a part come from the part's JSON next to its cards (snippets/babyset-slot.liquid: the block's own
 * products, then its collection). Fields of a piece ("Izaberi slovo", "Izaberi boje veza", "Odaberi print" – the same
 * as her product pages) are read from the card (snippets/babyset-card.liquid); their values go to the cart as
 * visible line-item properties named like the field. Typing or choosing in a field of a piece that is not in the
 * set yet puts it in the set (when the part has room).
 *
 * State per section:
 *   size       – normalised global size key ("mo:3-6") or null
 *   selection  – slotId -> [product handle, …] in the order the pieces were added
 *   skipped    – slots skipped with "Preskoči" (layout "koraci")
 *   cards      – one entry per product card:
 *                { slotId, handle, product, variant, override, near, reason, shown, fields }
 *                fields = [{ id, name, kind, required, input, value, … }] of the card ("Izaberi slovo", …)
 *   giftChoice – colour of the scrunchie for mum (used while the set value is above the threshold)
 *
 * Only "Dodaj u set" (the card's input) puts a piece in the set; changing a card's size or colour only changes
 * that card. Every picture of a piece (card, bar, hero, board, lists) is the same photo: the card's "shown" image
 * – a variant's own photo when it has one, otherwise the photo shown before. Exception: a colour option where only
 * some colours have their own photo – a colour without one shows a neutral colour tile ("swatch").
 *
 * The set size: one size for all pieces; a product without exactly that size gets the closest range that
 * contains it ("Najbliža veličina: 0–6 mjeseci" for 3–6 mjeseci). Only age sizes count (months / years): an option
 * "Veličina" with 65 × 65 cm (tetra pelene) is an ordinary option and never "Nema u veličini …". A new size that takes chosen pieces out of the
 * set says so in the bar/sidebar with "Poništi".
 * Back from the cart: a page restored from the back/forward cache is made usable again (pageshow), and a page that
 * reloads instead gets the last set back from sessionStorage (per tab, only on Back).
 *
 * The percentage set discount of the first demo has no settings any more (always 0 %); its code stays inert.
 *
 * Since 29. 9. 2026 (Marta):
 * - Gift: the customer no longer picks the scrunchie's colour (section "Kupac bira boju gumice" off, no chips): above
 *   the threshold the dekica line says "Poklon za mamu: Gumica S (boju bira Šigureca)" (the gift name) – the same
 *   property, threshold and cart attribute as before, so assets/babyset-cart.js works unchanged.
 * - "Tko bira boje?" (colourMode, layout redovi): "Boje neka izabere Šigureca" hides the colour buttons of products
 *   whose only option is a colour (a piece keeps any available variant – its line then says "Boje: bira Šigureca
 *   (navedena boja nije odabrana)") and the colour / print fields ("Izaberi boje veza", "Odaberi print"); "Za" (Dječak /
 *   Djevojčica) and "Veličina za cijeli set" (moved into the box) are required. Every line of the set gets "Za" and
 *   "Veličina seta"; "Boje" only where the card says "Boju / Print bira Šigureca" (a colour option, or colour / print
 *   fields shown with the piece's variant; since 1. 10. 2026, D17). The normal mode ("Ja biram boje") is the default.
 * - "Više o proizvodu" on every card: a modal <dialog> with the description, the photos and a link to the product
 *   page; Esc, × and a click outside close it and the focus goes back to the card's button.
 * - Tag babyset-glavna-fotografija: the card starts on the product's main photo until a colour is picked.
 *
 * Since 30. 9. 2026 (Marta's yarn palettes, 34 colours "Boja 530" … on the puffy pieces): a colour option with more
 * than 8 values and photos is a palette – the card has a button with the chosen swatch and name ("Boja 530 · 34 boje
 * – odaberite") that opens a <dialog> of swatch tiles (bindSwatchPicker). "Boje neka izabere Šigureca" hides it like
 * the pills. The product window leaves the swatch photos out (only the product photos).
 * A product with a colour and an age size (trakica "Boja / Veličina"): in "Boje neka izabere Šigureca" its list shows
 * only the sizes ("0–3 mjeseca" …, value "g:<size>"); a size picks any available variant of that size (the current
 * colour if it has one), "Veličina za cijeli set" ignores the colour, the line gets "Boje: bira Šigureca (navedena
 * boja nije odabrana)" and the set lists show the size only. "Ja biram boje" keeps the combined list (colourSized,
 * sizeGroups).
 *
 * Since 30. 9. 2026, evening (Marta: "ostavi sliku originalnu prije odabira boje"; Karlo):
 * - Tag babyset-glavna-fotografija: the card (and the set lists) always show the product's main photo; the swatch is
 *   only on the palette button and in its window. A palette product with the tag starts without a colour
 *   ("Odaberi boju (34 boje)"); "Dodaj u set" then asks for one ("Odaberi boju" on the card, focus on the
 *   colour button) and the piece goes into the set as soon as a colour is picked (pickColour).
 * - "Šigureca bira" is a real colour value (variant) of the products whose colour Marta picks: never a button or
 *   tile; in "Boje neka izabere Šigureca" the piece is exactly that variant (trakica: the one of its size), so the
 *   order says "Boja: Šigureca bira" and the line needs no "Boje: … (navedena boja nije odabrana)" (martaFor).
 *   Products without such a variant keep the old behaviour (any available variant + that property).
 * - Colour + age size with two options (trakica): two pickers (colour buttons + size list, value "g:<size>"),
 *   merged into the variant (card.splitColour / card.splitSize, product.split).
 * - Care (Karlo): the care symbols picture is no longer a product photo. The window's description of a product
 *   tagged odrzavanje-puffy already holds its care section – bold "Održavanje:", the list and the symbols picture in
 *   one panel (snippets/odrzavanje-simboli.liquid, assets/odrzavanje.css) – the same as on the product page.
 *
 * Since 2. 10. 2026 (Karlo, KARLO-DECISIONS §9.3–§9.6):
 * - "Dodaj u set" never moves the page. The photo, the title and "Dodaj u set" are <label>s of the card's visually
 *   hidden input at the card's top; a browser's label click focused that input and scrolled it into view (450–700 px
 *   up on a tall card). The builder now does the label's work itself (onLabelClick): the input gets the focus without
 *   scrolling and is clicked, so Tab, Space / Enter, screen readers and the dekica's radios work as before. Anything
 *   a click changes above the card is kept from moving it (keepInPlace; the part's message is under its cards now).
 * - "Dodaj u set" without a needed colour ("Ja biram boje"): a palette card (puffy) opens its colour window at once and
 *   the piece goes into the set with the colour picked there (wantsAdd); the window closes and the focus is back on
 *   "U setu". A card with colour buttons (trakica, majica) says "Odaberi boju" at its buttons, brings them into view
 *   only when they are not, and focuses the first one; the piece goes in with the colour. Closed without a colour:
 *   nothing is added and the card is as it was before "Dodaj u set".
 * - Fix round after the QA of 2. 10. 2026: the colour window ignores clicks, taps and Enter / Space in its first
 *   SWATCH_GUARD_MS (the second half of a double click / tap on "Dodaj u set" chose a colour nobody picked, or closed
 *   the window at once). In the window "Dodaj u set" opened, the arrow keys only move the choice; Enter / Space (Space
 *   picks the focused tile) or a tile put the piece in; Esc, × or the dimmed page leave the card as it was – no colour,
 *   nothing added, no "Odaberi boju" (finishAddWindow), the focus back on its "Dodaj u set". An arrow key onto a
 *   dekica without a colour only says "Odaberi boju" (no window: WCAG 3.2.2). A piece chosen in a part of one piece
 *   ends the other cards' "Odaberi boju" (dropOtherAsks).
 * - Fix round 2 (re-check of 2. 10. 2026): the guard judges a click or tap by when its press started. A press that
 *   starts in the window's first SWATCH_GUARD_MS is dropped however late it is let go (a press from 0.4 s let go at
 *   0.55 s chose a colour; on touch the click comes only after the finger is lifted).
 * - The set bar also has "Tko bira boje?" and "Veličina za cijeli set" (initBarControls): one state with the controls
 *   at the top, the same code (setColourMode, applySize with its notice and "Poništi"). Sizes chosen per piece show
 *   as "Po komadu".
 * - "Šigureca bira" at the nearest size says "Najbliža veličina" (setSizeFit); after Back from the cart the customer's
 *   own colour of a piece in "Boje neka izabere Šigureca" comes back too (saveState: own, colours).
 *
 * Since 2. 10. 2026, evening (KARLO-DECISIONS §11, PF3):
 * - The colour buttons of a colour + size card (trakica, long-sleeve majica) that "Dodaj u set" brought into view
 *   (askColour) drop a click or tap whose press starts in their first SWATCH_GUARD_MS, however late it is let go
 *   (bindColourGuard, the same rule as the colour window's): the first press of a double click / tap scrolled the
 *   buttons under the pointer and the second one chose a colour nobody picked. Keyboard presses are unchanged.
 * - The colour buttons may show a small picture of the colour before its name (the variant metafield
 *   custom.slika_boje, rendered by snippets/babyset-card.liquid); nothing in the script changes for it, and the card's
 *   photo stays the product's or the colour's own photo.
 *
 * Since 22. 10. 2026 (Karlo, the ideas for Marta after the first two weeks):
 * - „Gotovi setovi” (blocks „Gotovi set” on the start screen): „Odaberi ovaj set” puts the block's pieces in the set
 *   (the set before is replaced, „Poništi” brings it back) and opens Pregled, where a piece without its colour says
 *   „Odaberi boju” and „Uredi” opens its window (applyPreset). The tile shows the pieces' price („od …”) and whether
 *   the gift for mum comes with it (updatePresets).
 * - „Složi u set” on a product page (snippets/slozi-u-set.liquid) opens the builder with ?komad=<handle>
 *   [&varijanta=<id>]: that piece is in the set on load, with the colour picked on the product page (applyLinkedPiece).
 * - „Poruka za čestitku” in Pregled (optional, [data-card-note]): goes with the set's first piece like the gift.
 * - Praćenje: babyset_started and babyset_added_to_cart say where the set started – step = the „Gotovi set” block's id
 *   or „proizvod” (+ product = the handle) – in the fields the app already keeps (no other data).
 *
 * Since 22. 10. 2026, late (Karlo; Marta's weekend sale: −15 % on every piece, −20 % on the ready sets):
 * - „Skriveni kod za gotove setove” (data.presetCode): after a successful add, a set that holds every piece of a
 *   „Gotovi set” (any colours, extra pieces allowed) gets that code on the cart (putCode, like the set coupon); any other
 *   set gets nothing (the −15 % is an automatic discount in Shopify). The code is never shown; its text („−20 % ovaj
 *   vikend”) is on the tiles, in the preset notice and above the add button while the set matches – only from the first
 *   to the last day in shop time (presetDealActive). A swapped or removed piece turns the line into „Set više nije cijeli
 *   „…“ – … vrijedi samo za cijeli gotov set.”; a ready set missing a piece in the chosen size (the body at 0–6 months)
 *   or sold out says which piece in its notice (presetMissingText).
 * - ?gotovi=<naziv-seta> (or the block id without „gotovi_”) opens that ready set in Pregled on load (applyLinkedPreset);
 *   the parameter leaves the address, utm_* stay. Tracker step = the block id + „_link”.
 *
 * Set coupon ("Kupon za set", e.g. <kod> – a discount code made in Shopify → Discounts): while it is on and not
 * past its last day (checked again with the customer's clock), a line in the set bar says "Kod <kod> (−15 %) –
 * automatski na blagajni." After a successful add the code goes on the cart (applyCoupon: Ajax Cart
 * API, POST /cart/update.js { discount }) and the bar says "Set je u košarici · kod <kod> bit će primijenjen"
 * before the cart opens. A code that cannot be applied is only logged (console.debug) – the set is in the cart
 * either way.
 */
(() => {
  if (customElements.get('babyset-builder-v2')) return;

  const NBSP = '\u00a0';
  const MINUS = '\u2212';
  const SIZE_OPTION_RE = /^(veli[cč]in[aeiu]?|size|sizes|grö(ß|ss)e|taglia)$/i;
  const COLOUR_OPTION_RE = /^(boj[aeiu]?|boje|colou?rs?|farben?|uzor(ak|ci)|print|pattern)$/i;
  // v2's own saved state (sessionStorage key babyset2:…): another version is a fresh start
  const STORAGE_VERSION = 'v2.1';
  // the one tap (V2-SPEC §2.7): a pointer pick in a one-choice window adds the piece this long after it shows as chosen
  const ONE_TAP_MS = 180;
  // a window's first moments: the second half of a double tap / click on "Dodaj u set" lands in it and must not choose
  // a tile or close the window (guardSheetPresses; v1's SWATCH_GUARD_MS)
  const SHEET_GUARD_MS = 500;
  // "Vezica za dudu – Obična / Personalizirana": the fields belong to the personalised variant only
  const PERSONAL_VARIANT_RE = /personaliz/i;
  // The colour value Marta picks in "Boje neka izabere Šigureca" (a real variant, never shown as a colour)
  const MARTA_VALUE_RE = /^\s*šigureca\s+bira\s*$/i;
  // "Izaberi boje veza", "Odaberi print": fields that Šigureca fills in herself in "Boje neka izabere Šigureca"
  const COLOUR_FIELD_RE = /boj|print/i;
  // How full the gift meter may look while the gift is still locked (80,00 € is not "above 80 €").
  const LOCKED_MAX_RATIO = 0.96;
  const LETTER_SIZES = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xxxxl'];
  const YEAR_UNITS = /^(god|godina|godine|g|y|yr|yrs|year|years)$/;
  const UNIT_RE =
    /^(\d+)(?:\s*[-/]\s*(\d+))?\s*-?\s*(mj|mjes|mjesec|mjeseca|mjeseci|m|mo|mth|months?|god|godina|godine|g|y|yrs?|years?)\.?$/;
  const DESKTOP = '(min-width: 990px)';
  const MAX_THUMBS = 4; // set bar: 4 pictures + "+N" (more squeezes the gift progress next to them)
  // A modifier pressed on its own is not keyboard use (Alt+Tab is: Safari's Tab to every control).
  const MODIFIER_KEYS = new Set([
    'Alt',
    'AltGraph',
    'CapsLock',
    'Control',
    'Fn',
    'FnLock',
    'Hyper',
    'Meta',
    'NumLock',
    'OS',
    'ScrollLock',
    'Shift',
    'Super',
    'Symbol',
    'SymbolLock',
  ]);
  // Sizes of the small pictures of a piece (the srcset comes from the product data, so 2x/3x screens stay sharp).
  const SIZES = {
    thumb: '(min-width: 990px) 48px, 36px',
    formula: '(min-width: 750px) 90px, 54px',
    tile: '(min-width: 990px) 300px, 70px',
    list: '56px',
  };

  /** Croatian plural: 1 -> one, 2-4 -> few, else many (11-14 are "many"). */
  function plural(n, one, few, many) {
    const n10 = n % 10;
    const n100 = n % 100;
    if (n10 === 1 && n100 !== 11) return one;
    if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
    return many;
  }

  function pieces(n) {
    return `${n}${NBSP}${plural(n, 'komad', 'komada', 'komada')}`;
  }

  function capitalize(text) {
    const value = String(text || '');
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /** Keep "12,50 €" together: the euro sign never wraps onto its own line. */
  function glueCurrency(text) {
    return String(text || '').replace(/\s+€/g, `${NBSP}€`);
  }

  let EMOJI_RE = null;
  try {
    EMOJI_RE = new RegExp('\\s*\\p{Extended_Pictographic}\\uFE0F?', 'gu');
  } catch (error) {
    EMOJI_RE = null;
  }

  /** "Gumica za mamu je gratis 🤍" -> "Gumica za mamu je gratis" for the screen-reader summary. */
  function stripEmoji(text) {
    return EMOJI_RE ? String(text).replace(EMOJI_RE, '') : String(text);
  }

  function escapeRegExp(text) {
    return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Characters as the customer sees them ("🧸" or "👨‍👩‍👧" is one), not UTF-16 code units – for the field limit
  // and its counter ("52/60 znakova").
  let SEGMENTER = null;
  try {
    SEGMENTER = typeof Intl !== 'undefined' && Intl.Segmenter ? new Intl.Segmenter('hr', { granularity: 'grapheme' }) : null;
  } catch (error) {
    SEGMENTER = null;
  }

  function charsOf(text) {
    const value = String(text || '');
    if (SEGMENTER) return [...SEGMENTER.segment(value)].map((part) => part.segment);
    return Array.from(value);
  }

  function charCount(text) {
    return charsOf(text).length;
  }

  function truncateChars(text, max) {
    const chars = charsOf(text);
    return chars.length > max ? chars.slice(0, max).join('') : String(text || '');
  }

  // Shopify's name of the only variant of a product without options ("Default Title"): never shown.
  const DEFAULT_VARIANT_RE = /^default title$/i;

  /**
   * Normalise a size value so "3-6-mjeseci", "3-6 mjeseci" and "3 – 6 mj" match, and sort naturally
   * (months and years on one scale, 50/56 < 62/68, XS < S < M, numbers numerically).
   * Age sizes also get their range in months, so "0-6 mjeseci" can stand in for "3-6 mjeseci".
   */
  function parseSize(raw) {
    const text = String(raw == null ? '' : raw).trim();
    const s = text.toLowerCase().replace(/[–—_]/g, '-').replace(/\s+/g, ' ');

    const age = s.match(UNIT_RE);
    if (age) {
      const isYear = YEAR_UNITS.test(age[3]);
      const factor = isYear ? 12 : 1;
      const from = Number(age[1]);
      const to = age[2] != null ? Number(age[2]) : null;
      const last = to == null ? from : to;
      const range = to == null ? `${from}` : `${from}–${to}`;
      const word = isYear ? plural(last, 'godina', 'godine', 'godina') : plural(last, 'mjesec', 'mjeseca', 'mjeseci');
      return {
        key: `mo:${from * factor}-${last * factor}`,
        label: `${range} ${word}`,
        short: `${range} ${isYear ? 'god.' : 'mj.'}`,
        sort: [1, from * factor, last * factor, ''],
        range: [from * factor, last * factor],
      };
    }

    const numeric = s.match(/^(\d+)(?:\s*[-/]\s*(\d+))?\s*(cm)?$/);
    if (numeric) {
      const from = Number(numeric[1]);
      const to = numeric[2] != null ? Number(numeric[2]) : from;
      return { key: `n:${from}-${to}`, label: text, short: text, sort: [0, from, to, ''], range: null };
    }

    const letter = s.replace(/\s/g, '').replace(/^(\d)xl$/, (_, n) => 'x'.repeat(Number(n)) + 'l');
    const letterIndex = LETTER_SIZES.indexOf(letter);
    if (letterIndex !== -1) {
      return {
        key: `l:${letter}`,
        label: text.toUpperCase(),
        short: text.toUpperCase(),
        sort: [2, letterIndex, 0, ''],
        range: null,
      };
    }

    return { key: `t:${s}`, label: text, short: text, sort: [3, 0, 0, s], range: null };
  }

  function compareSort(a, b) {
    for (let i = 0; i < 3; i += 1) {
      if (a[i] !== b[i]) return a[i] - b[i];
    }
    return a[3].localeCompare(b[3], 'hr', { numeric: true });
  }

  function randomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID().slice(0, 8);
    }
    return Math.random().toString(36).slice(2, 10);
  }

  /** Same line item properties (a cart line from /cart.js vs. what the builder would send), in any key order. */
  function sameProperties(a, b) {
    const entries = (properties) =>
      Object.entries(properties || {})
        .filter(([, value]) => value != null && value !== '')
        .map(([key, value]) => `${key}\u0000${String(value)}`)
        .sort();
    return JSON.stringify(entries(a)) === JSON.stringify(entries(b));
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // dev-test-14 (A2): the pause after an add, so the card's "U setu" shows before the next step
  const ADVANCE_MS = 600;
  // dev-test-14 (A2): the stepper's mark of a skipped part – a curved arrow over a dot ("jumped over")
  const SKIP_ICON =
    '<svg class="bs2-stepper__skip" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
    '<path d="M2.6 9.6a5.6 5.6 0 0 1 10.3-2.4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '<path d="M13.4 3.9v3.6H9.8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<circle cx="8" cy="12.4" r="1.3" fill="currentColor"/></svg>';

  // dev-test-14 (F): Marta's tetra tiers (pieces, %), the same as the automatic discounts; the stepper's top
  const QTY_TIERS = [[3, 5], [6, 10], [9, 15]];
  const QTY_MAX = 99;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * The discount codes on a cart JSON (/cart.js, /cart/update.js): [{ code, applicable }]. From discount_codes; an
   * answer without that list gives the codes that apply (discount applications of type "discount_code").
   */
  function cartCodes(cart) {
    if (cart && Array.isArray(cart.discount_codes)) {
      return cart.discount_codes
        .filter((entry) => entry && entry.code)
        .map((entry) => ({ code: String(entry.code), applicable: entry.applicable !== false }));
    }
    const codes = new Map();
    const add = (application) => {
      if (!application || application.type !== 'discount_code' || !application.title) return;
      const code = String(application.title);
      codes.set(code.toLowerCase(), code);
    };
    ((cart && cart.cart_level_discount_applications) || []).forEach(add);
    ((cart && cart.items) || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => add(allocation && allocation.discount_application));
    });
    return [...codes.values()].map((code) => ({ code, applicable: true }));
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  /**
   * Today as "YYYY-MM-DD" in the shop's time zone, by the customer's clock. offsetMinutes = the shop's UTC offset
   * when the page was rendered ("+0200" -> 120); without it the customer's own time zone counts.
   */
  function shopToday(offsetMinutes) {
    const now = new Date();
    if (offsetMinutes == null) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    }
    return new Date(now.getTime() + offsetMinutes * 60000).toISOString().slice(0, 10);
  }

  /** „Set s tetra pelenom” -> "set-s-tetra-pelenom" (lower case, no diacritics, dashes): the ?gotovi= value. */
  function presetSlug(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/đ/g, 'd')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /** Put one photo ({id, src, srcset}) into a host element; does nothing when it is already there. */
  function setImage(host, image, sizes) {
    if (!host) return;
    const key = image ? String(image.id || image.src) : '';
    if (host.dataset.src === key) return;
    host.dataset.src = key;
    host.textContent = '';
    if (!image || !image.src) return;
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    if (image.srcset) {
      img.sizes = sizes || '100vw';
      img.srcset = image.srcset;
    }
    img.src = image.src;
    host.append(img);
  }

  /*
   * dev-test-19 (Karlo 4. 10. 2026, „make it sync”): the fields of a piece follow Marta's EasyFlow fields (Product
   * Options by EasyFlow, its app block on her product pages), so she edits them in one place.
   *
   * The card says data-easyflow="1" when the product has EasyFlow's metafield product_options.options (snippets/
   * babyset-card-v2.liquid). The builder then reads the product page itself (its main section through the Section
   * Rendering API; the first product of a template reads the whole page once to learn that section's id) – EasyFlow
   * renders its fields there server-side – and parses every .ef__product-option-root with DOMParser (parseEasyflow).
   * The fields are drawn with the builder's own field markup (the look of the "Polje uz proizvod" blocks) and go to
   * the cart under EasyFlow's exact names (data-option-in-cart-name, else data-option-title, untrimmed), so an order
   * from the builder reads like one from her product page. A field shows only with a variant of its root's
   * data-variant-ids, and EasyFlow's conditional rules (show / hide, on EasyFlow or Shopify options) are evaluated as
   * EasyFlow's common.js V2.1.6 does (efHiddenMap).
   *
   * Anything else – no root, no field, a field type or rule this code does not know, a network error, 5 s without an
   * answer – leaves the card on the builder's own field blocks (page.babyset.json), with a console warning where
   * EasyFlow's markup was not understood. EasyFlow prices (data-value-price > 0) never reach the cart: our prices are
   * in the variants; such a value only logs a warning.
   */
  // Supported EasyFlow field types (data-option-type) -> the builder's kinds
  const EF_TEXT_TYPES = new Set(['Text box', 'Multi-line text box']);
  const EF_CHOICE_TYPES = new Set(['Radio button', 'Button', 'Dropdown', 'Color swatch', 'Image swatch']);
  // a text field without EasyFlow's maxlength: the builder still caps what goes into the order
  const EF_TEXT_MAX = 150;
  // how long a window waits for the product page before it opens with the builder's own fields
  const EF_TIMEOUT_MS = 5000;

  function efText(node) {
    return node ? String(node.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  /** EasyFlow's price of a value in cents (data-value-price); 0 when empty or not a number. */
  function efPrice(node) {
    const value = Number.parseFloat(node ? node.getAttribute('data-value-price') || '' : '');
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * One .ef__product-option -> a field description, or null for content without a value (EasyFlow's HTML blocks).
   * Problems (unknown markup: the whole product falls back) and warnings (ignored details) are pushed to `out`.
   */
  function parseEasyflowOption(option, out) {
    const title = option.getAttribute('data-option-title');
    const type = String(option.getAttribute('data-option-type') || '').trim();
    const inputs = [...option.querySelectorAll('.ef__product-option-input')];
    if (!inputs.length) {
      if (option.querySelector('.ef__html-content')) {
        out.warnings.push(`„${title || type}”: sadržaj bez polja (preskočeno)`);
        return null;
      }
      out.problems.push(`„${title || type}”: polje bez unosa`);
      return null;
    }
    if (!title || !title.trim()) {
      out.problems.push(`polje bez naslova (${type || 'nepoznata vrsta'})`);
      return null;
    }
    const optionId = String(inputs[0].getAttribute('data-option-id') || '').trim();
    if (!optionId) {
      out.problems.push(`„${title}”: nema data-option-id`);
      return null;
    }
    const name = title.replace(/\s+/g, ' ').trim();
    const field = {
      optionId,
      // the cart property: exactly EasyFlow's (common.js: data-option-in-cart-name || data-option-title)
      cartName: option.getAttribute('data-option-in-cart-name') || title,
      name,
      type,
      kind: '',
      required: option.getAttribute('data-is-required') === 'true',
      max: 0,
      hint: '',
      choices: [],
    };
    const description = efText(option.querySelector('.ef__option-description'));
    if (inputs.some((input) => efPrice(input) > 0)) {
      out.warnings.push(`„${name}”: EasyFlow doplata se ne dodaje (cijene su u varijantama)`);
    }
    if (EF_TEXT_TYPES.has(type)) {
      const input = inputs[0];
      const tag = input.tagName.toLowerCase();
      if (inputs.length !== 1 || !(tag === 'textarea' || (tag === 'input' && (input.getAttribute('type') || 'text') === 'text'))) {
        out.problems.push(`„${name}”: neočekivani unos za ${type}`);
        return null;
      }
      const max = Number.parseInt(input.getAttribute('maxlength') || '', 10);
      field.kind = 'text';
      field.max = max > 0 ? max : EF_TEXT_MAX;
      const min = Number.parseInt(input.getAttribute('minlength') || '', 10);
      if (min > 1) out.warnings.push(`„${name}”: najmanji broj znakova (${min}) builder ne provjerava`);
      field.hint = description || String(input.getAttribute('placeholder') || '').replace(/\s+/g, ' ').trim();
      return field;
    }
    if (EF_CHOICE_TYPES.has(type)) {
      const group = option.querySelector('.ef__option-values');
      const select = option.querySelector('select');
      const multi =
        (group && group.getAttribute('data-option-multi-select') === 'true') ||
        (select && (select.multiple || select.classList.contains('ef__multi-select-dropdown'))) ||
        inputs.some((input) => input.getAttribute('type') === 'checkbox');
      if (multi) {
        out.problems.push(`„${name}”: višestruki odabir nije podržan`);
        return null;
      }
      if (inputs.some((input) => input.getAttribute('data-quantity-enabled') === 'true')) {
        out.problems.push(`„${name}”: količina po vrijednosti nije podržana`);
        return null;
      }
      const seen = new Set();
      inputs.forEach((input) => {
        const tag = input.tagName.toLowerCase();
        let title = '';
        if (tag === 'option') title = input.getAttribute('data-value-title') || input.getAttribute('value') || efText(input);
        else if (tag === 'input' && input.getAttribute('type') === 'radio') title = input.getAttribute('data-value-title') || '';
        else {
          out.problems.push(`„${name}”: neočekivani unos <${tag}> za ${type}`);
          return;
        }
        title = String(title);
        if (!title.trim() || seen.has(title)) return;
        seen.add(title);
        field.choices.push({ title, price: efPrice(input) });
      });
      if (!field.choices.length) {
        out.problems.push(`„${name}”: nema vrijednosti`);
        return null;
      }
      field.kind = 'choice';
      field.hint = description;
      return field;
    }
    out.problems.push(`„${name}”: vrsta polja „${type}” nije podržana`);
    return null;
  }

  /** Is an EasyFlow rule of a shape this code evaluates (efHiddenMap)? fieldIds: the parsed fields' option ids. */
  function efRuleProblem(rule, fieldIds) {
    if (!rule || typeof rule !== 'object') return 'pravilo nije objekt';
    if (!Array.isArray(rule.conditions) || !rule.conditions.length) return 'pravilo bez uvjeta';
    if (!Array.isArray(rule.actions) || !rule.actions.length) return 'pravilo bez radnje';
    for (const condition of rule.conditions) {
      if (!condition || condition.optionId == null) return 'uvjet bez polja';
      if (condition.relation !== 'in' && condition.relation !== 'not-in') return `uvjet „${condition.relation}”`;
      if (!Array.isArray(condition.values)) return 'uvjet bez vrijednosti';
      if (!condition.isShopifyOption && !fieldIds.has(String(condition.optionId))) return `uvjet na nepoznato polje ${condition.optionId}`;
    }
    for (const action of rule.actions) {
      if (!action || (action.type !== 'show' && action.type !== 'hide')) return `radnja „${action && action.type}”`;
      if (!fieldIds.has(String(action.optionId))) return `radnja na nepoznato polje ${action.optionId}`;
      const values = action.values;
      if (!(values === 'All' || values === true || Array.isArray(values))) return 'radnja s nepoznatim vrijednostima';
    }
    return '';
  }

  /**
   * Parse EasyFlow's server-rendered fields out of a product page or its main section (a Document from DOMParser).
   * Returns { roots, fields, rules, sectionKey, problems, warnings }:
   *   fields – [{ optionId, cartName, name, type, kind: 'text' | 'choice', required, max, hint, choices: [{ title,
   *            price }], variantIds: string[] | null }] in page order; the app embed's copy of the same root
   *            (id …-embed) is read once; a field in several roots applies to the union of their variants;
   *   rules – EasyFlow's conditional rules (script#ef__product-option-rules next to each root), each once;
   *   sectionKey – the id of the section that holds the first root ("template--…__main"), for the Section Rendering API;
   *   problems – markup or rules this code does not understand: the caller then keeps the builder's own fields.
   */
  function parseEasyflow(doc) {
    const out = { roots: 0, fields: [], rules: [], sectionKey: '', problems: [], warnings: [] };
    const roots = doc ? [...doc.querySelectorAll('.ef__product-option-root')] : [];
    const seenRoots = new Set();
    const seenRules = new Set();
    const byId = new Map();
    roots.forEach((root) => {
      const variantIds = String(root.getAttribute('data-variant-ids') || '')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => /^\d+$/.test(id));
      const options = [...root.querySelectorAll('.ef__product-option')];
      const signature = `${variantIds.join(',')}|${options
        .map((option) => {
          const input = option.querySelector('[data-option-id]');
          return `${option.getAttribute('data-option-title')}#${input ? input.getAttribute('data-option-id') : ''}`;
        })
        .join('|')}`;
      if (seenRoots.has(signature)) return;
      seenRoots.add(signature);
      out.roots += 1;
      if (!out.sectionKey) {
        const section = root.closest('[id^="shopify-section-"]');
        if (section) out.sectionKey = section.id.slice('shopify-section-'.length);
      }
      const holder = root.parentElement;
      const script = holder ? [...holder.querySelectorAll('script')].find((node) => node.id === 'ef__product-option-rules') : null;
      if (script) {
        const text = String(script.textContent || '').trim();
        if (text && !seenRules.has(text)) {
          seenRules.add(text);
          try {
            const rules = JSON.parse(text);
            if (Array.isArray(rules)) out.rules.push(...rules);
            else out.problems.push('pravila nisu popis');
          } catch (error) {
            out.problems.push('pravila nisu ispravan JSON');
          }
        }
      }
      options.forEach((option) => {
        const field = parseEasyflowOption(option, out);
        if (!field) return;
        const ids = variantIds.length ? variantIds : null;
        const known = byId.get(field.optionId);
        if (known) {
          known.variantIds = known.variantIds && ids ? [...new Set([...known.variantIds, ...ids])] : null;
          return;
        }
        field.variantIds = ids;
        byId.set(field.optionId, field);
        out.fields.push(field);
      });
    });
    const fieldIds = new Set(out.fields.map((field) => field.optionId));
    out.rules.forEach((rule) => {
      const problem = efRuleProblem(rule, fieldIds);
      if (problem) out.problems.push(`pravilo: ${problem}`);
    });
    return out;
  }

  /**
   * EasyFlow's conditional logic (common.js V2.1.6 applyRules, ported): which options or values are hidden.
   *   rules – parseEasyflow's rules; fields – [{ optionId, kind, choices }];
   *   selected(optionId) – the chosen value titles of that option ([] when empty; a filled text field: [null], as
   *     EasyFlow's text values have no title);
   *   shopify(optionName) – the current variant's value of a Shopify option (undefined while not known).
   * Returns { [optionId]: 'All' | [hidden value titles] }. Hidden values do not count as chosen; like EasyFlow it
   * repeats until nothing changes (at most 10 rounds).
   */
  function efHiddenMap(rules, fields, selected, shopify) {
    if (!rules || !rules.length) return {};
    const byId = new Map(fields.map((field) => [String(field.optionId), field]));
    let hidden = {};
    for (let round = 0; round < 10; round += 1) {
      const chosen = (optionId) => {
        const values = selected(optionId) || [];
        const off = hidden[optionId];
        if (off === 'All') return [];
        return Array.isArray(off) ? values.filter((value) => value == null || !off.includes(value)) : values;
      };
      const titlesOf = (optionId) => {
        const field = byId.get(String(optionId));
        return field && field.kind === 'choice' ? field.choices.map((choice) => choice.title) : [];
      };
      const gated = {};
      const gate = (optionId, values) => {
        if (gated[optionId] === 'All') return;
        if (values === 'All' || values === true) {
          gated[optionId] = 'All';
          return;
        }
        if (!Array.isArray(values) || !values.length) return;
        gated[optionId] = [...new Set([...(gated[optionId] || []), ...values])];
      };
      rules.forEach((rule) => {
        if (!rule.actions.some((action) => action.type === 'show')) return;
        const active = rule.conditions.some((condition) => condition.isShopifyOption || chosen(String(condition.optionId)).length > 0);
        rule.actions.forEach((action) => {
          if (action.type !== 'show') return;
          gate(String(action.optionId), action.initiallyHiddenValues);
          if (active) gate(String(action.optionId), action.values);
        });
      });
      const shows = {};
      const hides = {};
      const collect = (map, optionId, values) => {
        if (map[optionId] === 'All') return;
        if (values === 'All') map[optionId] = 'All';
        else if (Array.isArray(values)) map[optionId] = [...(map[optionId] || []), ...values];
      };
      rules.forEach((rule) => {
        const test = (condition) => {
          if (condition.isShopifyOption) {
            const value = shopify(String(condition.optionId));
            const includes = value != null && condition.values.includes(value);
            return condition.relation === 'in' ? includes : !includes;
          }
          const values = chosen(String(condition.optionId));
          if (condition.values.includes('__any__')) {
            const any = values.length > 0;
            return condition.relation === 'in' ? any : !any;
          }
          return values.length
            ? values.some((value) => {
                const includes = condition.values.includes(value);
                return condition.relation === 'in' ? includes : !includes;
              })
            : condition.relation === 'not-in';
        };
        const matches = rule.isAllConditionsRequired ? rule.conditions.every(test) : rule.conditions.some(test);
        if (!matches) return;
        rule.actions.forEach((action) => {
          const optionId = String(action.optionId);
          // an option without titled values (a text field) is all or nothing
          const all = !titlesOf(optionId).length || action.values === 'All' || action.values === true;
          collect(action.type === 'show' ? shows : hides, optionId, all ? 'All' : action.values);
        });
      });
      const next = {};
      byId.forEach((field, optionId) => {
        const titles = titlesOf(optionId);
        if (hides[optionId] === 'All') {
          next[optionId] = 'All';
          return;
        }
        const hideList = Array.isArray(hides[optionId]) ? hides[optionId] : [];
        if (shows[optionId] === 'All') {
          if (hideList.length) next[optionId] = [...new Set(hideList)];
          return;
        }
        const shown = Array.isArray(shows[optionId]) ? shows[optionId] : [];
        const gate = gated[optionId];
        if (gate === 'All' && !titles.length) {
          next[optionId] = 'All';
          return;
        }
        const gatedTitles = gate === 'All' ? titles : Array.isArray(gate) ? gate : [];
        const off = [...new Set([...gatedTitles.filter((title) => !shown.includes(title)), ...hideList])];
        if (!off.length) return;
        // every value hidden: the whole option is hidden
        next[optionId] = titles.length && titles.every((title) => off.includes(title)) ? 'All' : off;
      });
      const same = JSON.stringify(next) === JSON.stringify(hidden);
      hidden = next;
      if (same) break;
    }
    return hidden;
  }

  /*
   * Praćenje grešaka (Karlo 4. 10. 2026): a few events go to the Delmis Cjenici app (POST /api/babyset/events, the
   * section setting „Praćenje grešaka – adresa”; empty = off, and nothing below runs). Events: babyset_started (once
   * per browser session, when the first part opens), babyset_added_to_cart (pieces, set value in cents),
   * babyset_error (the add failed, a piece sold out or not found on add, an uncaught error from this script).
   * Never what the customer types: only part ids, product handles, variant ids, counts, totals and the builder's own
   * error codes and texts. Queued, sent with navigator.sendBeacon (text/plain: no preflight) when the page is hidden
   * or 10 events wait. Everything here is wrapped: it never throws and never waits.
   */
  const TRACK_SCRIPT = 'babyset-builder-v2';
  const TRACK_BATCH = 10;
  const TRACK_MAX_PER_PAGE = 60;
  const tracker = {
    url: '',
    shop: '',
    session: '',
    queue: [],
    sent: 0,
    uncaught: 0,
    init(config) {
      try {
        if (this.url) return;
        const url = config && typeof config.url === 'string' ? config.url.trim() : '';
        const shop = config && typeof config.shop === 'string' ? config.shop : '';
        if (!/^https:\/\/\S+$/.test(url) || !shop) return;
        this.url = url;
        this.shop = shop;
        this.session = this.readSession().id;
        const flush = () => this.flush();
        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') flush();
        });
        window.addEventListener('error', (event) => {
          if (event && typeof event.filename === 'string' && event.filename.indexOf(TRACK_SCRIPT) !== -1) {
            this.uncaughtError(event.message);
          }
        });
        window.addEventListener('unhandledrejection', (event) => {
          const reason = event && event.reason;
          if (reason && typeof reason.stack === 'string' && reason.stack.indexOf(TRACK_SCRIPT) !== -1) {
            this.uncaughtError(reason.message);
          }
        });
      } catch (error) {
        this.url = '';
      }
    },
    /** { id, started } for this browser session (sessionStorage; in memory when storage is blocked). */
    readSession() {
      if (this.memory) return this.memory;
      let state = null;
      try {
        state = JSON.parse(window.sessionStorage.getItem('bs2-track') || 'null');
      } catch (error) {
        state = null;
      }
      if (!state || typeof state.id !== 'string' || !/^[A-Za-z0-9_-]{6,64}$/.test(state.id)) {
        state = { id: `${randomId()}${randomId()}`, started: false };
      }
      this.memory = state;
      this.saveSession();
      return state;
    },
    saveSession() {
      try {
        window.sessionStorage.setItem('bs2-track', JSON.stringify(this.memory));
      } catch (error) {
        // storage blocked: the session lives as long as the page
      }
    },
    push(type, data) {
      try {
        if (!this.url || this.sent + this.queue.length >= TRACK_MAX_PER_PAGE) return;
        const mobile = Boolean(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
        this.queue.push({
          type,
          at: Date.now(),
          session: this.session,
          page: window.location.pathname,
          ua: String(navigator.userAgent || '').slice(0, 120),
          mobile,
          data: data || {},
        });
        if (this.queue.length >= TRACK_BATCH) this.flush();
      } catch (error) {
        // never in the customer's way
      }
    },
    started(data) {
      try {
        if (!this.url) return;
        const state = this.readSession();
        if (state.started) return;
        state.started = true;
        this.saveSession();
        this.push('babyset_started', data || {});
      } catch (error) {
        // never in the customer's way
      }
    },
    error(code, message, data) {
      const text = message == null ? '' : String(message).slice(0, 300);
      this.push('babyset_error', { ...(data || {}), code, message: text });
    },
    uncaughtError(message) {
      // a loop of the same failure is one problem: at most 5 per page
      this.uncaught += 1;
      if (this.uncaught <= 5) this.error('uncaught', message, {});
    },
    flush() {
      try {
        while (this.url && this.queue.length) {
          const events = this.queue.splice(0, TRACK_BATCH);
          this.sent += events.length;
          const body = JSON.stringify({ shop: this.shop, events });
          if (navigator.sendBeacon) {
            navigator.sendBeacon(this.url, body);
          } else if (window.fetch) {
            window.fetch(this.url, { method: 'POST', body, keepalive: true, mode: 'no-cors', credentials: 'omit' }).catch(() => {});
          }
        }
      } catch (error) {
        this.queue = [];
      }
    },
  };

  class BabysetBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      const dataEl = this.querySelector('[data-babyset-data]');
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('Babyset: neispravni podaci', error);
        return;
      }
      this.initialised = true;
      tracker.init(this.data.tracking);
      this.layout = this.data.layout || this.dataset.layout || 'v2';
      this.money = new Intl.NumberFormat('hr-HR', { style: 'currency', currency: this.data.currency || 'EUR' });
      const discount = this.data.discount || {};
      this.discount = {
        percent: Number(discount.percent) || 0,
        minimum: Math.max(0, Math.round(Number(discount.minimum) || 0)),
        name: discount.name || 'Popust na set',
        minimumRequired: Boolean(discount.minimumRequired),
      };
      this.gift = this.readGift(this.data.gift);
      this.colourMode = this.readColourMode(this.data.colourMode);
      this.martaMode = false;
      this.forChoice = '';
      this.coupon = this.readCoupon(this.data.coupon);
      this.presetDeal = this.readPresetDeal(this.data.presetCode);
      this.optionLabels = this.parseOptionLabels(this.data.optionLabels);
      this.texts = this.data.texts || {};
      this.dropped = new Set();
      this.skipped = new Set();
      this.noticeText = '';
      this.errorPiece = null;
      this.giftChoice = null;
      // an add whose answer never arrived ({ setId, signature }): the retry checks the cart first
      this.pendingAdd = null;
      // a set that is already in the cart and became gift-worthy only there (price changed): { setId, variantId }
      this.lateGift = null;
      // v2: the screen (0 = start, 1…M = the parts, M + 1 = Pregled), the open window, the next live note
      this.stepIndex = 0;
      this.started = false;
      this.returnTo = null;
      this.fromReview = false;
      this.sheet = null;
      this.liveNote = '';
      // 22. 10.: where the set started ({ step, product } for the tracker: a „Gotovi set” or a product page), or null
      this.origin = null;

      this.initInputModality();
      this.buildModel();
      this.bindCards();
      this.initEasyflow();
      this.renderSizePicker();
      this.initGift();
      this.initColourMode();
      this.initDialog();
      this.initSheet();
      this.initSummary();
      // fix round 1: the bar's list closes when the focus leaves the bar; "Više o proizvodu" closes with its step
      this.initPanelFocus();
      this.initDialogFocus();
      this.initNotice();
      this.initCoupon();
      this.markBestsellers();
      this.initStart();
      this.initPresets();
      this.initPresetDeal();
      this.initCardNote();
      this.initSteps();
      this.initHistory();
      this.initPreviewBarOffset();
      this.initEditorEvents();
      this.initPageShow();
      this.update();
      const restored = this.restoreState();
      this.restoreDone = true;
      const linkedPreset = restored ? null : this.linkedPreset();
      if (!restored && !linkedPreset) this.applyLinkedPiece();
      this.routeOnLoad(restored);
      // after the start screen: the ready set opens in Pregled
      if (linkedPreset) this.applyPreset(linkedPreset, { linked: true });
      // CSS shows the controls as inactive until now (JavaScript off or still loading on a slow connection)
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.setPageBarSpace(0);
      if (this.onPanelScroll) window.removeEventListener('scroll', this.onPanelScroll);
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      if (this.popHandler) window.removeEventListener('popstate', this.popHandler);
      if (this.onStageResize) window.removeEventListener('resize', this.onStageResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.pbarResize) this.pbarResize.disconnect();
      if (this.pbarMutation) this.pbarMutation.disconnect();
      window.clearInterval(this.pbarTimer);
      window.clearTimeout(this.editorTimer);
      if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
      if (this.editorHandler) document.removeEventListener('shopify:block:select', this.editorHandler);
      if (this.editorDeselectHandler) document.removeEventListener('shopify:block:deselect', this.editorDeselectHandler);
      if (this.editorSectionHandler) document.removeEventListener('shopify:section:select', this.editorSectionHandler);
      if (this.keyHandler) document.removeEventListener('keydown', this.keyHandler);
      if (this.onModalityKey) document.removeEventListener('keydown', this.onModalityKey, true);
      if (this.onModalityPointer) {
        document.removeEventListener('pointerdown', this.onModalityPointer, true);
        document.removeEventListener('mousedown', this.onModalityPointer, true);
        document.removeEventListener('touchstart', this.onModalityPointer, true);
      }
      // the window: its one-tap timer and the on-screen keyboard's listeners (fitSheetToKeyboard)
      if (this.sheet) window.clearTimeout(this.sheet.oneTapTimer);
      if (this.onSheetViewport && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.onSheetViewport);
        window.visualViewport.removeEventListener('scroll', this.onSheetViewport);
      }
      if (this.sheet) document.documentElement.classList.remove('babyset-dialog-open');
    }

    /* ---------- input modality ---------- */

    /**
     * The customer's last input: 'keyboard' (a key press) or 'pointer' (mouse, touch, pen). It decides whether a
     * focused control is raised above the set bar (raiseAboveBar) and, as data-input-modality on the builder, whether
     * the size chips, colour / size pills, cards and gift chips show a ring on plain :focus (CSS).
     * :focus-visible alone is not enough: WebKit (Safari) does not match it for a radio reached with the arrow keys
     * (Chrome does), so a size chip picked with ArrowRight stayed under the grown bar and had no ring.
     * The document is listened to in the capture phase: a Tab from the header into the builder counts, and no
     * handler that stops a key press or a click hides it. Mouse and touch clicks switch it to 'pointer', so they
     * never show a ring.
     */
    initInputModality() {
      this.inputModality = null;
      this.lastKey = null;
      this.onModalityKey = (event) => {
        // a modifier alone or a shortcut (Cmd / Ctrl + key) is not moving through the page; Shift+Tab, Alt+Tab are
        if (MODIFIER_KEYS.has(event.key) || event.metaKey || event.ctrlKey) return;
        // the key behind a radio's change: an arrow key only moves through the dekica's radios (onPick)
        this.lastKey = event.key;
        this.setInputModality('keyboard');
      };
      this.onModalityPointer = () => {
        this.lastKey = null;
        this.setInputModality('pointer');
      };
      document.addEventListener('keydown', this.onModalityKey, true);
      document.addEventListener('pointerdown', this.onModalityPointer, true);
      // mousedown / touchstart: browsers without pointer events (Safari before 13)
      document.addEventListener('mousedown', this.onModalityPointer, true);
      document.addEventListener('touchstart', this.onModalityPointer, { capture: true, passive: true });
    }

    setInputModality(modality) {
      if (this.inputModality === modality) return;
      this.inputModality = modality;
      this.dataset.inputModality = modality;
    }

    /** Focus by keyboard: the last input was a key press. Before any input (focus kept on load) :focus-visible says. */
    isKeyboardFocus(control) {
      if (this.inputModality) return this.inputModality === 'keyboard';
      try {
        return control.matches(':focus-visible');
      } catch (error) {
        return false;
      }
    }

    /* ---------- settings ---------- */

    readGift(raw) {
      const gift = raw || {};
      return {
        enabled: Boolean(gift.enabled),
        threshold: Math.max(0, Math.round(Number(gift.threshold) || 0)),
        name: String(gift.name || 'Gumica').trim(),
        property: String(gift.property || 'Poklon za mamu').trim(),
        pickColour: Boolean(gift.pickColour),
        progress: String(gift.progress || 'Još [iznos] do gratis gumice za mamu'),
        reached: String(gift.reached || 'Gumica za mamu je gratis 🤍').trim(),
      };
    }

    /** "Tko bira boje?" – the property names and values of "Boje neka izabere Šigureca". */
    readColourMode(raw) {
      const mode = raw || {};
      const text = (value, fallback) => String(value || fallback).trim();
      return {
        enabled: Boolean(mode.enabled),
        property: text(mode.property, 'Boje'),
        value: text(mode.value, 'bira Šigureca'),
        valueVariant: text(mode.valueVariant, 'bira Šigureca (navedena boja nije odabrana)'),
        forProperty: text(mode.forProperty, 'Za'),
        sizeProperty: text(mode.sizeProperty, 'Veličina seta'),
      };
    }

    /**
     * 3. 10. (Marta): her best-selling colours (the section's "Najprodavanije boje", one per line) go first in every
     * palette (the puffy pieces), in that order, with a small "Najprodavanije" label on the tile. Each radio stays right
     * before its tile (the CSS's input + label rules).
     */
    markBestsellers() {
      const FLAME_ICON = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M8 1.4c.5 2.2 2.3 3.5 3.3 5.3.6 1.1 1 2.3 1 3.5A4.3 4.3 0 0 1 8 14.5a4.3 4.3 0 0 1-4.3-4.3c0-1.7.8-3.1 1.9-4.1.1 1.2.6 2.1 1.4 2.6C6.6 6.3 7 3.8 8 1.4zm0 7.4c-.9 1.1-1.6 2-1.6 3a1.6 1.6 0 0 0 3.2 0c0-1-.7-1.9-1.6-3z"/></svg>';
      const names = String(this.data.bestsellers || '')
        .split(/\r?\n|,/)
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean);
      if (!names.length) return;
      this.querySelectorAll('.bs2-palette__grid').forEach((grid) => {
        const found = [];
        grid.querySelectorAll('label.babyset-swatches__tile').forEach((label) => {
          const input = label.previousElementSibling;
          const nameEl = label.querySelector('.babyset-swatches__name [aria-hidden="true"]');
          const rank = names.indexOf(nameEl ? nameEl.textContent.trim().toLowerCase() : '');
          if (rank === -1 || !input || input.tagName !== 'INPUT') return;
          label.classList.add('is-bestseller');
          // a small flame in the tile's corner (as shops mark their best-sellers); the legend above says what it means
          const badge = document.createElement('span');
          badge.className = 'bs2-bestseller';
          badge.setAttribute('aria-hidden', 'true');
          badge.title = 'Najprodavanije';
          badge.innerHTML = FLAME_ICON;
          // on the tile, not in the photo (the photo clips): centred on the photo's top edge
          label.append(badge);
          const spoken = label.querySelector('.babyset-swatches__name .visually-hidden');
          if (spoken) spoken.append(' – najprodavanije');
          found.push({ rank, input, label });
        });
        // the legend once per palette: "🔥 Najprodavanije boje" (visual; the tiles say it to screen readers)
        const hint = grid.closest('fieldset') && grid.closest('fieldset').querySelector('.bs2-palette__hint');
        // Karlo 3. 10.: inline, at the end of the line above the tiles ("34 boje pređe. … 🔥 Najprodavanije")
        if (found.length && hint && !hint.querySelector('.bs2-bestseller-legend')) {
          // Karlo 3. 10. late: always on the hint's row, also at 390 px (the text wraps in its own box, the legend never)
          const text = document.createElement('span');
          text.className = 'bs2-hint-text';
          text.append(...hint.childNodes);
          const legend = document.createElement('span');
          legend.className = 'bs2-bestseller-legend';
          legend.setAttribute('aria-hidden', 'true');
          legend.innerHTML = `<span class="bs2-bestseller bs2-bestseller--inline">${FLAME_ICON}</span> Najprodavanije`;
          hint.classList.add('bs2-hint-row');
          hint.append(text, legend);
        }
        found
          .sort((a, b) => b.rank - a.rank)
          .forEach(({ input, label }) => {
            grid.prepend(label);
            grid.prepend(input);
          });
      });
      // §19 (3. 10. late): the puffy dekica became colour + „Personalizacija” (split colour tiles, no palette): the same
      // order and flames on its tiles (any split colour tiles whose names are on the list; her fabrics' English names
      // never are), the legend as a line of its own above the tiles
      this.querySelectorAll('[data-split-colours][data-colour-tiles] .bs2-tiles__list').forEach((grid) => {
        const found = [];
        grid.querySelectorAll('label.bs2-tile').forEach((label) => {
          const input = label.previousElementSibling;
          if (!input || input.tagName !== 'INPUT') return;
          const rank = names.indexOf(String(input.value).trim().toLowerCase());
          if (rank === -1) return;
          label.classList.add('is-bestseller');
          const badge = document.createElement('span');
          badge.className = 'bs2-bestseller';
          badge.setAttribute('aria-hidden', 'true');
          badge.title = 'Najprodavanije';
          badge.innerHTML = FLAME_ICON;
          label.append(badge);
          const nameEl = label.querySelector('.bs2-tile__name');
          if (nameEl) nameEl.append(el('span', 'visually-hidden', ' – najprodavanije'));
          found.push({ rank, input, label });
        });
        if (!found.length) return;
        if (!grid.parentNode.querySelector('.bs2-hint-row')) {
          // the palette's line above the tiles: „34 boje” left, „🔥 Najprodavanije” right, one row (visual only)
          const count = grid.querySelectorAll('label.bs2-tile').length;
          const word = count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14) ? 'boje' : 'boja';
          const row = document.createElement('p');
          row.className = 'bs2-palette__hint bs2-hint-row';
          row.setAttribute('aria-hidden', 'true');
          row.innerHTML = `<span class="bs2-hint-text">${count} ${word}</span><span class="bs2-bestseller-legend"><span class="bs2-bestseller bs2-bestseller--inline">${FLAME_ICON}</span> Najprodavanije</span>`;
          grid.before(row);
        }
        found
          .sort((a, b) => b.rank - a.rank)
          .forEach(({ input, label }) => {
            grid.prepend(label);
            grid.prepend(input);
          });
      });
    }

    /** The set coupon from the section ("Kupon za set"); enabled = on, with a code, not over when rendered. */
    readCoupon(raw) {
      const coupon = raw || {};
      const code = String(coupon.code || '').trim();
      const end = String(coupon.end || '').trim();
      const offset = /^([+-])(\d{2}):?(\d{2})$/.exec(String(coupon.utcOffset || '').trim());
      return {
        enabled: Boolean(coupon.enabled) && Boolean(code),
        code,
        label: String(coupon.label || '')
          .trim()
          .replace(/ %/g, `${NBSP}%`),
        // "2026-10-05"; anything else = no last day (Liquid already hid a coupon with a wrong date)
        end: /^\d{4}-\d{2}-\d{2}$/.test(end) ? end : '',
        autoApply: coupon.autoApply !== false,
        offset: offset ? (offset[1] === '-' ? -1 : 1) * (Number(offset[2]) * 60 + Number(offset[3])) : null,
      };
    }

    /**
     * The hidden code of the ready sets („Skriveni kod za gotove setove”): on with a code; the first and the last day
     * („YYYY-MM-DD”, empty = no limit) in shop time. A date in another form turns it off rather than on for ever.
     */
    readPresetDeal(raw) {
      const deal = raw || {};
      const day = (value) => String(value || '').trim();
      const start = day(deal.start);
      const end = day(deal.end);
      const valid = (value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value);
      const base = this.readCoupon({ enabled: true, code: deal.code, label: deal.label, utcOffset: deal.utcOffset });
      return {
        enabled: base.enabled && valid(start) && valid(end),
        code: base.code,
        label: base.label,
        start,
        end,
        offset: base.offset,
      };
    }

    /* ---------- helpers ---------- */

    format(cents) {
      return this.money.format(cents / 100);
    }

    /** "80 €" for a whole amount (her wording), otherwise "80,50 €". */
    formatShort(cents) {
      if (cents % 100 === 0) return `${Math.round(cents / 100)}${NBSP}€`;
      return this.format(cents);
    }

    percentText() {
      return `${this.discount.percent}${NBSP}%`;
    }

    isDesktop() {
      return window.matchMedia(DESKTOP).matches;
    }

    /** Section setting "Nazivi boja i opcija za prikaz": one "value = Label" per line (or comma separated). */
    parseOptionLabels(text) {
      const map = new Map();
      String(text || '')
        .split(/[\n,;]+/)
        .forEach((line) => {
          const match = line.match(/^\s*(.+?)\s*[=:]\s*(.+?)\s*$/);
          if (match) map.set(match[1].toLowerCase(), match[2]);
        });
      return map;
    }

    optionLabel(value) {
      const key = String(value == null ? '' : value).trim().toLowerCase();
      return this.optionLabels.get(key) || capitalize(value);
    }

    slotById(slotId) {
      return this.slots.find((slot) => slot.id === slotId) || null;
    }

    cardKey(card) {
      return `${card.slotId}|${card.handle}`;
    }

    /* ---------- back from the cart ---------- */

    /**
     * Safari (and Chrome) restore the page from the back/forward cache when the customer goes Back from the
     * cart: the script state is kept, including "Dodajem set…" on a disabled button. Make it usable again; an open
     * window is cancelled ("Više o proizvodu" closes), the bar's list closes, and the step of the URL's hash shows.
     */
    initPageShow() {
      this.onPageShow = (event) => {
        if (!event.persisted) return;
        this.busy = false;
        this.addButtons.forEach((button) => button.removeAttribute('aria-busy'));
        if (this.sheet) this.cancelSheet({ focus: false });
        this.closeDialog();
        this.closePanel();
        const index = this.stepFromHash();
        if (index != null && index !== this.stepIndex) this.goToStep(index, { push: false, focus: false, scroll: false });
        else this.update();
      };
      window.addEventListener('pageshow', this.onPageShow);
    }

    storageKey() {
      return `babyset2:${window.location.pathname}${window.location.search}:${this.dataset.sectionId}`;
    }

    /**
     * Remember the set of this tab (sessionStorage) – only read back when the customer comes Back to the page (v1 rule).
     * v2 also keeps what she picked herself on each piece, the step, and whether she has started. Nothing is written
     * while a window is open (its choices are not in the set yet).
     */
    saveState() {
      if (!this.restoreDone || this.sheet) return;
      try {
        const variants = {};
        const notes = {};
        // her own colour behind "Šigureca bira" (back to "Boje biraš ti" after Back from the cart): the colour of
        // a card with colour buttons, the colour variant of a colour-only card
        const own = {};
        const colours = {};
        const picked = {};
        this.chosenPieces().forEach(({ card }) => {
          if (card.variant) variants[this.cardKey(card)] = card.variant.id;
          if (card.product.split && card.splitColour) colours[this.cardKey(card)] = card.splitColour;
          if (card.variant && card.variant.marta && card.ownVariant && !card.ownVariant.marta && card.colourPicked) {
            own[this.cardKey(card)] = card.ownVariant.id;
          }
          const values = {};
          card.fields.forEach((field) => {
            if (field.value) values[field.id] = field.value;
          });
          if (Object.keys(values).length) notes[this.cardKey(card)] = values;
          const flags = Object.keys(card.picked).filter((flag) => card.picked[flag]);
          if (flags.length) picked[this.cardKey(card)] = flags;
        });
        const state = {
          v: STORAGE_VERSION,
          size: this.size,
          selection: this.selection,
          variants,
          own,
          colours,
          notes,
          picked,
          gift: this.giftChoice,
          mode: this.martaMode ? 'sigureca' : '',
          forChoice: this.forChoice,
          skipped: [...this.skipped],
          step: this.stepKey(this.stepIndex),
          started: this.started,
          returnTo: this.returnTo == null ? null : this.stepKey(this.returnTo),
          card: this.cardNote,
          origin: this.origin,
        };
        window.sessionStorage.setItem(this.storageKey(), JSON.stringify(state));
      } catch (error) {
        // Storage blocked (private mode, disabled cookies): the builder works without it.
      }
    }

    /**
     * Back to the page (navigation type back_forward only, v1 rule): the set of this tab comes back – v1's fields, the
     * pieces' own picks (v2) – and the saved step, which routeOnLoad shows. Returns null when nothing is restored.
     */
    restoreState() {
      let navigation = '';
      try {
        const entry = window.performance.getEntriesByType('navigation')[0];
        navigation = entry ? entry.type : '';
      } catch (error) {
        return null;
      }
      if (navigation !== 'back_forward') return null;
      let state = null;
      try {
        state = JSON.parse(window.sessionStorage.getItem(this.storageKey()) || 'null');
      } catch (error) {
        return null;
      }
      if (!state || state.v !== STORAGE_VERSION) return null;

      if (this.colourMode.enabled) {
        if (state.forChoice && (this.forRadios || []).some((radio) => radio.value === state.forChoice)) {
          this.forChoice = state.forChoice;
        }
        if (state.mode === 'sigureca') this.setColourMode(true, { silent: true });
        this.syncModeInputs();
      }

      if (state.size && this.useGlobalSize && this.sizeList.some((size) => size.key === state.size)) {
        this.applySize(state.size, { silent: true });
        this.syncSizeInputs();
      }
      let restored = 0;
      Object.entries(state.selection || {}).forEach(([slotId, handles]) => {
        const slot = this.slotById(slotId);
        if (!slot || !Array.isArray(handles)) return;
        handles.forEach((handle) => {
          const card = this.cards.find((item) => item.slotId === slotId && item.handle === handle);
          if (!card || this.selection[slotId].length >= slot.limit) return;
          const key = this.cardKey(card);
          // her own colour behind "Šigureca bira" (saveState): back with "Boje biraš ti"
          const colour = state.colours && state.colours[key];
          if (card.product.split && colour && card.splitColours.some((radio) => radio.value === colour && !radio.disabled)) {
            card.splitColour = colour;
          }
          const own = state.own && state.own[key] ? this.findVariant(card, state.own[key]) : null;
          if (own && !own.marta && own.available) {
            card.ownVariant = own;
            card.colourPicked = true;
          }
          const variant = state.variants && state.variants[key] ? this.findVariant(card, state.variants[key]) : null;
          if (variant && variant.available) {
            if (variant !== card.variant) card.colourPicked = true;
            card.variant = variant;
            if (!variant.marta) card.ownVariant = variant;
            card.reason = null;
            this.syncSplit(card);
            this.setSizeFit(card);
            this.showVariantImage(card);
          }
          const flags = state.picked && Array.isArray(state.picked[key]) ? state.picked[key] : [];
          flags.forEach((flag) => {
            if (flag in card.picked) card.picked[flag] = true;
          });
          const values = state.notes && state.notes[key] ? state.notes[key] : null;
          if (values && typeof values === 'object') {
            card.fields.forEach((field) => {
              if (values[field.id] == null) return;
              this.setFieldValue(field, values[field.id]);
            });
            // dev-test-19: EasyFlow's fields come with the product page – their values wait for it (efApply)
            if (card.ef) card.ef.notes = values;
          }
          if (card.ef) this.efLoad(card);
          if (!this.isDisabled(card)) {
            this.selection[slotId].push(handle);
            restored += 1;
          }
        });
      });
      (state.skipped || []).forEach((slotId) => {
        if (this.slotById(slotId)) this.skipped.add(slotId);
      });
      if (state.gift && this.giftRadios.some((radio) => radio.value === state.gift)) this.giftChoice = state.gift;
      if (typeof state.card === 'string') {
        this.cardNote = state.card.slice(0, this.cardNoteMax);
        this.renderCardNote();
      }
      if (state.origin && typeof state.origin === 'object') this.origin = state.origin;
      if (!(restored > 0 || Boolean(this.size) || this.martaMode || state.started)) return null;
      return {
        step: this.stepFromKey(state.step),
        started: Boolean(state.started),
        returnTo: this.stepFromKey(state.returnTo),
      };
    }

    /* ---------- model ---------- */

    buildModel() {
      this.sizes = new Map();
      this.slots = (this.data.slots || []).map((slot) => {
        const slotEl = this.querySelector(`[data-slot="${slot.id}"]`);
        const max = Math.max(1, Math.round(Number(slot.max) || 1));
        return {
          ...slot,
          products: this.readSlotProducts(slotEl),
          max,
          // the required dekica with 1 piece: another dekica replaces it; every other part holds up to "max" pieces
          multi: !(slot.required && max === 1),
          el: slotEl,
          limitEl: slotEl ? slotEl.querySelector('[data-slot-limit]') : null,
        };
      });
      this.selection = {};
      this.size = null;
      this.cards = [];

      this.slots.forEach((slot) => {
        this.selection[slot.id] = [];
        slot.products.forEach((raw) => {
          const product = this.prepareProduct(raw);
          const cardEl = slot.el ? slot.el.querySelector(`[data-card][data-handle="${CSS.escape(raw.handle)}"]`) : null;
          if (!cardEl) return;
          const media = cardEl.querySelector('.babyset-card__media');
          const variant = this.defaultVariant(product);
          const addButton = cardEl.querySelector('[data-add-piece]');
          this.cards.push({
            slotId: slot.id,
            handle: product.handle,
            product,
            el: cardEl,
            select: cardEl.querySelector('[data-variant-select]'),
            pills: [...cardEl.querySelectorAll('[data-variant-radio]')],
            optionValueEl: cardEl.querySelector('[data-option-value]'),
            priceEl: cardEl.querySelector('[data-price]'),
            legalEl: cardEl.querySelector('[data-legal]'),
            statusEl: cardEl.querySelector('[data-status]'),
            personalEl: cardEl.querySelector('[data-personal]'),
            fields: this.readFields(cardEl),
            img: cardEl.querySelector('.babyset-card__img'),
            media,
            swatchName: cardEl.querySelector('[data-swatch-name]'),
            renderedImageId: media ? media.dataset.imageId || '' : '',
            // v2: nothing is preselected, so the card starts on the product's main photo (as the Liquid card); a variant
            // set at start (the only one of a product) shows its photo. tag babyset-glavna-fotografija: always the main photo
            shown:
              product.mainPhoto && product.image
                ? product.image
                : variant
                  ? this.photoFor(product, variant, product.image || null)
                  : product.image || null,
            colourPicked: false,
            // her own colour before "Boje bira Šigureca" (back to "Boje biraš ti")
            ownVariant: variant,
            // split card (trakica, long-sleeve majica): the colour button and the size chosen so far ("g:" key without the prefix)
            splitColours: [...cardEl.querySelectorAll('[data-split-colour]')],
            splitValueEl: cardEl.querySelector('[data-split-colour-value]'),
            splitColour: product.split && variant ? variant.options[product.colourIndex] : null,
            splitSize: null,
            pillsEl: cardEl.querySelector('.babyset-pills'),
            // v2: the grid card's one button and lines; the config block (moved into the window by initSheet)
            addButton,
            addText: addButton ? addButton.querySelector('[data-add-text]') : null,
            addEnd: addButton ? addButton.querySelector('[data-add-end]') : null,
            moreButton: cardEl.querySelector('[data-more]'),
            configEl: cardEl.querySelector('[data-config]'),
            chosenEl: cardEl.querySelector('[data-chosen]'),
            chosenPicture: cardEl.querySelector('[data-chosen-picture]'),
            chosenText: cardEl.querySelector('[data-chosen-text]'),
            martaEl: cardEl.querySelector('[data-marta]'),
            // what the customer chose herself on this piece (a colour, an option, a variant of a combined list)
            picked: { colour: false, option: false, variant: false },
            variant,
            override: false,
            near: false,
            // "Roza / Personalizirana" when a new set size could only keep the piece in another colour or type
            combo: '',
            reason: product.available ? null : 'soldout-all',
          });
        });
      });

      // Karlo 4. 10.: a part holds at most min("Najviše komada", the products it shows) – „Za glavu” with 2 products and
      // max 10 is full at 2 (auto-advance, notices); the step's limit line shows only when the setting really limits
      // (max lower than the part's products: capped)
      this.slots.forEach((slot) => {
        const shown = this.cards.filter((card) => card.slotId === slot.id).length;
        slot.limit = shown ? Math.min(slot.max, shown) : slot.max;
        slot.capped = slot.max < shown;
      });

      const filterKeys = String(this.data.sizeFilter || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => parseSize(part).key);
      const all = [...this.sizes.values()].sort((a, b) => compareSort(a.sort, b.sort));
      this.sizeList = filterKeys.length ? all.filter((size) => filterKeys.includes(size.key)) : all;
      this.hasSizes = this.sizeList.length > 0;
      this.useGlobalSize = Boolean(this.data.sameSize) && this.hasSizes;
    }

    /** The products of a part (JSON next to its cards): the block's own products, then its collection. */
    readSlotProducts(slotEl) {
      const node = slotEl ? slotEl.querySelector('[data-slot-products]') : null;
      if (!node) return [];
      try {
        const products = JSON.parse(node.textContent);
        return Array.isArray(products) ? products : [];
      } catch (error) {
        console.error('Babyset: neispravni podaci dijela seta', error);
        return [];
      }
    }

    /** "Polje uz proizvod" fields of a card ("Izaberi slovo", "Izaberi boje veza", "Odaberi print"). */
    readFields(cardEl) {
      return [...cardEl.querySelectorAll('[data-field]')]
        .map((node) => {
          const input = node.querySelector('[data-field-input]');
          if (!input) return null;
          const kind = node.dataset.fieldKind === 'choice' ? 'choice' : 'text';
          // the limit in characters as she sees them (data-max-length); maxlength only stops huge pastes
          const limit = Number(input.dataset.maxLength) || Number(input.maxLength);
          const max = kind === 'text' && limit > 0 ? limit : 100;
          return {
            id: node.dataset.field,
            name: String(node.dataset.fieldName || '').trim(),
            kind,
            required: node.dataset.fieldRequired === 'true',
            max,
            el: node,
            input,
            count: node.querySelector('[data-field-count]'),
            value: '',
          };
        })
        .filter((field) => field && field.name);
    }

    prepareProduct(raw) {
      // Only an option whose values are ages (0–3 mjeseci … 12–13 godina) is the set size; "Veličina: 65 × 65 cm"
      // (tetra pelene) stays an ordinary option.
      let sizeIndex = (raw.options || []).findIndex((name) => SIZE_OPTION_RE.test(String(name).trim()));
      if (sizeIndex !== -1 && !raw.variants.some((variant) => parseSize(variant.options[sizeIndex]).range)) {
        sizeIndex = -1;
      }
      const variants = raw.variants.map((variant) => {
        let size = null;
        if (sizeIndex !== -1) {
          size = parseSize(variant.options[sizeIndex]);
          if (!this.sizes.has(size.key)) this.sizes.set(size.key, size);
        }
        const legal = (variant.legal || []).map((line) => String(line).replace(/\s+/g, ' ').trim()).filter(Boolean);
        return {
          ...variant,
          legal,
          sizeKey: size ? size.key : null,
          sizeSort: size ? size.sort : null,
          sizeRange: size ? size.range : null,
        };
      });
      const colourIndex = (raw.options || []).findIndex((name) => COLOUR_OPTION_RE.test(String(name).trim()));
      // "Šigureca bira": only for "Boje neka izabere Šigureca", never a colour the customer sees
      variants.forEach((variant) => {
        variant.marta = colourIndex !== -1 && MARTA_VALUE_RE.test(String(variant.options[colourIndex] || ''));
      });
      const regular = variants.filter((variant) => !variant.marta);
      const personalVariants = regular.filter((variant) => PERSONAL_VARIANT_RE.test((variant.options || []).join(' ')));
      const optionCount = (raw.options || []).length;
      const colourOnly = colourIndex !== -1 && optionCount === 1;
      // the same rule as the Liquid card's palette ("swatches"): one colour option, more than 8 colours with photos
      const palette = colourOnly && sizeIndex === -1 && regular.length > 8 && regular.some((variant) => variant.image);
      const mainPhoto = Boolean(raw.mainPhoto) && Boolean(raw.image);
      return {
        ...raw,
        eligible: raw.eligible !== false,
        // "Vezica za dudu – Obična / Personalizirana": the fields belong to the personalised variant only
        personalByVariant: personalVariants.length > 0 && personalVariants.length < regular.length,
        sizeIndex,
        colourIndex,
        variants,
        hasMarta: regular.length < variants.length,
        sized: sizeIndex !== -1,
        single: regular.length === 1,
        // the only option is a colour / print (dekica "Boje", puffy "Boja"): "Boje neka izabere Šigureca" hides it
        colourOnly,
        // a colour and an age size (trakica "Boja / Veličina"): "Boje neka izabere Šigureca" leaves only the size
        colourSized: colourIndex !== -1 && sizeIndex !== -1 && colourIndex !== sizeIndex,
        // exactly those two options: two pickers on the card (colour buttons + size list)
        // (Karlo 3. 10.: also a colour + any other option – the tetra's cm sizes, the vezica's Obična / Personalizirana)
        split: colourIndex !== -1 && optionCount === 2,
        palette,
        // tag + palette: no colour until she picks one, "Dodaj u set" asks for it
        pickColour: palette && mainPhoto,
        mainPhoto,
        description: String(raw.description || ''),
        images: (Array.isArray(raw.images) ? raw.images : []).filter((image) => image && image.src),
        // a product without options: its only variant is Shopify's "Default Title", which is never shown
        defaultOnly: variants.length === 1 && DEFAULT_VARIANT_RE.test(String(variants[0].title || '').trim()),
        // Some colours have their own photo, so the photos are colour-specific: never show one colour's
        // photo for another colour (the dekica has a photo only for "Colorful Dogs"; "Majica: Boja + Veličina"
        // may have photos only for "Roza"). A variant without a photo borrows one of the same colour.
        colourAware: colourIndex !== -1 && regular.length > 1 && regular.some((variant) => variant.image),
      };
    }

    /**
     * §19: „Personaliziraj · +6,00 €” – the personalised price minus the plain price of the same colour (the chosen
     * colour, else the first colour that has both, else the lowest prices of the two types); only „Personaliziraj”
     * when there is no difference.
     */
    renderPersonalPrice(card) {
      const toggle = card.personalToggle;
      const priceEl = card.personalPriceEl;
      if (!toggle || !priceEl) return;
      const { product } = card;
      const other = product.options.findIndex((_, index) => index !== product.colourIndex);
      const plainName = toggle.dataset.togglePlain.slice(2);
      const personalName = toggle.dataset.togglePersonal.slice(2);
      const colourOf = (variant) => variant.options[product.colourIndex];
      const chosen = card.variant ? colourOf(card.variant) : card.splitColour;
      const pairFor = (colour) => {
        const plain = product.variants.find((variant) => colourOf(variant) === colour && variant.options[other] === plainName);
        const personal = product.variants.find((variant) => colourOf(variant) === colour && variant.options[other] === personalName);
        return plain && personal ? [plain, personal] : null;
      };
      let pair = chosen ? pairFor(chosen) : null;
      if (!pair) {
        const first = product.variants.find((variant) => !variant.marta && pairFor(colourOf(variant)));
        pair = first ? pairFor(colourOf(first)) : null;
      }
      let diff = pair ? pair[1].price - pair[0].price : 0;
      if (!pair) {
        // §21: no fabric in both types (the vezica: Obična = patterned, Personalizirana = plain): lowest against lowest
        const low = (name) => {
          const prices = product.variants.filter((variant) => !variant.marta && variant.options[other] === name).map((variant) => variant.price);
          return prices.length ? Math.min(...prices) : null;
        };
        const plainLow = low(plainName);
        const personalLow = low(personalName);
        if (plainLow !== null && personalLow !== null) diff = personalLow - plainLow;
      }
      const text = diff > 0 ? ` · +${this.format(diff)}` : '';
      if (priceEl.textContent !== text) priceEl.textContent = text;
    }

    /** Does the card show its fields with its current variant? (Only the personalised variant, if there is one.) */
    fieldsActive(card) {
      if (!card.fields.length) return false;
      // dev-test-19: EasyFlow's fields say themselves which variants they belong to (fieldHidden → efFieldOn)
      if (card.efFields) return true;
      if (!card.product.personalByVariant) return true;
      // §21: „Personaliziraj” ticked counts before the fabric / colour is picked (the box sets card.splitSize)
      if (!card.variant && card.personalToggle) return PERSONAL_VARIANT_RE.test(card.splitSize || '');
      return Boolean(card.variant) && PERSONAL_VARIANT_RE.test((card.variant.options || []).join(' '));
    }

    /** "Boje neka izabere Šigureca": the colour / print fields ("Izaberi boje veza", "Odaberi print") are hers to fill. */
    fieldHidden(field) {
      if (this.martaMode && COLOUR_FIELD_RE.test(field.name)) return true;
      // dev-test-19: an EasyFlow field outside its variants or hidden by EasyFlow's rules
      return Boolean(field.ef) && !this.efFieldOn(field.card, field);
    }

    /** The fields of a piece that go into the cart: shown and filled in. */
    filledFields(card) {
      return this.fieldsActive(card) ? card.fields.filter((field) => field.value && !this.fieldHidden(field)) : [];
    }

    /** The first required field of a piece that is still empty (or null). */
    missingField(card) {
      if (!this.fieldsActive(card)) return null;
      return card.fields.find((field) => field.required && !field.value && !this.fieldHidden(field)) || null;
    }

    /**
     * "Boje neka izabere Šigureca" and a colour-only product (no colour buttons, any available variant) or a colour +
     * size product (the list shows only the sizes, any available variant of that size).
     */
    hidesColour(card) {
      // §19 (3. 10. late): also every other colour + option card (split: the puffy dekica's „Personalizacija”, the
      // tetra's cm sizes, the vezica's Obična / Personalizirana) – only the other option is asked; its colour is
      // „Šigureca bira” where the product has that variant (the dekica), else any available colour of it (martaFor)
      return this.martaMode && (card.product.colourOnly || card.product.colourSized || card.product.split);
    }

    pieceLabel(card, { short = false } = {}) {
      const skip = this.hidesColour(card) ? card.product.colourIndex : -1;
      const label = this.variantLabel(card.product, card.variant, { short, skip });
      const qty = this.qtyOf(card);
      if (qty <= 1) return label;
      return label ? `${label} · ${qty} kom.` : `${qty} kom.`;
    }

    /** Key of a variant's options without the colour (trakica: its size). */
    sizeGroupKey(product, variant) {
      return variant.options.filter((_, index) => index !== product.colourIndex).join('\u0001');
    }

    /**
     * Colour + size products: the list of "Boje neka izabere Šigureca" – one entry per size (value "g:<key>", in the
     * order of the combined list), sold out when no colour of that size is available. Built once, swapped in
     * renderCard; the combined options stay as they are for "Ja biram boje".
     */
    buildSizeGroups(card) {
      const { product } = card;
      if (product.split) {
        // the card's list already has the sizes ("g:<size>"): only the variants of each size
        const groups = new Map();
        product.variants.forEach((variant) => {
          if (variant.marta) return;
          const key = this.sizeGroupKey(product, variant);
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(variant);
        });
        const options = [...card.select.options];
        // age sizes get their label and natural order; other options (35 × 35 cm, Obična) stay as she wrote them
        if (product.sized) {
          options.forEach((option) => {
            if (!option.value) return;
            option.textContent = parseSize(option.value.slice(2)).label;
          });
          // the same natural order as the size buttons
          options
            .filter((option) => option.value)
            .sort((a, b) => compareSort(parseSize(a.value.slice(2)).sort, parseSize(b.value.slice(2)).sort))
            .forEach((option) => card.select.append(option));
        }
        const placeholder = options.find((option) => !option.value);
        if (placeholder) placeholder.textContent = product.sized ? 'Veličina' : 'Odaberi';
        card.sizeGroups = { split: true, groups, mode: false };
        return;
      }
      const combined = [...card.select.options];
      const groups = new Map();
      combined.forEach((option) => {
        const variant = option.value ? this.findVariant(card, option.value) : null;
        if (!variant) return;
        const key = this.sizeGroupKey(product, variant);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(variant);
      });
      const rest = product.options.filter((_, index) => index !== product.colourIndex);
      const sizeOnly = rest.length === 1;
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = sizeOnly ? 'Veličina' : 'Odaberi';
      const options = [placeholder];
      groups.forEach((variants, key) => {
        const option = document.createElement('option');
        option.value = `g:${key}`;
        const available = variants.some((variant) => variant.available);
        option.disabled = !available;
        option.textContent =
          this.variantLabel(product, variants[0], { short: !sizeOnly, skip: product.colourIndex }) +
          (available ? '' : ' – rasprodano');
        options.push(option);
      });
      const labelEl = card.el.querySelector('.babyset-card__option-label');
      const labelText = labelEl ? [...labelEl.childNodes].find((node) => node.nodeType === 3 && node.textContent.trim()) : null;
      card.sizeGroups = {
        combined,
        options,
        groups,
        mode: false,
        labelText,
        labelCombined: labelText ? labelText.textContent : '',
        labelSize: rest.join(' / '),
      };
    }

    /**
     * "g:<key>" -> "Boje neka izabere Šigureca": its "Šigureca bira" variant, else the current variant if it has that
     * size, else the same colour, else any available colour. Split card, "Ja biram boje": only the chosen colour in
     * that size (null without a colour or when it is sold out).
     */
    variantForGroup(card, value) {
      const { product } = card;
      const list = card.sizeGroups ? card.sizeGroups.groups.get(String(value).slice(2)) || [] : [];
      const available = list.filter((variant) => variant.available);
      if (product.split && !this.hidesColour(card)) {
        return available.find((variant) => variant.options[product.colourIndex] === card.splitColour) || null;
      }
      const current = card.variant && !card.variant.marta ? card.variant : null;
      let pick = null;
      if (current && available.includes(current)) pick = current;
      else {
        const colour = current ? current.options[product.colourIndex] : card.splitColour;
        pick = available.find((variant) => variant.options[product.colourIndex] === colour) || available[0] || null;
      }
      if (!pick && list.length) {
        // every colour of that size is sold out, but "Šigureca bira" may still be there
        const marta = product.variants.find(
          (variant) => variant.marta && variant.available && this.sizeGroupKey(product, variant) === String(value).slice(2)
        );
        if (marta && this.hidesColour(card)) return marta;
      }
      return this.martaFor(card, pick);
    }

    /** Swaps the card's list between the combined options and the sizes (renderCard). */
    syncSizeGroups(card) {
      const groups = card.sizeGroups;
      if (!groups) return;
      if (groups.split) {
        const key = card.variant ? this.sizeGroupKey(card.product, card.variant) : card.splitSize;
        card.select.value = key ? `g:${key}` : '';
        return;
      }
      const on = this.hidesColour(card);
      if (groups.mode !== on) {
        groups.mode = on;
        card.select.replaceChildren(...(on ? groups.options : groups.combined));
        if (groups.labelText) groups.labelText.textContent = on ? groups.labelSize : groups.labelCombined;
      }
      card.select.value = card.variant
        ? on
          ? `g:${this.sizeGroupKey(card.product, card.variant)}`
          : String(card.variant.id)
        : '';
    }

    /**
     * "Boju bira Šigureca" on the card: its colour buttons or its colour / print fields are hers now. The fields count
     * only while they are active (D17, 1. 10. 2026: the vezica's belong to "Personalizirana", so "Obična" has no
     * note). The cart line's "Boje: bira Šigureca" follows this note (cartItems).
     */
    martaNote(card) {
      if (!this.martaMode) return '';
      // dev-test-19: an EasyFlow field counts only where it applies (its variants, its rules)
      const fields = this.fieldsActive(card) ? card.fields.filter((field) => !field.ef || this.efFieldOn(card, field)) : [];
      const colour = card.product.colourIndex !== -1 || fields.some((field) => /boj/i.test(field.name));
      const print = fields.some((field) => /print/i.test(field.name));
      if (colour && print) return 'Boju i print bira Šigureca';
      if (print) return 'Print bira Šigureca';
      return colour ? 'Boju bira Šigureca' : '';
    }

    /** „Izaberi slovo“ – for messages and the button. */
    fieldPrompt(field, withCard) {
      return `Popuni „${field.name}“${withCard ? ' na kartici' : ''}`;
    }

    cleanText(text, max) {
      return truncateChars(String(text || '').replace(/\s+/g, ' ').trim(), max).trim();
    }

    setFieldValue(field, raw) {
      if (field.kind === 'choice') {
        const value = String(raw || '');
        const option = [...field.input.options].find((item) => item.value === value);
        field.value = option ? value : '';
        field.input.value = field.value;
      } else {
        field.value = this.cleanText(raw, field.max);
        field.input.value = field.value;
      }
    }

    /** A variant's own photo, else (colour-specific photos) the photo of another variant in the same colour. */
    variantPhoto(product, variant) {
      if (!variant) return null;
      if (variant.image) return variant.image;
      if (!product.colourAware) return null;
      const colour = variant.options[product.colourIndex];
      const twin = product.variants.find((item) => !item.marta && item.image && item.options[product.colourIndex] === colour);
      return twin ? twin.image : null;
    }

    /** The picture of a variant: its (colour's) photo, a colour tile (colour without a photo) or the current one. */
    photoFor(product, variant, current) {
      const photo = this.variantPhoto(product, variant);
      if (photo) return photo;
      if (variant && product.colourAware) return { swatch: true, id: `swatch:${variant.id}`, variantId: variant.id };
      return current;
    }

    /** Name on the colour tile: "Mint" (dekica) or just the colour of "Bijela / 3–6 mjeseci / Upeklo". */
    swatchLabel(product, variant) {
      if (!variant) return '';
      if (product.colourIndex !== -1 && variant.options.length > 1) return this.optionLabel(variant.options[product.colourIndex]);
      return this.variantLabel(product, variant);
    }

    /**
     * v2: no preselection where the customer has a real choice – the grid card does not show the options, so a
     * preselected colour or option would go into the set unseen (V2-SPEC SD3). Null for a colour option with more than
     * one colour in "Boje biraš ti" and for any other option with more than one value. Unchanged (v1): the only
     * variant of a one-variant product; "Šigureca bira" for a colour-only product in that mode (else any available
     * variant, v1's fallback); null for sized products (applySize sets them).
     */
    defaultVariant(product) {
      if (!product.available) return null;
      if (this.martaMode && product.colourOnly) {
        const marta = product.variants.find((variant) => variant.marta && variant.available);
        if (marta) return marta;
      }
      if (product.sized && !product.single) return null;
      if (!product.single) {
        if (product.colourIndex === -1) return null;
        if (!this.martaMode) return null;
      }
      return product.variants.find((variant) => variant.available && !variant.marta) || null;
    }

    /**
     * "Boje neka izabere Šigureca": the "Šigureca bira" variant with the same other options (trakica: the same size)
     * instead of `variant`, when the product has one in stock; otherwise `variant` (the old behaviour).
     */
    martaFor(card, variant) {
      if (!variant || variant.marta || !this.hidesColour(card)) return variant;
      const { product } = card;
      const twin = product.variants.find(
        (item) =>
          item.marta &&
          item.available &&
          item.options.every((value, index) => index === product.colourIndex || value === variant.options[index])
      );
      return twin || variant;
    }

    /** Back to "Ja biram boje": a "Šigureca bira" variant becomes her own colour again (or none: she picks one). */
    ownFor(card, variant) {
      if (!variant || !variant.marta) return variant;
      const { product } = card;
      if (product.pickColour && !card.colourPicked) return null;
      const colour = product.split ? card.splitColour : card.ownVariant ? card.ownVariant.options[product.colourIndex] : null;
      if (!colour && product.colourOnly) return product.pickColour ? null : this.defaultVariant(product);
      if (!colour) return null;
      return (
        product.variants.find(
          (item) =>
            !item.marta &&
            item.available &&
            item.options.every((value, index) => (index === product.colourIndex ? value === colour : value === variant.options[index]))
        ) || null
      );
    }

    /** Split card (colour buttons + size list): its colour and size follow the variant (not "Šigureca bira"). */
    syncSplit(card) {
      const { product, variant } = card;
      if (!product.split || !variant) return;
      card.splitSize = this.sizeGroupKey(product, variant);
      if (!variant.marta) card.splitColour = variant.options[product.colourIndex];
    }

    /** What the customer still has to pick on a card without a variant: 'boju', 'veličinu' or 'boju i veličinu'. */
    missingOption(card) {
      if (card.variant) return '';
      const { product } = card;
      if (product.split) {
        const colour = !card.splitColour && !this.hidesColour(card);
        const size = !card.splitSize;
        if (colour && size) return 'boju i veličinu';
        return colour ? 'boju' : 'veličinu';
      }
      if (!product.sized && product.colourIndex !== -1) return 'boju';
      return 'veličinu';
    }

    sizeLabel(key) {
      const size = this.sizes.get(key);
      return size ? size.label : '';
    }

    /** "3–6 mjeseci", "Mint", "Bijela / 3–6 mjeseci / Upeklo" ('' for "Default Title"); short: "3–6 mj." */
    variantLabel(product, variant, { short = false, skip = -1 } = {}) {
      if (!variant || product.defaultOnly) return '';
      return variant.options
        .filter((_, index) => index !== skip)
        .map((value, filtered) => {
          const index = skip !== -1 && filtered >= skip ? filtered + 1 : filtered;
          if (index !== product.sizeIndex) return this.optionLabel(value);
          const size = parseSize(value);
          return short ? size.short : size.label;
        })
        .join(' / ');
    }

    /** Options of `variant` that differ from `other` (size left out): "Roza / Personalizirana". */
    otherOptionsLabel(product, variant, other) {
      if (!variant || !other) return '';
      return variant.options
        .filter((value, index) => index !== product.sizeIndex && value !== other.options[index])
        .map((value) => this.optionLabel(value))
        .join(' / ');
    }

    findVariant(card, id) {
      return card.product.variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    /**
     * Variants of a product for a set size: exactly that size, otherwise the age ranges that contain it
     * (narrowest first) – "0-6 mjeseci" for 3–6 mjeseci, "12-24 mj" for 12–18 mjeseci.
     */
    sizeMatches(product, key) {
      const exact = product.variants.filter((variant) => !variant.marta && variant.sizeKey === key);
      if (exact.length) return { exact, near: [] };
      const target = this.sizes.get(key);
      if (!target || !target.range) return { exact, near: [] };
      const near = product.variants
        .filter(
          (variant) =>
            !variant.marta && variant.sizeRange && variant.sizeRange[0] <= target.range[0] && variant.sizeRange[1] >= target.range[1]
        )
        .sort((a, b) => a.sizeRange[1] - a.sizeRange[0] - (b.sizeRange[1] - b.sizeRange[0]));
      return { exact, near };
    }

    /** override = a size other than the set size; near = the closest range that contains the set size. */
    setSizeFit(card) {
      card.override = false;
      card.near = false;
      if (!card.variant || !this.size || !card.product.sized) return;
      if (card.variant.sizeKey === this.size) return;
      const { near } = this.sizeMatches(card.product, this.size);
      // "Šigureca bira" is never in that list: its size counts (2. 10. 2026: "Najbliža", not "Druga veličina")
      if (near.some((variant) => variant === card.variant || variant.sizeKey === card.variant.sizeKey)) card.near = true;
      else card.override = true;
    }

    chosenCards(slotId) {
      const handles = this.selection[slotId] || [];
      return handles
        .map((handle) => this.cards.find((card) => card.slotId === slotId && card.handle === handle))
        .filter(Boolean);
    }

    /** First piece of a slot (hero formula, board tile). */
    chosenCard(slotId) {
      return this.chosenCards(slotId)[0] || null;
    }

    isChosen(card) {
      return (this.selection[card.slotId] || []).includes(card.handle);
    }

    /**
     * The other part that already holds this product (a product in two parts' collections is shown in both, but
     * one set has it once – otherwise the cart line would get quantity 2 and count twice towards the gift).
     */
    chosenElsewhere(card) {
      return (
        this.slots.find((slot) => slot.id !== card.slotId && (this.selection[slot.id] || []).includes(card.handle)) || null
      );
    }

    elsewhereText(card, slot) {
      return `„${card.product.title}“ je već u setu (dio „${slot.name}“) – jedan set ima svaki komad jednom.`;
    }

    chosenPieces() {
      const result = [];
      this.slots.forEach((slot) => {
        this.chosenCards(slot.id).forEach((card) => result.push({ slot, card }));
      });
      return result;
    }

    isDisabled(card) {
      return card.reason !== null;
    }

    needsPersonal(card) {
      return Boolean(this.missingField(card));
    }

    pieceReady(card) {
      return Boolean(card.variant) && !this.needsPersonal(card);
    }

    isErrorCard(card) {
      return Boolean(this.errorPiece) && this.errorPiece.slotId === card.slotId && this.errorPiece.handle === card.handle;
    }

    slotHasError(slotId) {
      return Boolean(this.errorPiece) && this.errorPiece.slotId === slotId;
    }

    /** The price of a piece in the set: the variant's (else the lowest), times its quantity (the tetra). */
    piecePrice(card) {
      return this.qtyTotal(card, card.variant ? card.variant.price : this.fromPrices(card).price);
    }

    /**
     * §21 (3. 10. late): no variant yet, but „Personaliziraj” already says the type (card.splitSize): the lowest
     * price of that type („od 20,00 €” for the ticked vezica, the dekica's 51,00 €); otherwise the product's lowest.
     * Šigureca 6. 10. (Karlo): the same for a size picked before the colour (muslin dekica 80 × 80 cm → 48,00 €,
     * 70 × 70 cm → 43,00 €, not „od 43,00 €”); „od” only while that size's colours differ in price.
     * shown: the variant whose price-information lines go with that price; varies: show „od” before the price.
     */
    fromPrices(card) {
      const { product } = card;
      const fallback = () => ({
        price: product.priceMin,
        compare: product.compareMin,
        shown: product.variants.find((item) => item.price === product.priceMin) || product.variants[0],
        varies: product.priceVaries,
      });
      if (card.variant || !card.splitSize || !product.split) return fallback();
      const list = product.variants.filter((item) => !item.marta && this.sizeGroupKey(product, item) === card.splitSize);
      if (!list.length) return fallback();
      const shown = list.reduce((low, item) => (item.price < low.price ? item : low), list[0]);
      const varies = card.personalToggle ? product.priceVaries : list.some((item) => item.price !== shown.price);
      return { price: shown.price, compare: shown.compare, shown, varies };
    }

    /** Marta's tier reached by the stepper's quantity (QTY_TIERS: 3 → 5 %, 6 → 10 %, 9 → 15 %), else 0. */
    qtyPercent(card) {
      if (!card || !card.qtyOn) return 0;
      const qty = this.qtyOf(card);
      const reached = [...QTY_TIERS].reverse().find(([count]) => qty >= count);
      return reached ? reached[1] : 0;
    }

    /** Karlo 3. 10.: unit × quantity less the tier the automatic discount gives at checkout (rounded to cents). */
    qtyTotal(card, unit) {
      return Math.round((unit * this.qtyOf(card) * (100 - this.qtyPercent(card))) / 100);
    }

    /** The one photo of a piece, used everywhere (card, bar, hero, board, lists); null for a colour tile. */
    pieceImage(card) {
      return card && card.shown && !card.shown.swatch ? card.shown : null;
    }

    /** A variant with its own photo shows it; without one the card keeps its photo (colours: the colour tile). */
    showVariantImage(card) {
      const { product } = card;
      // "Boje neka izabere Šigureca" (no colour chosen) or the tag babyset-glavna-fotografija: the main photo
      if (product.image && (this.hidesColour(card) || product.mainPhoto)) {
        card.shown = product.image;
        return;
      }
      if (card.variant) {
        card.shown = this.photoFor(product, card.variant, card.shown);
      } else if (product.split && card.splitColour) {
        // a colour without a size yet: that colour's photo (or its tile)
        const any = product.variants.find((item) => !item.marta && item.options[product.colourIndex] === card.splitColour);
        if (any) card.shown = this.photoFor(product, any, card.shown);
      }
    }

    /* ---------- events ---------- */

    bindCards() {
      this.cards.forEach((card) => {
        if (card.select) {
          const options = [...card.select.options];
          // One option (size): "Veličina". Several options (Boja / Veličina / Print): "Odaberite", like the label
          // above the list, and short sizes ("Bijela / 3–6 mj. / Upeklo") so the closed list stays readable.
          const several = card.product.options.length > 1;
          options.forEach((option) => {
            if (!option.value) {
              option.textContent = card.product.sized && !several ? 'Veličina' : 'Odaberi';
              return;
            }
            const variant = this.findVariant(card, option.value);
            if (!variant) return;
            option.textContent =
              this.variantLabel(card.product, variant, { short: several }) + (variant.available ? '' : ' – rasprodano');
          });
          // Same natural order as the size buttons (her data sometimes has "6 – 9" last).
          if (card.product.sized) {
            options
              .filter((option) => option.value)
              .map((option, index) => ({ option, index, variant: this.findVariant(card, option.value) }))
              .sort((a, b) => {
                if (!a.variant || !b.variant) return a.index - b.index;
                return compareSort(a.variant.sizeSort, b.variant.sizeSort) || a.index - b.index;
              })
              .forEach(({ option }) => card.select.append(option));
          }
          if (card.product.colourSized || card.product.split) this.buildSizeGroups(card);
          // v2 (W11): the label above the list says "Veličina", so a size list starts with "Odaberite"
          const placeholder = [...card.select.options].find((option) => !option.value);
          if (placeholder && card.product.sized) placeholder.textContent = 'Odaberi';
          card.select.addEventListener('change', () => {
            const { value } = card.select;
            // "Boje bira Šigureca": a size ("g:<key>") stands for any available colour of it
            if (card.product.split) {
              // two pickers: the size, with the colour chosen so far
              card.splitSize = value ? value.slice(2) : null;
              const variant = value ? this.variantForGroup(card, value) : null;
              this.onVariantChange(card, variant ? variant.id : '', { colour: false });
            } else if (value.startsWith('g:')) {
              const variant = this.variantForGroup(card, value);
              this.onVariantChange(card, variant ? variant.id : '', { colour: false });
            } else {
              // several options in one list (Boja / Veličina / Print): her own choice of the whole variant
              if (card.product.options.length > 1) card.picked.variant = true;
              this.onVariantChange(card, value, { colour: card.product.options.length > 1 });
            }
            if (card.personalToggle) card.personalToggle.checked = card.select.value === card.personalToggle.dataset.togglePersonal;
            // Karlo 3. 10.: a size that completes the piece adds it (or saves the edit) as a colour tile does
            if (this.sheet && this.sheet.card === card && this.sheet.oneTap && this.inputModality !== 'keyboard') {
              this.scheduleOneTap(this.sheet);
            }
          });
        }
        // the vezica: „Personaliziraj · 20,00 €” sets the list to Personalizirana / Obična (the list stays hidden)
        const personalToggle = card.configEl && card.configEl.querySelector('[data-personal-toggle]');
        if (personalToggle && card.select) {
          card.personalToggle = personalToggle;
          // §19: the box says what the personalisation adds: „Personaliziraj · +6,00 €” (renderPersonalPrice)
          card.personalPriceEl = personalToggle.parentNode.querySelector('[data-personal-price]');
          this.renderPersonalPrice(card);
          // Karlo 3. 10. late: box → fabric / colour tiles → the name / letter fields (they open when the box is ticked)
          if (card.personalEl && card.personalEl !== personalToggle) {
            const colourPart = card.configEl.querySelector('[data-need="colour"]');
            (colourPart || personalToggle.closest('label')).after(card.personalEl);
          }
          personalToggle.addEventListener('change', () => {
            card.select.value = personalToggle.checked ? personalToggle.dataset.togglePersonal : personalToggle.dataset.togglePlain;
            // §21: a fabric picked before that is not made in the new type (the vezica: Obična = patterned,
            // Personalizirana = plain) is dropped, so she picks again – no hidden, stale pick
            const type = card.select.value.slice(2);
            if (
              card.splitColour &&
              !card.product.variants.some(
                (variant) =>
                  !variant.marta &&
                  variant.available &&
                  variant.options[card.product.colourIndex] === card.splitColour &&
                  this.sizeGroupKey(card.product, variant) === type
              )
            ) {
              card.splitColour = null;
              card.picked.colour = false;
              card.splitColours.forEach((radio) => {
                radio.checked = false;
              });
            }
            card.select.dispatchEvent(new Event('change'));
            // the fields open under the box, often below the window's fold: bring the first one into view
            if (personalToggle.checked) {
              window.setTimeout(() => {
                const body = this.sheetParts && this.sheetParts.body;
                const first = [...card.configEl.querySelectorAll('[data-need^="field"]')].find((part) => !part.hidden);
                if (!body || !first) return;
                const top = first.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 16;
                body.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
              }, 60);
            }
          });
          if (!card.select.value) {
            card.select.value = personalToggle.dataset.togglePlain;
            card.splitSize = personalToggle.dataset.togglePlain.slice(2);
          }
        }
        card.pills.forEach((pill) => {
          // Display name of the colour (from the section setting); the cart keeps her value. A photo tile has its
          // name in [data-tile-name], a pill as its first text.
          const label = this.querySelector(`label[for="${CSS.escape(pill.id)}"]`);
          const variant = this.findVariant(card, pill.value);
          const name = label ? label.querySelector('[data-tile-name]') : null;
          const text = label && !name ? [...label.childNodes].find((node) => node.nodeType === 3 && node.textContent.trim()) : null;
          if (variant && (name || text)) (name || text).textContent = this.variantLabel(card.product, variant);
          pill.addEventListener('change', () => {
            if (!pill.checked) return;
            // pills / tiles on a colour option (the puffy palette, the muslin's prints) or on another option (tetra, Vrsta)
            if (card.product.colourIndex !== -1) card.picked.colour = true;
            else card.picked.option = true;
            this.onVariantChange(card, pill.value, { pick: true });
          });
        });
        card.splitColours.forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            card.splitColour = radio.value;
            card.picked.colour = true;
            const variant = card.splitSize ? this.variantForGroup(card, `g:${card.splitSize}`) : null;
            this.onVariantChange(card, variant ? variant.id : '', { pick: true });
          });
        });
        card.fields.forEach((field) => this.bindField(card, field));
        // v2: the card's one button, and its photo (pointers only: "Više o proizvodu" is the keyboard's way)
        if (card.addButton) card.addButton.addEventListener('click', () => this.onAddPiece(card, card.addButton));
        const photo = card.el.querySelector('[data-more-photo]');
        if (photo) photo.addEventListener('click', () => this.openDialog(card, card.moreButton));
      });

      // Keyboard focus on a card's button or a chip: the page already keeps the focused control above the set bar
      // (scroll-padding); the whole card comes up when it fits (raiseAboveBar).
      this.addEventListener('focusin', (event) => this.raiseAboveBar(event.target));
    }

    /* ---------- dev-test-19: EasyFlow's fields (parseEasyflow above says how) ---------- */

    /**
     * Cards with EasyFlow's metafield (data-easyflow="1" from the card's Liquid) read their fields from the product
     * page (efLoad): when their step shows (efWarm) or at the latest when their window opens (efThen). Not in the
     * theme editor: there the "Polje uz proizvod" blocks are what she edits and previews.
     */
    initEasyflow() {
      this.efSections = new Map();
      this.efFirst = new Map();
      if (window.Shopify && window.Shopify.designMode) return;
      this.cards.forEach((card) => {
        if (!card.el || card.el.dataset.easyflow !== '1' || !card.configEl || !card.personalEl) return;
        card.ef = { state: 'idle', promise: null, template: card.el.dataset.template || '', notes: null, rules: [], waiting: false };
      });
    }

    /** EasyFlow's fields not read yet (neither applied nor given up). */
    efPending(card) {
      return Boolean(card.ef) && card.ef.state !== 'done' && card.ef.state !== 'failed';
    }

    /** A step shows: its EasyFlow products read their fields when the browser is idle. */
    efWarm(slotId) {
      const cards = (this.cards || []).filter((card) => card.slotId === slotId && card.ef && card.ef.state === 'idle');
      if (!cards.length) return;
      const idle = (fn) =>
        typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback(fn, { timeout: 1500 }) : window.setTimeout(fn, 200);
      idle(() => cards.forEach((card) => this.efLoad(card)));
    }

    /**
     * Before a window opens on an EasyFlow product whose fields are not read yet: wait for them (at most
     * EF_TIMEOUT_MS; the button says aria-busy), then `then`. Returns false when there is nothing to wait for.
     */
    efThen(card, opener, then) {
      if (!this.efPending(card)) return false;
      if (card.ef.waiting) return true;
      card.ef.waiting = true;
      const control = opener instanceof Element ? opener : card.addButton;
      if (control) control.setAttribute('aria-busy', 'true');
      this.efLoad(card).then(() => {
        card.ef.waiting = false;
        if (control) control.removeAttribute('aria-busy');
        then();
      });
      return true;
    }

    /** Read the product's EasyFlow fields once per page load; resolves when they are applied or given up. */
    efLoad(card) {
      if (!card.ef) return Promise.resolve();
      if (card.ef.promise) return card.ef.promise;
      card.ef.state = 'loading';
      const timeout = wait(EF_TIMEOUT_MS).then(() => ({ timeout: true }));
      const fetched = this.efFetch(card).then(
        (parsed) => ({ parsed }),
        (error) => ({ error })
      );
      card.ef.promise = Promise.race([fetched, timeout]).then((result) => {
        if (card.ef.state !== 'loading') return;
        if (result.timeout) {
          this.efFail(card, `nema odgovora u ${EF_TIMEOUT_MS / 1000} s`);
          return;
        }
        if (result.error) {
          this.efFail(card, String((result.error && result.error.message) || result.error));
          return;
        }
        const { parsed } = result;
        parsed.warnings.forEach((text) => console.warn(`Babyset: EasyFlow „${card.handle}”: ${text}`));
        if (parsed.problems.length) {
          this.efFail(card, `ne razumijem EasyFlow (${parsed.problems.join('; ')})`, true);
          return;
        }
        if (!parsed.fields.length) {
          this.efFail(card, parsed.roots ? 'EasyFlow nema polja' : 'na stranici proizvoda nema EasyFlow polja');
          return;
        }
        // a window that opened anyway (never with efThen): its fields stay as they are
        if (this.sheet && this.sheet.card === card) {
          this.efFail(card, 'prozor je već otvoren');
          return;
        }
        this.efApply(card, parsed);
      });
      return card.ef.promise;
    }

    /** The builder's own field blocks stay (page.babyset.json). Unknown markup or rules: a console warning. */
    efFail(card, reason, loud = false) {
      card.ef.state = 'failed';
      card.ef.notes = null;
      const text = `Babyset: EasyFlow „${card.handle}”: ${reason} – ostaju polja buildera.`;
      if (loud) console.warn(text);
      else console.debug(text);
    }

    /**
     * The product page's EasyFlow markup, parsed. The first product of a template reads the whole page and learns the
     * id of the section that holds EasyFlow (its main section); later products of that template ask the Section
     * Rendering API for only that section (/products/<handle>?section_id=…), and the whole page again only when the
     * section has no EasyFlow root.
     */
    async efFetch(card) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${root}products/${encodeURIComponent(card.handle)}`;
      const template = card.ef.template;
      const read = async (href) => {
        const response = await fetch(href, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return parseEasyflow(new DOMParser().parseFromString(await response.text(), 'text/html'));
      };
      let key = this.efSections.get(template);
      if (!key && this.efFirst.has(template)) {
        await this.efFirst.get(template).catch(() => null);
        key = this.efSections.get(template);
      }
      if (key) {
        const parsed = await read(`${url}?section_id=${encodeURIComponent(key)}`);
        if (parsed.roots) return parsed;
      }
      const page = read(url);
      if (!this.efFirst.has(template)) this.efFirst.set(template, page);
      const parsed = await page;
      if (parsed.sectionKey) this.efSections.set(template, parsed.sectionKey);
      return parsed;
    }

    /**
     * EasyFlow's fields replace the card's "Polje uz proizvod" fields: the same markup as snippets/babyset-card-v2.liquid
     * draws (label with „(obavezno)” / „(po želji)”, the list or the text field, the hint, the counter, the error
     * line), EasyFlow's hint (its description, else its placeholder), its max length and required flag. What she
     * typed in a builder field of the same name comes along, then the values saved before Back (restoreState).
     */
    efApply(card, parsed) {
      const holder = card.personalEl;
      const before = card.fields;
      const chevron = this.querySelector('.babyset-card__chevron');
      const base = `Babyset2-${this.dataset.sectionId}-ef${this.cards.indexOf(card)}`;
      holder.querySelectorAll('[data-field]').forEach((node) => node.remove());
      const fields = parsed.fields.map((spec) => {
        const id = `ef-${spec.optionId}`;
        const inputId = `${base}-${spec.optionId}`;
        const node = el('div', 'babyset-card__field bs2-config__part');
        node.dataset.field = id;
        node.dataset.need = `field:${id}`;
        node.dataset.fieldName = spec.name;
        node.dataset.fieldKind = spec.kind;
        node.dataset.fieldRequired = String(spec.required);
        node.dataset.easyflowField = spec.optionId;
        const label = el('label', 'babyset-card__option-label babyset-card__personal-label', spec.name);
        label.htmlFor = inputId;
        const state = el('span', 'babyset-card__field-state', spec.required ? ' (obavezno)' : ' (po želji)');
        state.dataset.fieldState = '';
        label.append(state);
        node.append(label);
        let input;
        if (spec.kind === 'choice') {
          const wrap = el('div', 'babyset-card__select-wrap');
          input = el('select', 'babyset-card__select babyset-card__field-select');
          input.append(new Option('Odaberi', ''));
          spec.choices.forEach((choice) => input.append(new Option(choice.title, choice.title)));
          wrap.append(input);
          if (chevron) wrap.append(chevron.cloneNode(true));
          node.append(wrap);
        } else {
          // the limit counts characters as she sees them (data-max-length); maxlength only stops a huge paste
          input = el('input', 'babyset-card__text');
          input.type = 'text';
          input.dataset.maxLength = String(spec.max);
          input.maxLength = spec.max * 8;
          input.autocomplete = 'off';
          input.setAttribute('autocapitalize', 'sentences');
          input.spellcheck = false;
          input.setAttribute('enterkeyhint', 'done');
          node.append(input);
        }
        input.id = inputId;
        input.dataset.fieldInput = '';
        const described = [];
        if (spec.hint) {
          const hint = el('p', 'babyset-card__field-hint', spec.hint);
          hint.id = `${inputId}-hint`;
          node.append(hint);
          described.push(hint.id);
        }
        let count = null;
        if (spec.kind !== 'choice') {
          count = el('p', 'babyset-card__field-count');
          count.id = `${inputId}-count`;
          count.dataset.fieldCount = '';
          count.hidden = true;
          node.append(count);
          described.push(count.id);
        }
        if (described.length) input.setAttribute('aria-describedby', described.join(' '));
        const error = el('p', 'bs2-config__error');
        error.id = `${inputId}-err`;
        error.dataset.needError = '';
        error.hidden = true;
        node.append(error);
        holder.append(node);
        return {
          id,
          name: spec.name,
          cartName: spec.cartName,
          kind: spec.kind,
          required: spec.required,
          max: spec.kind === 'text' ? spec.max : 100,
          el: node,
          input,
          count,
          value: '',
          ef: true,
          card,
          optionId: spec.optionId,
          choices: spec.choices,
          variantIds: spec.variantIds ? new Set(spec.variantIds) : null,
        };
      });
      card.fields = fields;
      card.efFields = true;
      card.ef.rules = parsed.rules;
      card.ef.state = 'done';
      before.forEach((field) => {
        if (!field.value) return;
        const same = fields.find((item) => item.name.toLowerCase() === field.name.toLowerCase());
        if (same) this.setFieldValue(same, field.value);
      });
      const { notes } = card.ef;
      card.ef.notes = null;
      if (notes) {
        fields.forEach((field) => {
          if (notes[field.id] != null) this.setFieldValue(field, notes[field.id]);
        });
      }
      fields.forEach((field) => this.bindField(card, field));
      this.update();
    }

    /** The variants a card can still become (its colour and type picked so far), without "Šigureca bira". */
    efCandidates(card) {
      const { product } = card;
      return product.variants.filter((variant) => {
        if (variant.marta) return false;
        if (product.split) {
          if (card.splitColour && variant.options[product.colourIndex] !== card.splitColour) return false;
          if (card.splitSize && this.sizeGroupKey(product, variant) !== card.splitSize) return false;
        }
        return true;
      });
    }

    /** A Shopify option's value for EasyFlow's rules: the variant's, else what the card knows so far. */
    efShopifyValue(card, name) {
      const { product } = card;
      const index = product.options.indexOf(name);
      if (index === -1) return undefined;
      if (card.variant) return card.variant.options[index];
      if (product.split) return (index === product.colourIndex ? card.splitColour : card.splitSize) || undefined;
      return undefined;
    }

    /** EasyFlow's rules on this card now: { [optionId]: 'All' | [hidden values] } (efHiddenMap). */
    efHidden(card) {
      const rules = card.ef && card.ef.rules;
      if (!rules || !rules.length) return {};
      const byOption = new Map(card.fields.map((field) => [field.optionId, field]));
      return efHiddenMap(
        rules,
        card.fields,
        (optionId) => {
          const field = byOption.get(optionId);
          if (!field || !field.value) return [];
          return field.kind === 'choice' ? [field.value] : [null];
        },
        (name) => this.efShopifyValue(card, name)
      );
    }

    /**
     * Does an EasyFlow field apply now? Its root's data-variant-ids (no variant yet: any variant the card can still
     * become – the vezica's „Personaliziraj” box decides) and EasyFlow's rules.
     */
    efFieldOn(card, field) {
      if (!card) return true;
      if (field.variantIds) {
        if (card.variant) {
          if (!field.variantIds.has(String(card.variant.id))) return false;
        } else {
          const candidates = this.efCandidates(card);
          if (candidates.length && !candidates.some((variant) => field.variantIds.has(String(variant.id)))) return false;
        }
      }
      if (!card.ef || !card.ef.rules || !card.ef.rules.length) return true;
      const hidden = this.efHidden(card)[field.optionId];
      if (!hidden) return true;
      if (hidden === 'All') return false;
      return field.kind !== 'choice' || field.choices.some((choice) => !hidden.includes(choice.title));
    }

    /** EasyFlow's rules may hide single values of a list: those leave the list (and a hidden choice is cleared). */
    efSyncChoices(card) {
      if (!card.ef || !card.ef.rules || !card.ef.rules.length) return;
      const hidden = this.efHidden(card);
      card.fields.forEach((field) => {
        if (field.kind !== 'choice') return;
        const off = Array.isArray(hidden[field.optionId]) ? hidden[field.optionId] : [];
        [...field.input.options].forEach((option) => {
          if (!option.value) return;
          const gone = off.includes(option.value);
          option.hidden = gone;
          option.disabled = gone;
        });
        if (field.value && off.includes(field.value)) this.setFieldValue(field, '');
      });
    }

    /** A field's input (the "Polje uz proizvod" blocks; dev-test-19: also EasyFlow's, efApply). */
    bindField(card, field) {
      const onValue = () => {
        if (field.kind === 'text' && charCount(field.input.value) > field.max) {
          // the limit counts characters as she sees them (an emoji is one), so maxlength cannot do it
          const caret = field.input.selectionStart;
          field.input.value = truncateChars(field.input.value, field.max);
          if (caret != null) {
            const at = Math.min(caret, field.input.value.length);
            try {
              field.input.setSelectionRange(at, at);
            } catch (error) {
              // not every input type supports a selection
            }
          }
        }
        field.value =
          field.kind === 'choice' ? String(field.input.value || '') : this.cleanText(field.input.value, field.max);
        // v2: a field never adds its piece (only the window's button does)
        if (this.isChosen(card) && this.isErrorCard(card)) this.clearError();
        this.update();
      };
      field.input.addEventListener(field.kind === 'choice' ? 'change' : 'input', onValue);
      if (field.kind === 'text') {
        field.input.addEventListener('change', () => {
          field.input.value = field.value;
        });
        field.input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || event.isComposing) return;
          event.preventDefault();
          field.input.value = field.value;
          // Touch: "done" closes the on-screen keyboard – the focus moves to the field's box, so it is not lost to
          // the page (v1). A keyboard: Enter confirms the window, like its button (with its checks).
          if (window.matchMedia('(pointer: coarse)').matches) {
            if (!field.el.hasAttribute('tabindex')) field.el.setAttribute('tabindex', '-1');
            field.el.focus({ preventScroll: true });
          } else if (this.sheet && this.sheet.card === card) {
            this.confirmSheet();
          }
        });
      }
    }

    /** Any new choice ends the "Uklonjeno iz seta …" notice (its undo would no longer be what she expects). */
    clearTransient() {
      this.addedNotice = null;
      this.clearNotice();
      this.clearError();
    }

    /**
     * Run `change` without moving `element` on the screen: what a click changes above it (a card's lines, a part's
     * message) would push it down. WebKit anchors the scroll position itself (then nothing is left to do here); Chrome
     * does not inside the builder's fieldsets. Measured after a forced layout, so a browser's own anchoring counts.
     */
    keepInPlace(element, change) {
      const box = element && element.isConnected ? element.getBoundingClientRect() : null;
      change();
      if (!box || (!box.width && !box.height) || !element.isConnected) return;
      const now = element.getBoundingClientRect();
      if (!now.width && !now.height) return;
      const delta = now.top - box.top;
      if (Math.abs(delta) >= 0.5) window.scrollBy(0, delta);
    }

    /** The bottom of a sticky header over the page (0 without one; her header is not sticky). */
    headerBottom() {
      const header = document.querySelector('.section-header');
      if (!header) return 0;
      const position = window.getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return 0;
      return Math.max(0, header.getBoundingClientRect().bottom);
    }

    /**
     * Scroll a box into the room between a sticky header and the set bar only when it is not fully in it, and only as
     * far as needed (its top just under the top edge, or its bottom just above the bar), so "Dodaj u set" below it
     * stays in view where it can.
     */
    revealInView(box) {
      if (!box) return;
      const rect = box.getBoundingClientRect();
      const top = this.headerBottom() + 16;
      const bottom = window.innerHeight - (this.pageBarSpace || 0) - 16;
      if (rect.top >= top && rect.bottom <= bottom) return;
      const delta = rect.top < top || rect.height > bottom - top ? rect.top - top : rect.bottom - bottom;
      window.scrollTo({
        top: Math.max(0, window.scrollY + delta),
        behavior: reducedMotion() || this.inputModality === 'keyboard' ? 'auto' : 'smooth',
      });
    }

    /**
     * Size, colour or option of a card (in the window): it changes only that card. While a window is open nothing else
     * changes: a tried colour does not end a pending "Poništi" (clearError only), the part's message waits, and update()
     * renders the window only. pick: a tile, pill or palette choice – with a pointer in a one-tap window it adds the
     * piece 180 ms later (V2-SPEC §2.7; the keyboard's arrows only move the choice).
     */
    onVariantChange(card, id, { colour = true, pick = false } = {}) {
      const sheet = this.sheet && this.sheet.card === card ? this.sheet : null;
      const oneTap = Boolean(sheet) && sheet.oneTap && pick && this.inputModality === 'pointer';
      if (this.sheet) this.clearError();
      else this.clearTransient();
      const variant = this.findVariant(card, id);
      // a size chosen in "Boje bira Šigureca" is not a colour choice (the main photo stays)
      if (colour) card.colourPicked = true;
      card.variant = variant && variant.available ? variant : null;
      if (card.variant && !card.variant.marta) card.ownVariant = card.variant;
      card.combo = '';
      this.syncSplit(card);
      this.setSizeFit(card);
      if (card.variant) card.reason = null;
      this.showVariantImage(card);
      if (!this.sheet && this.isChosen(card)) this.setSlotMessage(card.slotId, '');
      this.update();
      if (oneTap) this.scheduleOneTap(sheet);
    }

    /**
     * "Dodaj u set" on a card (V2-SPEC §2.6): in the set → the window in edit mode; in the set in another part (N9), or
     * the part is full (N8) → v1's message in the bar's notice row, nothing opens; nothing missing → the piece goes in at
     * once (then the next step after ADVANCE_MS, scheduleAdvance); otherwise the window asks for what is missing.
     */
    onAddPiece(card, opener) {
      if (this.sheet || this.busy) return;
      // dev-test-19: EasyFlow's fields first (the window asks for them), at most EF_TIMEOUT_MS
      if (this.efThen(card, opener || card.addButton, () => this.onAddPiece(card, opener))) return;
      const slot = this.slotById(card.slotId);
      if (!slot || !card.product.available) return;
      if (this.isChosen(card)) {
        this.openSheet(card, 'edit', opener);
        return;
      }
      const elsewhere = this.chosenElsewhere(card);
      const single = !slot.multi || slot.max === 1;
      let text = '';
      if (elsewhere) text = this.elsewhereText(card, elsewhere);
      else if (!single && this.selection[slot.id].length >= slot.limit) {
        text = `U dijelu „${slot.name}“ može biti najviše ${pieces(slot.limit)}. Ukloni jedan iz seta pa dodaj novi.`;
      }
      if (text) {
        this.notify({ text });
        this.update();
        // the notice makes the bar taller: the button she pressed stays above it
        this.keepAboveBar(opener || card.addButton);
        return;
      }
      this.setSlotMessage(slot.id, '');
      this.forgetColour(card);
      const ready = Boolean(card.variant) && card.variant.available && !this.missingField(card);
      if (ready && !this.sheetNeeds(card).length) {
        const added = this.commitAdd(card);
        // the button keeps the focus ("U setu · Uredi"); Safari does not focus a tapped button by itself
        if (card.addButton && document.activeElement !== card.addButton) card.addButton.focus({ preventScroll: true });
        this.keepAboveBar(card.addButton);
        if (added && this.lastCommit === 'add') this.scheduleAdvance(card);
        return;
      }
      this.openSheet(card, 'add', opener);
    }

    /**
     * Karlo 3. 10.: "Dodaj u set" on a piece that is not in the set always asks its colour again – no first variant's
     * or earlier pick's colour goes in by itself. The colour tiles are cleared, so any tile (also the same one) picks.
     */
    forgetColour(card) {
      const part = card.configEl && card.configEl.querySelector('[data-need="colour"]');
      if (!part || this.hidesColour(card) || card.product.single) return;
      card.picked.colour = false;
      part.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = false;
      });
    }

    /** A control stays above the set bar after the bar grew (a notice row): the page moves only as far as needed. */
    keepAboveBar(control) {
      if (!control || !control.isConnected) return;
      this.updatePageBarSpace();
      const rect = control.getBoundingClientRect();
      const bottom = window.innerHeight - (this.pageBarSpace || 0) - 12;
      if (rect.height && rect.top < window.innerHeight && rect.bottom > bottom) window.scrollBy({ top: rect.bottom - bottom, behavior: 'auto' });
    }

    /**
     * The piece goes into the set (at once, or confirmed in the window). A part of one piece swaps its piece (v1): the
     * notice N2 with "Poništi" (the piece before comes back with its colour). An add has no visible notice: the live
     * region says "Dodano u set: …" (N1). lastCommit: 'add' or 'replace' (only an add moves on to the next step).
     */
    commitAdd(card) {
      const slot = this.slotById(card.slotId);
      this.lastCommit = '';
      if (!slot || this.isChosen(card)) return false;
      const before = this.snapshot();
      this.clearTransient();
      const list = this.selection[slot.id];
      let replaced = null;
      if (!slot.multi || slot.max === 1) {
        replaced = this.chosenCards(slot.id).find((other) => other !== card) || null;
        this.selection[slot.id] = [card.handle];
      } else if (list.length < slot.limit) {
        list.push(card.handle);
      } else {
        return false;
      }
      this.skipped.delete(slot.id);
      this.setSlotMessage(slot.id, '');
      if (replaced) {
        this.notify({
          text: `U setu je sada ${card.product.title}. Uklonjeno: ${replaced.product.title}.`,
          undoLabel: 'Poništi zamjenu',
          before,
          kind: 'replace',
        });
      } else {
        this.liveNote = this.addedNote(card);
      }
      this.lastCommit = replaced ? 'replace' : 'add';
      this.update();
      return true;
    }

    /** N1: "Dodano u set: Puffy dekica (Svijetlo roza)." */
    addedNote(card) {
      const label = this.pieceLabel(card);
      return `Dodano u set: ${card.product.title}${label ? ` (${label})` : ''}.`;
    }

    /**
     * After an add: ADVANCE_MS later the next step (the last part, or a part opened from Pregled: Pregled). The live
     * text waits for it, so a screen reader hears one sentence: "Dodano u set: … Korak 3 od 5: Gornji dio." Only from a
     * part's step; cancelled by any step change, a window, "Više o proizvodu", the bar's list, Undo or a removal.
     */
    scheduleAdvance(card) {
      this.cancelAdvance({ say: true });
      const step = (this.steps || [])[this.stepIndex];
      if (!step || !step.slot || step.slot.id !== card.slotId || !this.isChosen(card)) return;
      // Karlo 3. 10.: a part that takes more pieces moves on only once it is full (its "Najviše N" from the editor)
      const slot = this.slotById(card.slotId);
      // Karlo 4. 10.: full = min(max, the part's products) (slot.limit), so „Za glavu” with 2 products moves on at 2
      if (slot && slot.multi && slot.limit > 1 && (this.selection[slot.id] || []).length < slot.limit) return;
      // the live text of the add waits for the step change (one sentence, no "Tvoj set · …" first)
      window.clearTimeout(this.liveTimer);
      const pending = { card, from: this.stepIndex, note: this.addedNote(card), timer: 0 };
      pending.timer = window.setTimeout(() => this.runAdvance(pending), ADVANCE_MS);
      this.pendingAdvance = pending;
    }

    /** A cancelled advance still says what was added (the live region; nothing visible). */
    cancelAdvance({ say = true } = {}) {
      const pending = this.pendingAdvance;
      if (!pending) return;
      window.clearTimeout(pending.timer);
      this.pendingAdvance = null;
      if (say && this.live && this.isChosen(pending.card)) this.live.textContent = pending.note;
    }

    runAdvance(pending) {
      if (this.pendingAdvance !== pending) return;
      this.pendingAdvance = null;
      const steps = this.steps || [];
      const panelOpen = Boolean(this.panel) && !this.panel.hidden;
      const blocked =
        !this.isConnected ||
        this.stepIndex !== pending.from ||
        Boolean(this.sheet) ||
        this.busy ||
        (this.dialog && this.dialog.open) ||
        panelOpen ||
        !this.isChosen(pending.card);
      if (blocked) {
        if (this.live && this.isChosen(pending.card)) this.live.textContent = pending.note;
        return;
      }
      const last = steps.length - 1;
      const next = this.fromReview ? last : Math.min(this.stepIndex + 1, last);
      const total = steps.length - 2;
      const target = steps[next];
      const where = target && target.slot ? `Korak ${next} od ${total}: ${target.slot.name}.` : `${this.texts.reviewHeading || 'Pregled seta'}.`;
      this.liveNote = `${pending.note} ${where}`;
      this.goToStep(next);
    }

    /** A piece leaves the set ("Ukloni iz seta", "×", "Ukloni"): the notice N3 with "Poništi". */
    removePiece(slotId, handle) {
      const card = this.cards.find((item) => item.slotId === slotId && item.handle === handle);
      const before = this.snapshot();
      this.clearTransient();
      this.selection[slotId] = (this.selection[slotId] || []).filter((item) => item !== handle);
      this.setSlotMessage(slotId, '');
      if (card) {
        this.notify({ text: `Uklonjeno iz seta: ${card.product.title}.`, undoLabel: 'Poništi uklanjanje', before, kind: 'remove' });
      }
      this.update();
    }

    snapshot() {
      const selection = {};
      Object.entries(this.selection).forEach(([slotId, handles]) => {
        selection[slotId] = [...handles];
      });
      return {
        size: this.size,
        selection,
        skipped: [...this.skipped],
        martaMode: this.martaMode,
        forChoice: this.forChoice,
        cards: this.cards.map((card) => ({
          variant: card.variant,
          override: card.override,
          near: card.near,
          reason: card.reason,
          shown: card.shown,
          combo: card.combo,
          splitColour: card.splitColour,
          splitSize: card.splitSize,
          ownVariant: card.ownVariant,
          colourPicked: card.colourPicked,
          picked: { ...card.picked },
        })),
      };
    }

    restoreSnapshot(state) {
      this.size = state.size;
      this.selection = {};
      Object.entries(state.selection).forEach(([slotId, handles]) => {
        this.selection[slotId] = [...handles];
      });
      this.skipped = new Set(state.skipped);
      state.cards.forEach((saved, index) => Object.assign(this.cards[index], saved, { picked: { ...saved.picked } }));
      if (this.colourMode.enabled && 'martaMode' in state) {
        this.martaMode = state.martaMode;
        this.forChoice = state.forChoice;
        this.classList.toggle('is-colour-mode', this.martaMode);
        this.syncModeInputs();
      }
      this.syncSizeInputs();
      this.slots.forEach((slot) => this.setSlotMessage(slot.id, ''));
      // „Poništi” brings back another set: no „Set više nije cijeli …” for a ready set that is no longer there
      this.presetDealLast = null;
    }

    syncSizeInputs() {
      (this.sizeInputs || []).forEach(({ input, size }) => {
        input.checked = size.key === this.size;
      });
    }

    /**
     * silent: restoring a saved set (no notice). Otherwise a size that takes chosen pieces out of the set
     * says so where the customer is looking (bar / sidebar) and offers "Poništi".
     * Sizes tried one after another (arrow keys walk through the radios, every step is a "change") all start from
     * the set before the first of them: a piece taken out at 9–12 comes back at 18–24, and "Poništi" still goes back
     * to where she started. Any other choice ends the series (clearTransient drops the undo state).
     * A piece keeps its other options (colour, Personalizirana, print); when the new size has the piece only in
     * another colour or type, a chosen piece leaves the set with the notice instead of silently changing (the card
     * shows the closest variant, and "Poništi" brings the piece, its typed letter and print back).
     */
    applySize(key, { silent = false } = {}) {
      const base = silent ? null : this.undoState;
      if (base) {
        // the series starts from the set before its first size: the ready set it was stays known for the line
        const last = this.presetDealLast;
        this.restoreSnapshot(base);
        this.presetDealLast = last;
      }
      const before = silent ? null : base || this.snapshot();
      this.clearTransient();
      this.size = key;
      const label = this.sizeLabel(key);
      const comboLost = new Set();
      this.cards.forEach((card) => {
        const { product } = card;
        if (!product.available) return;
        if (!product.sized) return; // e.g. the dekica: size-independent, keeps its own colour
        card.override = false;
        card.near = false;
        card.combo = '';
        const { exact, near } = this.sizeMatches(product, key);
        // a split card with a colour but no size yet keeps that colour; "Šigureca bira" counts as no colour
        const own = card.variant && !card.variant.marta ? card.variant : null;
        const current =
          own ||
          (product.split && card.splitColour
            ? { options: product.options.map((_, index) => (index === product.colourIndex ? card.splitColour : null)) }
            : null);
        // "Boje neka izabere Šigureca" (trakica Boja / Veličina): the colour is hers, so it never takes a piece out
        // of the set – the same colour is only preferred
        const free = this.hidesColour(card) ? product.colourIndex : -1;
        const counts = (index) => index !== product.sizeIndex && index !== free;
        const others = current ? current.options.filter((_, index) => counts(index)).length : 0;
        // how many of the current variant's other options (colour, type, print) a variant keeps
        const kept = (variant) =>
          current ? variant.options.filter((value, index) => counts(index) && value === current.options[index]).length : 0;
        const sameFree = (variant) =>
          free !== -1 && current && variant.options[free] === current.options[free] ? 0.5 : 0;
        const pickFrom = (list) => {
          let best = null;
          let bestKept = -1;
          let bestScore = -1;
          list.forEach((variant) => {
            if (!variant.available) return;
            const score = kept(variant) + sameFree(variant);
            if (score > bestScore) {
              best = variant;
              bestScore = score;
              bestKept = kept(variant);
            }
          });
          return best ? { variant: best, whole: bestKept === others } : null;
        };
        // prefer the exact size with every other option kept, then a wider size with every option kept, then
        // whatever keeps the most
        const exactPick = pickFrom(exact);
        const nearPick = pickFrom(near);
        let pick = exactPick;
        let isNear = false;
        if (!pick || (!pick.whole && nearPick && nearPick.whole)) {
          pick = nearPick || exactPick;
          isNear = Boolean(nearPick) && pick === nearPick;
        }
        if (pick) {
          if (current && !pick.whole) {
            card.combo = this.otherOptionsLabel(product, current, pick.variant);
            if (this.isChosen(card)) comboLost.add(card);
          }
          card.variant = this.martaFor(card, pick.variant);
          card.reason = null;
          card.near = isNear;
          if (product.split && !this.hidesColour(card) && !card.splitColour) {
            // her colour is still to pick: the card shows the size only
            card.splitSize = this.sizeGroupKey(product, pick.variant);
            card.variant = null;
          }
          this.syncSplit(card);
          if (card.variant && !card.variant.marta) card.ownVariant = card.variant;
          this.showVariantImage(card);
        } else {
          card.variant = null;
          card.reason = exact.length || near.length ? 'soldout' : 'missing';
          // a split card (colour buttons + size list) drops its old size too: its list and its status follow the
          // set size like every other card ("Nema u veličini …"), not "<boja> – rasprodano u veličini <old size>"
          if (product.split) card.splitSize = null;
        }
      });

      const dropped = [];
      this.slots.forEach((slot) => {
        const keep = [];
        let lost = 0;
        this.chosenCards(slot.id).forEach((card) => {
          if (this.isDisabled(card) || comboLost.has(card)) {
            dropped.push({ slot, card });
            lost += 1;
          } else {
            keep.push(card.handle);
          }
        });
        this.selection[slot.id] = keep;
        this.setSlotMessage(
          slot.id,
          lost
            ? `${lost === 1 ? 'Odabrani komad nije dostupan' : 'Neki odabrani komadi nisu dostupni'} u veličini ${label} – odaberi drugi.`
            : ''
        );
      });
      this.syncSizeInputs();
      if (dropped.length && before) this.showNotice(dropped, label, before);
      this.update();
    }

    /* ---------- notice: pieces taken out by a new size ---------- */

    initNotice() {
      this.notices = [...this.querySelectorAll('[data-notice]')];
      this.noticeKind = '';
      this.noticeUndo = null;
      this.notices.forEach((notice) => {
        const undo = notice.querySelector('[data-notice-undo]');
        if (undo) undo.addEventListener('click', () => this.undo());
      });
    }

    /** v1's notice of a new set size that took pieces out (N4), now through notify; applySize keeps its series rule. */
    showNotice(dropped, label, before) {
      const names = dropped.map(({ card }) => (card.combo ? `${card.product.title} (${card.combo})` : card.product.title)).join(', ');
      this.dropped = new Set(dropped.map(({ slot }) => slot.id));
      // sizes tried one after another all start from the set before the first (applySize reads undoState, v1)
      this.undoState = before;
      this.notify({
        text: `Uklonjeno iz seta: ${names} – nije dostupno u veličini ${label}.`,
        undoLabel: 'Poništi promjenu veličine',
        before,
        kind: 'size',
      });
    }

    /** The notice goes (the next change of the set, a step change, "Poništi"); its undo goes with it. */
    clearNotice() {
      if (!this.noticeText && !this.dropped.size && !this.undoState && !this.noticeUndo) return;
      this.noticeText = '';
      this.noticeKind = '';
      this.noticeUndo = null;
      this.dropped = new Set();
      this.undoState = null;
      (this.notices || []).forEach((notice) => {
        notice.hidden = true;
      });
    }

    /**
     * One notice in the bar and on the start screen (V2-SPEC §2.9, §3.2): N2 (replaced), N3 (removed), N4 (a new size
     * took pieces out), N5 / N6 (the colour mode) with "Poništi" – its name says what it undoes ("Poništi zamjenu") –
     * and the messages without an undo (N8, N9, T10). It stays until the next change of the set, a step change or
     * "Poništi": never on a timer, and trying choices in an open window does not end it.
     */
    notify({ text, undoLabel = '', before = null, kind = '' }) {
      this.noticeText = text;
      this.noticeKind = kind;
      this.noticeUndo = before;
      (this.notices || []).forEach((notice) => {
        const textEl = notice.querySelector('[data-notice-text]');
        if (textEl && textEl.textContent !== text) textEl.textContent = text;
        const undo = notice.querySelector('[data-notice-undo]');
        if (undo) {
          undo.hidden = !before;
          undo.setAttribute('aria-label', undoLabel || 'Poništi');
        }
        notice.hidden = false;
      });
    }

    /**
     * "Poništi": the set as before the change (the snapshot), "Poništeno." for screen readers (N12). A focus that was on
     * the notice's button goes to the bar's "Tvoj set" on the steps (v1), on the start screen to the changed answer.
     */
    undo() {
      const state = this.noticeUndo || this.undoState;
      if (!state) return;
      const kind = this.noticeKind;
      const focusBack = (this.notices || []).some((notice) => notice.contains(document.activeElement));
      this.clearNotice();
      this.restoreSnapshot(state);
      this.liveNote = 'Poništeno.';
      this.update();
      if (!focusBack) return;
      let target = null;
      if (this.stepIndex === 0) {
        if (kind === 'mode') target = (this.modeRadios || []).find((radio) => radio.checked) || null;
        else target = (this.sizeInputs || []).map(({ input }) => input).find((input) => input.checked) || null;
        target = target || this.titleEl;
      } else {
        target =
          (this.toggle && this.toggle.getBoundingClientRect().width > 0 && this.toggle) ||
          this.addButtons.find((button) => !button.hidden) ||
          null;
      }
      if (target) target.focus({ preventScroll: true });
    }

    setSlotMessage(slotId, text) {
      const slot = this.slotById(slotId);
      const target = slot && slot.el ? slot.el.querySelector('[data-slot-message]') : null;
      // a status region: rewriting the same text would make screen readers announce it again
      if (target && target.textContent !== text) target.textContent = text;
    }

    scrollToElement(element, offset) {
      if (!element) return;
      const top = element.getBoundingClientRect().top + window.scrollY - (offset == null ? 24 : offset);
      window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /** A part's step (from Pregled's list: its step button then says "Pregled seta"). */
    focusSlot(slotId) {
      this.closePanel();
      const index = this.stepIndexOf(slotId);
      if (index === -1) return;
      this.goToStep(index, { fromReview: this.isReview() });
    }

    /** A piece that still needs something: its step, and the piece window in edit mode. */
    focusCard(card) {
      this.closePanel();
      const index = this.stepIndexOf(card.slotId);
      if (index !== -1 && index !== this.stepIndex) this.goToStep(index, { focus: false, fromReview: this.isReview() });
      if (this.efThen(card, card.addButton, () => this.openSheet(card, 'edit', card.addButton))) return;
      this.openSheet(card, 'edit', card.addButton);
    }

    /** The baby's age (the set size): the start screen, the focus on the chosen chip or the first one. */
    focusSizePicker() {
      this.closePanel();
      this.goToStep(0, { focus: false, scroll: false });
      const inputs = (this.sizeInputs || []).map(({ input }) => input);
      const target = inputs.find((input) => input.checked) || inputs[0];
      if (target) target.focus();
    }

    /**
     * The locked main button ("Odaberi još: Dekica") takes the customer to what is still missing (v1's order). A
     * required part without a piece: its step, with its first card's button focused and above the set bar for every
     * input (V2-SPEC §2.4, §4.6). The step change and the reveal are one scroll, measured from the step's top
     * (revealCard from stepScrollTop): where the card fits under the step's header the page ends there, as on any step
     * change; on a small phone it goes on until the card, or its button, is above the bar. A smooth step scroll followed
     * by the focus would end with the button under the bar (the keyboard's raiseAboveBar looks before the scroll ends).
     */
    goToMissing() {
      const pieces = this.chosenPieces();
      const empty = this.slots.filter((slot) => slot.required && !this.chosenCards(slot.id).length);
      const soldOut = empty.find((slot) => !this.cards.some((card) => card.slotId === slot.id && card.product.available));
      // Karlo 4. 10.: from Pregled, adding the missing piece returns to Pregled (fromReview)
      const fromReview = this.isReview();
      if (soldOut) {
        this.goToStep(this.stepIndexOf(soldOut.id), { fromReview });
        return;
      }
      if (empty.length) {
        const slot = empty[0];
        const index = this.stepIndexOf(slot.id);
        const card = this.cards.find((item) => item.slotId === slot.id && item.product.available && item.addButton);
        if (!card || index < 1) {
          this.goToStep(index, { fromReview });
          return;
        }
        this.goToStep(index, { focus: false, scroll: false, fromReview });
        // the step's bar now (Pregled's rows are gone; the bar's ResizeObserver tells later)
        this.updatePageBarSpace();
        this.revealCard(card.el, card.addButton, { from: this.stepScrollTop(this.stepIndex) });
        card.addButton.focus({ preventScroll: true });
        return;
      }
      if (this.needsFor()) {
        this.focusFor();
        return;
      }
      if (this.needsSetSize()) {
        this.focusSizePicker();
        return;
      }
      const incomplete = pieces.find(({ card }) => !card.variant) || pieces.find(({ card }) => this.needsPersonal(card));
      if (incomplete) {
        this.focusCard(incomplete.card);
        return;
      }
      if (!pieces.length) this.goToStep(1);
    }

    /* ---------- size picker ---------- */

    /**
     * Question 1 of the start screen: the size chips (radios) in the Liquid fieldset's host, each with its full label
     * ("0–3 mjeseca"). No age sizes (after the size filter): no question 1, and question 2 loses its number.
     */
    renderSizePicker() {
      this.sizeInputs = [];
      this.sizeQuestion = this.querySelector('[data-size-question]');
      this.sizeBadge = this.querySelector('[data-size-badge]');
      const host = this.querySelector('[data-size-picker]');
      if (!this.useGlobalSize || !host) {
        if (this.sizeQuestion) this.sizeQuestion.remove();
        this.sizeQuestion = null;
        this.querySelectorAll('[data-question-number]').forEach((number) => number.remove());
        return;
      }
      const name = `Babyset2-size-${this.dataset.sectionId}`;
      host.textContent = '';
      this.sizeList.forEach((size, index) => {
        const id = `${name}-${index}`;
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.id = id;
        input.value = size.key;
        input.className = 'babyset-size__input visually-hidden';
        const label = el('label', 'babyset-size__chip');
        label.htmlFor = id;
        // the note says when a required part has nothing in that size (updateSizeChips)
        const note = el('span', 'visually-hidden');
        label.append(el('span', '', size.label), note);
        input.addEventListener('change', () => {
          if (input.checked) this.applySize(size.key);
        });
        host.append(input, label);
        this.sizeInputs.push({ input, label, note, size });
      });
      this.sizeHost = host;
    }

    /** Mark sizes in which some required part has nothing available (size-less products always count). */
    updateSizeChips() {
      if (!this.sizeInputs) return;
      const required = this.slots.filter((slot) => slot.required);
      this.sizeInputs.forEach(({ label, note, size }) => {
        const limited = required.some(
          (slot) =>
            !this.cards.some((card) => {
              if (card.slotId !== slot.id || !card.product.available) return false;
              if (!card.product.sized) return true;
              const { exact, near } = this.sizeMatches(card.product, size.key);
              return [...exact, ...near].some((variant) => variant.available);
            })
        );
        label.classList.toggle('is-limited', limited);
        note.textContent = limited ? ' (nije dostupno za sve obavezne dijelove)' : '';
      });
    }

    /* ---------- gift for mum ---------- */

    initGift() {
      this.giftRoot = this.querySelector('[data-gift]');
      this.giftPicker = this.querySelector('[data-gift-picker]');
      this.giftStatus = this.querySelector('[data-gift-status]');
      this.giftRadios = [...this.querySelectorAll('[data-gift-radio]')];
      this.giftMeters = [...this.querySelectorAll('[data-gift-meter]')];
      this.giftRadios.forEach((radio) => {
        const label = this.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        const name = label ? label.querySelector('[data-gift-name]') : null;
        if (name) name.textContent = this.optionLabel(radio.value);
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          this.giftChoice = radio.value;
          this.update();
          // the set is already in the cart and became gift-worthy there: write the colour onto it
          if (this.lateGift) this.writeLateGift();
        });
      });
      this.addEventListener('click', (event) => {
        const go = event.target.closest('[data-gift-go]');
        if (!go) return;
        event.preventDefault();
        this.focusGift();
      });
    }

    /** The set value above the threshold (strictly: "kada vrijednost pređe 80 €") unlocks the scrunchie. */
    giftState(totals, count) {
      const { enabled, threshold } = this.gift;
      if (!enabled) return { enabled: false, reached: false, remaining: 0, ratio: 0 };
      const value = totals.subtotal;
      const reached = count > 0 && value > threshold;
      // Full only when the gift is unlocked: at exactly 80,00 € the meter still looks "almost there".
      const ratio = reached ? 1 : threshold > 0 ? Math.min(LOCKED_MAX_RATIO, value / threshold) : 0;
      return { enabled: true, reached, remaining: Math.max(0, threshold - value), ratio };
    }

    giftColourLabel() {
      return this.giftChoice ? this.optionLabel(this.giftChoice) : '';
    }

    /** "Gumica – Mint" for the order (her colour name), or null while the gift is locked. */
    giftValue() {
      if (!this.giftNow || !this.giftNow.reached) return null;
      if (!this.giftRadios.length) return this.gift.name;
      return `${this.gift.name} – ${this.giftChoice || 'boja nije odabrana'}`;
    }

    giftImage() {
      const radio = this.giftRadios.find((item) => item.value === this.giftChoice);
      const label = radio ? this.querySelector(`label[for="${CSS.escape(radio.id)}"]`) : null;
      const img = label ? label.querySelector('img') : null;
      if (!img) return null;
      return { id: `gift:${radio.value}`, src: img.currentSrc || img.src, srcset: img.getAttribute('srcset') || '' };
    }

    giftProgressText(state) {
      if (state.remaining > 0) return this.gift.progress.replace('[iznos]', this.format(state.remaining));
      return `Još samo malo – set mora vrijediti više od ${this.formatShort(this.gift.threshold)}`;
    }

    updateGift(state) {
      if (!this.gift.enabled) return;
      const hasColours = this.giftRadios.length > 0;
      this.giftMeters.forEach((meter) => {
        const text = meter.querySelector('[data-gift-text]');
        const fill = meter.querySelector('[data-gift-fill]');
        meter.classList.toggle('is-reached', state.reached);
        fill.style.width = `${Math.round(state.ratio * 1000) / 10}%`;
        let key;
        if (!state.reached) key = `p:${this.giftProgressText(state)}`;
        else if (!hasColours) key = 'r';
        else if (this.giftChoice) key = `c:${this.giftChoice}:${this.giftColourLabel()}`;
        else key = meter.hasAttribute('data-gift-link') ? 'r:link' : 'r:text';
        if (text.dataset.key === key) return;
        text.dataset.key = key;
        text.textContent = '';
        if (!state.reached) {
          text.textContent = this.giftProgressText(state);
        } else if (!hasColours) {
          text.textContent = this.gift.reached;
        } else if (this.giftChoice) {
          // the bar is narrow: "Gumica za mamu je gratis 🤍 – Mint" (the scrunchie's photo is next to it)
          const compact = meter.classList.contains('babyset-discount--compact');
          text.textContent = `${this.gift.reached} – ${compact ? '' : 'boja: '}${this.giftColourLabel()}`;
        } else if (meter.hasAttribute('data-gift-link')) {
          const go = el('button', 'babyset-discount__go', 'odaberi boju');
          go.type = 'button';
          go.dataset.giftGo = '';
          text.append(`${this.gift.reached} – `, go);
        } else {
          text.textContent = `${this.gift.reached} – odaberi boju`;
        }
      });

      if (this.giftPicker) {
        this.giftPicker.classList.toggle('is-locked', !state.reached);
        this.giftRadios.forEach((radio) => {
          radio.disabled = !state.reached;
          radio.checked = radio.value === this.giftChoice;
        });
      }
      if (this.giftRoot) this.giftRoot.classList.toggle('is-reached', state.reached);
      if (this.giftStatus) {
        let status = `Boju biraš kad vrijednost seta prijeđe ${this.formatShort(this.gift.threshold)}.`;
        if (state.reached && this.giftChoice) {
          status = `Odabrano: ${this.giftColourLabel()} – gumica stiže gratis u paketu s tvojim setom.`;
        } else if (state.reached) {
          status = 'Odaberi boju – gumica stiže gratis u paketu s tvojim setom.';
        }
        // role="status": only a new text is announced (not the same sentence on every key press)
        if (this.giftStatus.textContent !== status) this.giftStatus.textContent = status;
      }
      this.querySelectorAll('[data-gift-row]').forEach((row) => {
        const colour = state.reached && this.giftChoice ? ` – ${this.giftColourLabel()}` : '';
        row.querySelector('[data-gift-row-label]').textContent = `${this.gift.property}: ${this.gift.name}${colour}`;
        row.querySelector('[data-gift-row-value]').textContent = state.reached
          ? 'Gratis'
          : `za set iznad ${this.formatShort(this.gift.threshold)}`;
        row.classList.toggle('is-pending', !state.reached);
      });
    }

    /** "odaberi boju": the gift box on Pregled. */
    focusGift() {
      this.closePanel();
      const last = this.steps.length - 1;
      if (this.stepIndex !== last) this.goToStep(last, { focus: false, scroll: false });
      if (!this.giftRoot) return;
      this.scrollToElement(this.giftRoot, 16);
      const target =
        this.giftRadios.find((radio) => radio.checked && !radio.disabled) ||
        this.giftRadios.find((radio) => !radio.disabled) ||
        this.giftRoot;
      target.focus({ preventScroll: true });
    }

    /* ---------- "Tko bira boje?" (Boje neka izabere Šigureca) ---------- */

    initColourMode() {
      this.modeRoot = this.querySelector('[data-colour-mode]');
      this.modeRadios = [...this.querySelectorAll('[data-mode-radio]')];
      this.forRadios = [...this.querySelectorAll('[data-for-radio]')];
      this.forRoot = this.querySelector('[data-mode-for]');
      this.modeExtra = this.querySelector('[data-mode-extra]');
      this.modeSize = this.querySelector('[data-mode-size]');
      this.modeStatus = this.querySelector('[data-mode-status]');
      if (!this.colourMode.enabled || !this.modeRoot) {
        this.colourMode.enabled = false;
        return;
      }
      this.modeRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (radio.checked) this.setColourMode(radio.value === 'sigureca');
        });
      });
      this.forRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          this.forChoice = radio.value;
          this.clearError();
          this.update();
        });
      });
    }

    /**
     * "Boje bira Šigureca" on / off (v1). The set's pieces stay; colour-only cards take their "Šigureca bira" variant
     * (or show the main photo); back in "Boje biraš ti" her own colours come back where known (ownFor). v2: the
     * set size is not moved (it is asked on the start screen), and a switch with pieces in the set shows N5 or N6 with
     * "Poništi" (V2-SPEC §2.14). silent: a restore (no notice).
     */
    setColourMode(on, { silent = false } = {}) {
      if (!this.colourMode.enabled || this.martaMode === on) return;
      const before = silent ? null : this.snapshot();
      if (!silent) this.clearTransient();
      this.martaMode = on;
      this.classList.toggle('is-colour-mode', on);
      this.cards.forEach((card) => {
        const { product } = card;
        if (on) {
          if (!card.variant && product.colourOnly && product.available) {
            card.variant = this.defaultVariant(product);
            if (card.variant) card.reason = null;
          } else if (!card.variant && product.split && card.splitSize && product.available) {
            // a size without a colour: "Šigureca bira" of that size
            const variant = this.variantForGroup(card, `g:${card.splitSize}`);
            if (variant) {
              card.variant = variant;
              card.reason = null;
            }
          } else if (card.variant) {
            card.variant = this.martaFor(card, card.variant);
          }
        } else if (card.variant && card.variant.marta) {
          card.variant = this.ownFor(card, card.variant);
        }
        this.setSizeFit(card);
        this.showVariantImage(card);
      });
      if (silent) return;
      const chosen = this.chosenPieces();
      if (chosen.length) {
        let text = 'Boje sada bira Šigureca.';
        if (!on) {
          // pieces still without a colour stay in the set, marked; Pregled's button guides to them
          const missing = chosen.filter(({ card }) => !card.variant).length;
          text = missing ? `Boje sada biraš ti. Za ${pieces(missing)} još odaberi boju.` : 'Boje sada biraš ti.';
        }
        this.notify({ text, undoLabel: 'Poništi promjenu načina biranja boja', before, kind: 'mode' });
      }
      this.update();
    }

    syncModeInputs() {
      (this.modeRadios || []).forEach((radio) => {
        radio.checked = (radio.value === 'sigureca') === this.martaMode;
      });
      (this.forRadios || []).forEach((radio) => {
        radio.checked = radio.value === this.forChoice;
      });
    }

    needsFor() {
      return this.martaMode && this.forRadios.length > 0 && !this.forChoice;
    }

    /** Karlo 3. 10.: the baby's age stays optional also in "Boje bira Šigureca" (then each piece asks its size). */
    needsSetSize() {
      return false;
    }

    /** "Za koga je set?": the start screen, the focus on the chosen chip or the first one. */
    focusFor() {
      this.closePanel();
      this.goToStep(0, { focus: false, scroll: false });
      const target = this.forRadios.find((radio) => radio.checked) || this.forRadios[0];
      if (target) target.focus();
    }

    /**
     * "Za koga je set?" shows in "Boje bira Šigureca"; question 1 gets its badge "Obavezno". The status says v1's whole
     * sentence once both are chosen (S17); while one is missing it stays empty – the errors under the questions say it
     * (in the start screen's words).
     */
    renderColourMode() {
      if (!this.colourMode.enabled) return;
      if (this.modeExtra) this.modeExtra.hidden = !this.martaMode;
      if (this.sizeBadge) this.sizeBadge.hidden = true;
      if (!this.modeStatus) return;
      let status = '';
      if (this.martaMode && !this.needsFor() && !this.needsSetSize()) {
        const parts = [];
        if (this.forChoice) parts.push(`za: ${this.forChoice}`);
        if (this.size) parts.push(`veličina ${this.sizeLabel(this.size)}`);
        status = `Set ${parts.join(', ')} – boje i printove biram ja.`;
      }
      // role="status": only a new sentence is announced
      if (this.modeStatus.textContent !== status) this.modeStatus.textContent = status;
    }

    /* ---------- "Više o proizvodu" (product window) ---------- */

    initDialog() {
      this.dialog = this.querySelector('[data-dialog]');
      this.addEventListener('click', (event) => {
        const button = event.target.closest ? event.target.closest('[data-more]') : null;
        if (!button) return;
        event.preventDefault();
        const card = this.cards.find((item) => item.el.contains(button));
        if (card) this.openDialog(card, button);
      });
      if (!this.dialog) return;
      this.dialogTitle = this.dialog.querySelector('[data-dialog-title]');
      this.dialogPhoto = this.dialog.querySelector('[data-dialog-photo]');
      this.dialogThumbs = this.dialog.querySelector('[data-dialog-thumbs]');
      this.dialogGallery = this.dialog.querySelector('[data-dialog-gallery]');
      this.dialogPrice = this.dialog.querySelector('[data-dialog-price]');
      this.dialogText = this.dialog.querySelector('[data-dialog-text]');
      this.dialogLink = this.dialog.querySelector('[data-dialog-link]');
      const close = this.dialog.querySelector('[data-dialog-close]');
      if (close) close.addEventListener('click', () => this.dialog.close());
      // a click on the dimmed page around the window (the <dialog> itself, outside its content) closes it
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('babyset-dialog-open');
        const opener = this.dialogOpener;
        this.dialogOpener = null;
        if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
      });
    }

    /** The card's product in the window: title, price, photos (the card's photo first), description, link. */
    openDialog(card, opener) {
      const { product } = card;
      if (!this.dialog || typeof this.dialog.showModal !== 'function') {
        // very old browsers without <dialog>: the product page in a new tab
        window.open(product.url, '_blank', 'noopener');
        return;
      }
      this.closePanel();
      this.dialogOpener = opener;
      this.dialogTitle.textContent = product.title;
      const prefix = !card.variant && this.fromPrices(card).varies ? 'od ' : '';
      this.dialogPrice.textContent = `${prefix}${this.format(this.piecePrice(card))}`;
      const html = product.description.trim();
      this.dialogText.innerHTML = '';
      if (html) this.dialogText.innerHTML = html;
      else this.dialogText.append(el('p', 'babyset-dialog__empty', 'Opis proizvoda uskoro.'));
      this.dialogLink.href = product.url;
      const images = product.images.length ? [...product.images] : product.image ? [product.image] : [];
      // the card's photo first when it is not among the first 16 (e.g. a colour's own photo further down)
      const shown = this.pieceImage(card);
      let start = shown ? images.findIndex((image) => String(image.id) === String(shown.id)) : 0;
      if (shown && start === -1) {
        images.unshift(shown);
        start = 0;
      }
      this.dialogImages = images;
      this.dialogGallery.hidden = images.length === 0;
      this.dialogThumbs.textContent = '';
      this.dialogThumbs.hidden = images.length < 2;
      images.forEach((image, index) => {
        const thumb = el('button', 'babyset-dialog__thumb');
        thumb.type = 'button';
        thumb.setAttribute('aria-label', `Fotografija ${index + 1} od ${images.length}`);
        const host = el('span', 'babyset-dialog__thumb-img');
        setImage(host, image, '64px');
        thumb.append(host);
        thumb.addEventListener('click', () => this.showDialogPhoto(index));
        this.dialogThumbs.append(thumb);
      });
      this.showDialogPhoto(start);
      document.documentElement.classList.add('babyset-dialog-open');
      this.dialog.showModal();
      const inner = this.dialog.querySelector('.babyset-dialog__inner');
      if (inner) inner.scrollTop = 0;
    }

    showDialogPhoto(index) {
      const images = this.dialogImages || [];
      const image = images[index];
      if (!image) return;
      this.dialogPhoto.textContent = '';
      this.dialogPhoto.dataset.src = '';
      setImage(this.dialogPhoto, image, '(min-width: 750px) 44rem, 100vw');
      // a landscape photo (e.g. the wide crop of the dekica) is shown whole, not cut to the 4:5 frame
      this.dialogPhoto.classList.toggle('is-contain', Number(image.ratio) > 1.15);
      const img = this.dialogPhoto.querySelector('img');
      if (img) img.alt = `${this.dialogTitle.textContent} – fotografija ${index + 1} od ${images.length}`;
      [...this.dialogThumbs.children].forEach((thumb, i) => {
        thumb.setAttribute('aria-pressed', String(i === index));
      });
    }

    /* ---------- v2: the piece window (sheet on phones, dialog on desktop) ---------- */

    /**
     * One <dialog> for the whole builder (snippets/babyset-sheet-v2.liquid). Every card's config block is moved into
     * the window's body here – after buildModel has read its controls (the references stay valid) – and only the
     * block of the card the window opens for is shown. ✕, Esc, a click on the dimmed page and the platform's close
     * request cancel; Enter on a choice or in a text field (keyboard) confirms, like the button, and in a one-tap
     * window Space does too (onSheetKey). A focused tile or field comes into view inside the window's body
     * (revealInSheet), and on a phone the window follows the on-screen keyboard (fitSheetToKeyboard).
     * Without <dialog> support (showModal missing) the window opens inline after the card (V2-SPEC §2.7).
     */
    initSheet() {
      this.sheetEl = this.querySelector('[data-sheet]');
      if (!this.sheetEl) return;
      const find = (selector) => this.sheetEl.querySelector(selector);
      this.sheetParts = {
        head: find('[data-sheet-head]'),
        foot: find('[data-sheet-foot]'),
        photo: find('[data-sheet-photo]'),
        badge: find('[data-sheet-badge]'),
        title: find('[data-sheet-title]'),
        price: find('[data-sheet-price]'),
        legal: find('[data-sheet-legal]'),
        close: find('[data-sheet-close]'),
        body: find('[data-sheet-body]'),
        replace: find('[data-sheet-replace]'),
        marta: find('[data-sheet-marta]'),
        sizeInfo: find('[data-sheet-size-info]'),
        sizeText: find('[data-sheet-size-text]'),
        sizeChange: find('[data-sheet-size-change]'),
        gift: find('[data-sheet-gift]'),
        summary: find('[data-sheet-summary]'),
        remove: find('[data-sheet-remove]'),
        confirm: find('[data-sheet-confirm]'),
      };
      // the window's place in the page (the inline fallback moves it after a card, and back)
      this.sheetHome = { parent: this.sheetEl.parentNode, next: this.sheetEl.nextSibling };
      this.sheetInline = typeof this.sheetEl.showModal !== 'function';
      this.sheetEl.classList.toggle('is-inline', this.sheetInline);
      const { body } = this.sheetParts;
      this.cards.forEach((card) => {
        if (!card.configEl || !body) return;
        card.configEl.hidden = true;
        body.append(card.configEl);
      });
      this.sheetParts.close.addEventListener('click', () => this.cancelSheet());
      this.sheetParts.confirm.addEventListener('click', () => this.confirmSheet());
      this.sheetParts.remove.addEventListener('click', () => this.removeFromSheet());
      if (this.sheetParts.sizeChange) this.sheetParts.sizeChange.addEventListener('click', () => this.openSheetSize());
      this.sheetEl.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.cancelSheet();
      });
      this.sheetEl.addEventListener('click', (event) => {
        if (event.target === this.sheetEl && !this.sheetInline) this.cancelSheet();
      });
      // closed some other way (a second Esc the browser no longer lets the page keep): the same as a cancel. The
      // close event comes a task after close(): when the window is open again by then (the theme editor's next
      // preview, opened in the same task or just after), the event belongs to the window before, not to this one
      this.sheetEl.addEventListener('close', () => {
        if (this.sheet && !this.sheetClosing && !this.sheetEl.open) this.cancelSheet();
      });
      this.sheetEl.addEventListener('keydown', (event) => this.onSheetKey(event));
      this.sheetEl.addEventListener('focusin', (event) => {
        this.revealInSheet(event.target);
        this.fitSheetToKeyboard();
      });
      // the next focus is known only after the event
      this.sheetEl.addEventListener('focusout', () => window.requestAnimationFrame(() => this.fitSheetToKeyboard()));
      if (window.visualViewport) {
        this.onSheetViewport = () => this.fitSheetToKeyboard();
        window.visualViewport.addEventListener('resize', this.onSheetViewport);
        window.visualViewport.addEventListener('scroll', this.onSheetViewport);
      }
    }

    /**
     * Keys in the window: Enter on a choice confirms (like the button); in a one-tap window Space picks the focused
     * choice and confirms too – the arrow keys only move the choice (WCAG 3.2.2). The inline fallback: Esc cancels.
     */
    onSheetKey(event) {
      if (!this.sheet || event.defaultPrevented || event.isComposing) return;
      if (this.sheet.inline && event.key === 'Escape') {
        event.preventDefault();
        this.cancelSheet();
        return;
      }
      const target = event.target;
      if (!target || !target.matches || !target.matches('input[type="radio"]')) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        this.confirmSheet();
        return;
      }
      if ((event.key === ' ' || event.key === 'Spacebar') && this.sheet.oneTap && !target.disabled) {
        event.preventDefault();
        if (!target.checked) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.confirmSheet();
      }
    }

    /**
     * The focused control is fully visible in the window's body, clear of its head and foot (WCAG 2.4.11). A visually
     * hidden radio sits at its group's corner, so its tile or pill (the label) is what comes into view.
     */
    revealInSheet(target) {
      const body = this.sheetParts && this.sheetParts.body;
      if (!this.sheet || !body || !target || !target.matches || !body.contains(target)) return;
      let box = target;
      if (target.matches('input[type="radio"], input[type="checkbox"]') && target.id) {
        box = body.querySelector(`label[for="${CSS.escape(target.id)}"]`) || target;
      }
      const pad = 12;
      const outer = body.getBoundingClientRect();
      const rect = box.getBoundingClientRect();
      if (!rect.height) return;
      const top = outer.top + pad;
      const bottom = outer.bottom - pad;
      if (rect.top < top) body.scrollTop -= top - rect.top;
      else if (rect.bottom > bottom) body.scrollTop += Math.min(rect.bottom - bottom, rect.top - top);
    }

    /**
     * The iPhone keyboard (V2-SPEC §2.7, R4): while a text field in the window has the focus on a phone, the window's
     * bottom sits on the keyboard's top and its height is the visible height (visualViewport), so the field and the
     * button stay in view. Otherwise the window has its own size again. Checked on a real iPhone (Q34).
     */
    fitSheetToKeyboard() {
      const dialog = this.sheetEl;
      if (!dialog) return;
      const viewport = window.visualViewport;
      const active = document.activeElement;
      const typing =
        Boolean(this.sheet) &&
        !this.sheet.inline &&
        Boolean(viewport) &&
        Boolean(active) &&
        dialog.contains(active) &&
        active.matches('input[type="text"], input[type="search"], textarea') &&
        !window.matchMedia('(min-width: 750px)').matches;
      // the layout viewport's height: the window is fixed to it, the keyboard covers its bottom
      const layout = document.documentElement.clientHeight;
      const covered = typing ? Math.round(layout - (viewport.offsetTop + viewport.height)) : 0;
      if (!typing || covered < 80) {
        if (dialog.classList.contains('is-keyboard')) {
          dialog.classList.remove('is-keyboard');
          dialog.style.removeProperty('margin-bottom');
          dialog.style.removeProperty('max-height');
        }
        return;
      }
      dialog.classList.add('is-keyboard');
      dialog.style.marginBottom = `${covered}px`;
      dialog.style.maxHeight = `${Math.floor(viewport.height)}px`;
      this.revealInSheet(active);
    }

    sheetNeeds(card) {
      const { product } = card;
      const needs = [];
      if (!card.configEl || !product.available) return needs;
      const has = (need) => Boolean(card.configEl.querySelector(`[data-need="${need}"]`));
      if (has('colour') && !this.hidesColour(card) && !product.single) {
        const picked = product.split ? Boolean(card.splitColour) && card.picked.colour : card.picked.colour && Boolean(card.variant);
        if (!picked) needs.push('colour');
      }
      if (has('size')) {
        const split = product.split && !this.hidesColour(card);
        const missing = split ? !card.splitSize || (Boolean(card.splitColour) && !card.variant) : !card.variant;
        if (missing) needs.push('size');
      }
      if (has('option') && !product.single && (!card.picked.option || !card.variant)) needs.push('option');
      if (has('variant')) needs.push('variant');
      // §19: „Boje bira Šigureca” on a card with „Personaliziraj” (dekica, vezica): nothing else is asked, but the
      // window still opens for the box (otherwise the piece would go in at once, never personalised)
      if (card.personalToggle && this.hidesColour(card) && has('size') && !needs.includes('size')) needs.push('size');
      if (this.fieldsActive(card)) {
        card.fields.forEach((field) => {
          if (!this.fieldHidden(field)) needs.push(`field:${field.id}`);
        });
      }
      // the tetra: the window always asks how many (its stepper; never a one-tap window)
      if (has('qty')) needs.push('qty');
      return needs;
    }

    /**
     * Is a part of the open window's card shown? Edit mode (and the editor's preview): every part that applies; add
     * mode: what was asked, and the size list once "Promijeni" on the size line was pressed.
     */
    partVisible(card, need) {
      const { sheet } = this;
      if (!sheet || sheet.card !== card) return false;
      const [kind, id] = need.split(':');
      if (kind === 'colour' && this.hidesColour(card)) return false;
      if ((kind === 'colour' || kind === 'option') && card.product.single) return false;
      if (kind === 'field') {
        const field = card.fields.find((item) => item.id === id);
        if (!field || this.fieldHidden(field)) return false;
        // the editor's preview shows a field that is not active yet (the vezica's on "Obična")
        if (!this.fieldsActive(card) && sheet.mode !== 'preview') return false;
      }
      return sheet.mode !== 'add' || sheet.needs.has(need) || (kind === 'size' && sheet.sizeOpen);
    }

    /**
     * Open the window for a card (V2-SPEC §2.6–§2.7): mode 'add' (only what is missing; "Zamijeni" when the part's one
     * piece is another), 'edit' (the piece in the set: every part, "Spremi", "Ukloni iz seta") or 'preview' (the theme
     * editor: the edit look, nothing is added). opener: where the focus goes back – { kind: 'card' | 'list' |
     * 'review', key, row } or an element (the card's button).
     */
    openSheet(card, mode, opener) {
      const dialog = this.sheetEl;
      if (!dialog || !card.configEl || this.sheet) return false;
      this.closePanel();
      if (this.dialog && this.dialog.open) this.dialog.close();
      const slot = this.slotById(card.slotId);
      const single = Boolean(slot) && (!slot.multi || slot.max === 1);
      const holder = mode === 'add' && single ? this.chosenCards(slot.id).find((other) => other !== card) || null : null;
      this.sheet = {
        card,
        mode,
        opener: opener && opener.kind ? opener : { kind: 'card', key: this.cardKey(card), el: opener || card.addButton },
        snapshot: this.cardSnapshot(card),
        needs: new Set(mode === 'add' ? this.sheetNeeds(card) : []),
        errors: [],
        replacing: holder,
        // "Promijeni" on the size line was pressed (the size list shows; no one tap)
        sizeOpen: false,
        // W12 / W13: kept while the window is open, so the list does not move when she picks a size
        sizeReason: '',
        oneTap: false,
        oneTapTimer: 0,
        inline: this.sheetInline,
      };
      this.cards.forEach((item) => {
        if (item.configEl) item.configEl.hidden = item !== card;
      });
      this.renderSheet();
      if (this.sheet.inline) {
        // no <dialog> in this browser: the window opens inline after the card, the width of its row
        card.el.after(dialog);
        dialog.setAttribute('open', '');
      } else {
        document.documentElement.classList.add('babyset-dialog-open');
        dialog.showModal();
      }
      // the window's first moments: a press that starts now is the second half of a double tap (guardSheetPresses)
      this.sheetOpenedAt = window.performance.now();
      this.sheetEarlyPress = false;
      this.guardSheetPresses();
      if (this.sheetParts.body) this.sheetParts.body.scrollTop = 0;
      this.sheetParts.title.focus({ preventScroll: !this.sheet.inline });
      return true;
    }

    /**
     * The second half of a double tap / click on "Dodaj u set" (or "Uredi") lands in the window it has just opened: it
     * chose a tile nobody picked (a one-tap window then adds the piece) or closed the window through the dimmed page.
     * As v1's colour guard (bindColourGuard, KARLO-DECISIONS §9.3): a press that starts in the window's first
     * SHEET_GUARD_MS chooses and closes nothing, however late it is let go; pointerdown starts every press, touchstart
     * and mousedown only add an early start. A key's click (detail 0) is never dropped: the keyboard works as before.
     * Capture listeners on the builder, so they run before the window's own.
     */
    guardSheetPresses() {
      if (this.sheetGuardOn) return;
      this.sheetGuardOn = true;
      const early = () => Boolean(this.sheet) && window.performance.now() - (this.sheetOpenedAt || -Infinity) < SHEET_GUARD_MS;
      const inWindow = (event) => Boolean(this.sheetEl) && event.target instanceof Node && this.sheetEl.contains(event.target);
      const pressStart = (event) => {
        if (!inWindow(event)) return;
        if (event.type === 'pointerdown') this.sheetEarlyPress = early();
        else if (early()) this.sheetEarlyPress = true;
      };
      const drop = (event) => {
        if (!inWindow(event) || !event.detail || !(early() || this.sheetEarlyPress)) return;
        if (event.type === 'click') this.sheetEarlyPress = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      ['pointerdown', 'touchstart', 'mousedown'].forEach((type) => this.addEventListener(type, pressStart, { capture: true, passive: true }));
      ['mousedown', 'click'].forEach((type) => this.addEventListener(type, drop, true));
    }

    /**
     * The window follows the card: its photo, price and lines, the parts with their errors, the size line, the one
     * tap's hint (W7), the gift line (W19) and the buttons.
     */
    renderSheet() {
      const { sheet } = this;
      if (!sheet || !this.sheetParts) return;
      const { card, mode } = sheet;
      const { product, variant } = card;
      const parts = this.sheetParts;
      this.renderControls(card);
      // add mode: what is still missing joins what was asked (a part never disappears while she chooses)
      if (mode === 'add') this.sheetNeeds(card).forEach((need) => sheet.needs.add(need));
      // an error goes as soon as its part is answered (new errors only come with the next confirm)
      if (sheet.errors.length) {
        const now = this.validateSheet();
        sheet.errors = now.filter((item) => sheet.errors.some((old) => old.need === item.need));
      }
      // head: the card's photo (the same rule as the card), the title, the price and its Delmis Cjenici lines
      setImage(parts.photo, this.pieceImage(card) || product.image || null, '(min-width: 750px) 96px, 72px');
      parts.badge.hidden = mode !== 'edit';
      if (parts.title.textContent !== product.title) parts.title.textContent = product.title;
      const from = this.fromPrices(card);
      const price = variant ? variant.price : from.price;
      const compare = variant ? variant.compare : from.compare;
      const prefix = !variant && from.varies ? 'od ' : '';
      parts.price.textContent = '';
      const qtyNow = this.qtyOf(card);
      if (card.qtyOn && qtyNow > 1) {
        // Karlo 3. 10.: the window's price follows the quantity: "22,80 €" and "3 × 8,00 € · popust 5 %"
        const pct = this.qtyPercent(card);
        parts.price.append(el('span', '', `${prefix}${this.format(this.qtyTotal(card, price))}`));
        parts.price.append(
          el('span', 'bs2-sheet__qty-note', ` ${qtyNow} × ${this.format(price)}${pct ? ` · popust ${pct} %` : ''}`)
        );
      } else {
        parts.price.append(el('span', compare > price ? 'babyset-card__sale' : '', `${prefix}${this.format(price)}`));
        if (compare > price) {
          parts.price.append(el('span', 'visually-hidden', ' Redovna cijena: '), el('s', 'babyset-card__compare', this.format(compare)));
        }
      }
      const shownVariant = variant || from.shown;
      const lines = shownVariant ? shownVariant.legal.map(glueCurrency) : [];
      parts.legal.textContent = '';
      lines.forEach((line) => parts.legal.append(el('span', 'babyset-card__legal-line', line)));
      parts.legal.hidden = lines.length === 0;
      parts.close.setAttribute('aria-label', mode === 'add' ? 'Zatvori bez dodavanja' : 'Zatvori bez spremanja');
      // body: the piece this one replaces, the Šigureca note, the parts with their errors
      const replace = sheet.replacing ? `Zamjenjuje komad u setu: ${sheet.replacing.product.title}.` : '';
      parts.replace.hidden = !replace;
      if (parts.replace.textContent !== replace) parts.replace.textContent = replace;
      const note = this.martaNote(card);
      parts.marta.hidden = !note;
      if (parts.marta.textContent !== note) parts.marta.textContent = note;
      card.configEl.querySelectorAll('[data-need]').forEach((part) => {
        const need = part.dataset.need;
        part.hidden = !this.partVisible(card, need);
        const error = sheet.errors.find((item) => item.need === need);
        const errorEl = part.querySelector('[data-need-error]');
        if (errorEl) {
          const text = error ? error.text : '';
          errorEl.hidden = !error;
          if (errorEl.textContent !== text) errorEl.textContent = text;
        }
        this.linkNeedError(part, errorEl, Boolean(error));
      });
      if (mode === 'preview' && card.personalEl) {
        // the editor shows the fields of the block she selected, also where they are not active yet
        card.personalEl.hidden = false;
        card.fields.forEach((field) => {
          field.el.hidden = this.fieldHidden(field);
        });
      }
      this.renderSheetSize(card);
      // the one tap: W7 under the group's legend (and in its description) while a pointer pick adds the piece
      sheet.oneTap = this.sheetOneTap(card);
      card.configEl.querySelectorAll('[data-onetap-hint]').forEach((hint) => {
        hint.hidden = !sheet.oneTap;
        const group = hint.closest('fieldset');
        // the palette's hint paragraph describes its radiogroup in the Liquid already
        if (group && hint.id) this.describe(group, hint.id, sheet.oneTap);
      });
      // foot: a visual summary of the errors, the gift line, the buttons
      const texts = sheet.errors.map((item) => item.text);
      parts.summary.hidden = !texts.length;
      parts.summary.textContent = texts.join(' ');
      if (parts.gift) parts.gift.hidden = !this.sheetReachesGift(card);
      parts.remove.hidden = mode !== 'edit';
      // Karlo 3. 10.: the button only where a pick cannot finish the piece (typed fields, the tetra's quantity);
      // closing an edit window keeps a complete change (cancelSheet)
      // (keyboard users keep the button: a list's arrow keys must not add the piece)
      parts.confirm.hidden = mode !== 'preview' && sheet.oneTap && this.inputModality !== 'keyboard';
      let confirm = 'Spremi';
      if (mode === 'add') confirm = `${sheet.replacing ? 'Zamijeni' : 'Dodaj u set'} · ${prefix}${this.format(this.qtyTotal(card, price))}`;
      else if (mode === 'preview') confirm = 'Zatvori';
      if (parts.confirm.textContent !== confirm) parts.confirm.textContent = confirm;
      this.renderQty(card);
    }

    /**
     * The window's size (V2-SPEC §2.7, §2.8 c, k): while the size comes from the set and is not asked, the line
     * "Veličina: 3–6 mjeseci (veličina seta)" with "Promijeni" stands where the size list is; the list's label names the
     * set size in edit mode and after "Promijeni" (W10); above the list the reason it is asked (W12, W13).
     */
    renderSheetSize(card) {
      const { sheet } = this;
      const parts = this.sheetParts;
      const sizePart = card.configEl.querySelector('[data-need="size"]');
      const line = sizePart && sizePart.hidden && sheet.mode === 'add' ? this.sizeLineText(card) : '';
      if (parts.sizeInfo) {
        if (line && parts.sizeInfo.nextElementSibling !== sizePart) sizePart.before(parts.sizeInfo);
        parts.sizeInfo.hidden = !line;
        if (parts.sizeText.textContent !== line) parts.sizeText.textContent = line;
      }
      if (!sizePart) return;
      const setLabel = sizePart.querySelector('[data-size-set-label]');
      if (setLabel) {
        const named = Boolean(this.size) && !sizePart.hidden && (sheet.mode !== 'add' || sheet.sizeOpen);
        const text = named ? ` (veličina seta: ${this.sizeLabel(this.size)})` : '';
        if (setLabel.textContent !== text) setLabel.textContent = text;
      }
      const reasonEl = sizePart.querySelector('[data-size-reason]');
      if (reasonEl) {
        const now = sheet.mode === 'add' ? this.sizeReasonText(card) : '';
        if (now) sheet.sizeReason = now;
        const text = sizePart.hidden ? '' : sheet.sizeReason;
        reasonEl.hidden = !text;
        if (reasonEl.textContent !== text) reasonEl.textContent = text;
        if (card.select && sizePart.contains(card.select)) this.describe(card.select, reasonEl.id, Boolean(text));
      }
    }

    /** W5: "Veličina: 3–6 mjeseci (veličina seta)", "Veličina: 0–6 mjeseci (najbliža za 3–6 mjeseci)" ('' without one). */
    sizeLineText(card) {
      const { product } = card;
      if (!this.size || !product.sized) return '';
      let key = card.variant && card.variant.sizeKey ? card.variant.sizeKey : null;
      if (!key && product.split && card.splitSize) key = parseSize(card.splitSize).key;
      if (!key) return '';
      const label = this.sizeLabel(key);
      if (!label) return '';
      if (key === this.size) return `Veličina: ${label} (veličina seta)`;
      if (card.near) return `Veličina: ${label} (najbliža za ${this.sizeLabel(this.size)})`;
      return `Veličina: ${label}`;
    }

    /**
     * W12 / W13: why the window asks for the size – the set size is missing for the piece or sold out, or the chosen
     * colour is sold out in it ("Krem – rasprodano u veličini 3–6 mjeseci. …").
     */
    sizeReasonText(card) {
      if (!this.size) return '';
      const { product } = card;
      const size = this.sizeLabel(this.size);
      if (product.split && card.splitColour && card.splitSize && !card.variant && !this.hidesColour(card)) {
        return `${this.optionLabel(card.splitColour)} – rasprodano u veličini ${parseSize(card.splitSize).label}. Odaberi drugu veličinu.`;
      }
      if (card.reason === 'missing') return `Nema u veličini ${size}. Odaberi drugu veličinu.`;
      if (card.reason === 'soldout') return `Rasprodano u veličini ${size}. Odaberi drugu veličinu.`;
      return '';
    }

    /** One id more or less in a control's aria-describedby (the others stay). */
    describe(control, id, on) {
      if (!control || !id) return;
      const ids = (control.getAttribute('aria-describedby') || '').split(/\s+/).filter((item) => item && item !== id);
      if (on) ids.unshift(id);
      if (ids.length) control.setAttribute('aria-describedby', ids.join(' '));
      else control.removeAttribute('aria-describedby');
    }

    /** "Promijeni" on the size line: the size list in its place (two groups now, so no one tap); the focus on it. */
    openSheetSize() {
      const { sheet } = this;
      if (!sheet) return;
      sheet.sizeOpen = true;
      this.renderSheet();
      const select = sheet.card.configEl.querySelector('[data-need="size"] select');
      if (select) select.focus();
    }

    /**
     * One tap (V2-SPEC §2.6, SD5): add mode (replacing too), exactly one group shown, a choice (the palette, photo
     * tiles, pills; not a list or a field), and every available value of it completes the piece. Not the vezica's
     * "Vrsta" ("Personalizirana" brings fields), not while a colour of the trakica is sold out in the set size (it
     * brings the size list); never in edit mode and never inline.
     */
    sheetOneTap(card) {
      const { sheet } = this;
      if (!sheet || sheet.card !== card || (sheet.mode !== 'add' && sheet.mode !== 'edit') || sheet.inline) return false;
      const shown = [...card.configEl.querySelectorAll('[data-need]')].filter((part) => !part.hidden);
      if (!shown.length) return false;
      // Karlo 3. 10.: a pick completes the piece by itself (add and edit, no button) unless the window has something
      // typed or counted (a name, a letter, the tetra's quantity) – only those keep "Dodaj u set" / "Spremi"
      if (shown.some((part) => /^(field|qty)/.test(part.dataset.need))) return false;
      const { product } = card;
      // the vezica: after the fabric she may still tick „Personaliziraj” – the button adds it
      if (card.personalToggle) return false;
      // a value that brings fields of its own (the vezica's „Personalizirana”) is not complete by itself
      return !card.pills.some((pill) => {
        const variant = this.findVariant(card, pill.value);
        return (
          Boolean(variant) &&
          product.personalByVariant &&
          PERSONAL_VARIANT_RE.test((variant.options || []).join(' ')) &&
          card.fields.some((field) => !this.fieldHidden(field))
        );
      });
    }

    /** A pointer pick in a one-tap window: the choice shows, then 180 ms later the piece goes in (v1 §9.3's timing). */
    scheduleOneTap(sheet) {
      if (!sheet || this.sheet !== sheet) return;
      window.clearTimeout(sheet.oneTapTimer);
      // only a pick that completes the piece (a colour that needs the size list does not)
      if (!sheet.oneTap || this.validateSheet().length) return;
      sheet.oneTapTimer = window.setTimeout(() => {
        if (this.sheet === sheet) this.confirmSheet();
      }, ONE_TAP_MS);
    }

    /** W19: in add mode, this piece takes the set above the gift's threshold (it is not reached yet). */
    sheetReachesGift(card) {
      const { sheet } = this;
      if (!sheet || sheet.mode !== 'add' || !this.gift.enabled || !this.giftNow || this.giftNow.reached) return false;
      // the price must be known: a variant, or one price for the product
      if (!card.variant && card.product.priceVaries) return false;
      const cards = this.chosenPieces()
        .map(({ card: item }) => item)
        .filter((item) => item !== card && item !== sheet.replacing);
      cards.push(card);
      return this.giftState(this.computeTotals(cards), cards.length).reached;
    }

    /** Did the window change the piece (Spremi): its variant, colour, size or a field? */
    cardChanged(card, snapshot) {
      if (!snapshot) return false;
      if ((card.variant && card.variant.id) !== (snapshot.variant && snapshot.variant.id)) return true;
      if (card.splitColour !== snapshot.splitColour || card.splitSize !== snapshot.splitSize) return true;
      return card.fields.some((field, index) => (field.value || '') !== (snapshot.values[index] || ''));
    }

    /**
     * The card's v1 controls follow its state (the window shows them): the lists, pills, tiles, fields, counters, and
     * v2's "· Odabrano: …" next to a tile group's or the palette's legend (W8).
     */
    renderControls(card) {
      const selected = this.isChosen(card);
      if (card.select) {
        if (card.sizeGroups) this.syncSizeGroups(card);
        else card.select.value = card.variant ? String(card.variant.id) : '';
        card.select.disabled = !card.product.available;
      }
      if (card.product.split) this.renderSplit(card, selected);
      // forgetColour: a colour shows as chosen only once she picked it (or the piece is in the set)
      const colourShown = card.product.colourIndex === -1 || card.picked.colour || selected || this.hidesColour(card);
      card.pills.forEach((pill) => {
        pill.checked = colourShown && Boolean(card.variant) && pill.value === String(card.variant.id);
      });
      if (card.optionValueEl) {
        card.optionValueEl.textContent = card.variant ? this.variantLabel(card.product, card.variant) || '—' : '';
      }
      if (card.configEl) {
        let chosen = '';
        if (card.product.split) chosen = card.splitColour ? this.optionLabel(card.splitColour) : '';
        else if (card.variant && !card.variant.marta && card.product.colourIndex !== -1) chosen = this.variantLabel(card.product, card.variant);
        card.configEl.querySelectorAll('[data-chosen-value]').forEach((node) => {
          const text = chosen ? ` · Odabrano: ${chosen}` : '';
          node.hidden = !chosen;
          if (node.textContent !== text) node.textContent = text;
        });
      }
      // Fields as on her product pages ("Izaberi slovo", "Izaberi boje veza", "Odaberi print")
      if (card.personalEl) {
        const active = this.fieldsActive(card);
        card.personalEl.hidden = !active || card.fields.every((field) => this.fieldHidden(field));
        if (card.efFields) this.efSyncChoices(card);
        card.fields.forEach((field) => {
          field.el.hidden = this.fieldHidden(field);
          field.input.required = field.required;
          if (field.count) {
            // "52/60 znakova" close to the limit, so a long name is never cut without notice
            const length = charCount(field.input.value);
            const near = length >= Math.max(1, field.max - 12);
            field.count.hidden = !near;
            field.count.textContent = near
              ? length >= field.max
                ? `Najviše ${field.max} znakova`
                : `${length}/${field.max} znakova`
              : '';
            field.count.classList.toggle('is-full', length >= field.max);
          }
        });
      }
    }

    /** An error line linked to its part's controls (aria-describedby; aria-invalid on lists and text fields). */
    linkNeedError(part, errorEl, on) {
      if (!errorEl) return;
      part.querySelectorAll('select, input[type="text"], fieldset').forEach((control) => {
        const ids = (control.getAttribute('aria-describedby') || '').split(/\s+/).filter((id) => id && id !== errorEl.id);
        if (on) ids.unshift(errorEl.id);
        if (ids.length) control.setAttribute('aria-describedby', ids.join(' '));
        else control.removeAttribute('aria-describedby');
        if (control.tagName === 'FIELDSET') return;
        if (on) control.setAttribute('aria-invalid', 'true');
        else control.removeAttribute('aria-invalid');
      });
    }

    /** What the window still misses before its piece can go in: [{ need, text }] (W14). */
    validateSheet() {
      const { card } = this.sheet;
      const { product } = card;
      const errors = [];
      const has = (need) => Boolean(card.configEl.querySelector(`[data-need="${need}"]`));
      const add = (need) => errors.push({ need, text: this.needText(card, need) });
      if (!card.variant || !card.variant.available) {
        if (product.split) {
          if (!card.splitColour && !this.hidesColour(card)) add('colour');
          if (!card.splitSize || card.splitColour || this.hidesColour(card)) add('size');
        } else if (has('colour') && !this.hidesColour(card)) add('colour');
        else if (has('size')) add('size');
        else if (has('option')) add('option');
        else if (has('variant')) add('variant');
      }
      // forgetColour: in add mode the colour is always picked in the window
      if (
        this.sheet.mode === 'add' &&
        has('colour') &&
        !this.hidesColour(card) &&
        !product.single &&
        !card.picked.colour &&
        !errors.some((item) => item.need === 'colour')
      ) {
        add('colour');
      }
      if (this.fieldsActive(card)) {
        card.fields.forEach((field) => {
          if (field.required && !field.value && !this.fieldHidden(field)) add(`field:${field.id}`);
        });
      }
      return errors;
    }

    /** "Odaberi boju." / "Odaberi veličinu." / "Odaberi: Vrsta." / "Popuni „Upiši ime“." */
    needText(card, need) {
      const [kind, id] = need.split(':');
      if (kind === 'colour') return 'Odaberi boju.';
      if (kind === 'size') return 'Odaberi veličinu.';
      if (kind === 'field') {
        const field = card.fields.find((item) => item.id === id);
        return field ? `Popuni „${field.name}“.` : '';
      }
      return `Odaberi: ${card.product.options.join(' / ')}.`;
    }

    /** The first control of a part: the chosen radio or the first one, else its list or text field. */
    focusNeed(card, need) {
      const part = card.configEl.querySelector(`[data-need="${CSS.escape(need)}"]`);
      if (!part) return;
      const radios = [...part.querySelectorAll('input[type="radio"]')].filter((radio) => !radio.disabled);
      const target = radios.find((radio) => radio.checked) || radios[0] || part.querySelector('select, input[type="text"]');
      if (target) target.focus();
    }

    confirmSheet() {
      const { sheet } = this;
      if (!sheet) return;
      window.clearTimeout(sheet.oneTapTimer);
      if (sheet.mode === 'preview') {
        this.cancelSheet();
        return;
      }
      const errors = this.validateSheet();
      if (errors.length) {
        sheet.errors = errors;
        this.renderSheet();
        this.focusNeed(sheet.card, errors[0].need);
        return;
      }
      let added = false;
      if (sheet.mode === 'add') added = this.commitAdd(sheet.card) && this.lastCommit === 'add';
      else if (this.cardChanged(sheet.card, sheet.snapshot)) {
        this.clearTransient();
        this.setSlotMessage(sheet.card.slotId, '');
      }
      this.closeSheet('confirm');
      // a new piece (not a replacement, not an edit): "U setu" shows on the card, then the next step
      if (added) this.scheduleAdvance(sheet.card);
    }

    /** ✕, Esc, the dimmed page, the platform's close request, the browser's Back: the card as it was. */
    cancelSheet({ focus = true } = {}) {
      const { sheet } = this;
      if (!sheet) return;
      // edit mode has no "Spremi": a complete change is kept, an incomplete one goes back to the piece as it was
      if (sheet.mode === 'edit' && !this.validateSheet().length) {
        window.clearTimeout(sheet.oneTapTimer);
        if (this.cardChanged(sheet.card, sheet.snapshot)) {
          this.clearTransient();
          this.setSlotMessage(sheet.card.slotId, '');
        }
        this.closeSheet('confirm', { focus });
        return;
      }
      this.restoreCard(sheet.card, sheet.snapshot);
      this.closeSheet('cancel', { focus });
    }

    /**
     * Close the window, then a full update (the grid card, the bar, the live region); the focus back to the opener.
     * The browser's own close already put the focus back on the card's button, before the update grew the card (its
     * chosen line): so the focused control is kept above the bar here, after the update (keyboard focus only,
     * raiseAboveBar; WCAG 2.4.11).
     */
    closeSheet(reason, { focus = true } = {}) {
      const { sheet } = this;
      if (!sheet) return;
      window.clearTimeout(sheet.oneTapTimer);
      this.sheetClosing = true;
      try {
        if (sheet.inline) {
          this.sheetEl.removeAttribute('open');
          this.sheetHome.parent.insertBefore(this.sheetEl, this.sheetHome.next);
        } else if (this.sheetEl.open) {
          this.sheetEl.close(reason);
        }
      } finally {
        this.sheetClosing = false;
      }
      this.sheet = null;
      // the on-screen keyboard's size goes with the window
      this.fitSheetToKeyboard();
      if (!this.dialog || !this.dialog.open) document.documentElement.classList.remove('babyset-dialog-open');
      const { card } = sheet;
      card.configEl.querySelectorAll('[data-need]').forEach((part) => {
        const errorEl = part.querySelector('[data-need-error]');
        this.linkNeedError(part, errorEl, false);
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
      });
      card.configEl.hidden = true;
      this.update();
      if (!focus) return;
      this.focusOpener(sheet);
      const active = document.activeElement;
      if (active && this.contains(active)) this.raiseAboveBar(active);
    }


    /**
     * The focus back to what opened the window (V2-SPEC §4.3): the card's button, or "Uredi" in the bar's list (the
     * list opens again) or on Pregled – found again by key after the re-render. When it is gone (the piece left the
     * set): the first button of the row now in its place, else the list's heading, else the step's heading.
     */
    focusOpener(sheet) {
      const visible = (node) => Boolean(node) && node.isConnected && node.getClientRects().length > 0;
      const { opener, card } = sheet;
      if (opener.kind === 'list' || opener.kind === 'review') {
        const inList = opener.kind === 'list';
        if (inList && this.panel && this.panel.hidden) this.openPanel();
        const list = inList ? this.lists[0] : this.reviewList;
        const attr = inList ? 'data-list-action' : 'data-review-action';
        const again = list ? list.querySelector(`[${attr}="edit:${CSS.escape(opener.key)}"]`) : null;
        if (visible(again)) {
          again.focus();
          return;
        }
        const rows = list ? [...list.children] : [];
        const row = rows[Math.min(opener.row || 0, rows.length - 1)];
        const first = row ? row.querySelector(`[${attr}]`) : null;
        if (visible(first)) {
          first.focus();
          return;
        }
        const heading = inList ? this.panel && this.panel.querySelector('.babyset-bar__panel-title') : null;
        if (visible(heading)) {
          if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
          heading.focus();
          return;
        }
        this.focusStep(this.stepIndex);
        return;
      }
      if (visible(opener.el)) {
        opener.el.focus({ preventScroll: true });
        return;
      }
      if (visible(card.addButton)) {
        card.addButton.focus({ preventScroll: true });
        return;
      }
      this.focusStep(this.stepIndex);
    }


    /**
     * "Ukloni iz seta" (edit mode): the piece as it was in the set leaves it (with "Poništi"); the focus goes to the
     * card's button, or – opened from a list – to the row now in its place (V2-SPEC §2.7, §4.3).
     */
    removeFromSheet() {
      const { sheet } = this;
      if (!sheet || sheet.mode !== 'edit') return;
      const { card } = sheet;
      this.restoreCard(card, sheet.snapshot);
      this.closeSheet('remove', { focus: false });
      this.removePiece(card.slotId, card.handle);
      this.focusOpener(sheet);
    }

    cardSnapshot(card) {
      return {
        variant: card.variant,
        override: card.override,
        near: card.near,
        reason: card.reason,
        shown: card.shown,
        combo: card.combo,
        splitColour: card.splitColour,
        splitSize: card.splitSize,
        ownVariant: card.ownVariant,
        colourPicked: card.colourPicked,
        picked: { ...card.picked },
        values: card.fields.map((field) => field.value),
        qty: card.qty,
      };
    }

    restoreCard(card, snapshot) {
      if (!snapshot) return;
      const { values, picked, qty, ...rest } = snapshot;
      Object.assign(card, rest);
      card.picked = { ...picked };
      card.fields.forEach((field, index) => this.setFieldValue(field, values[index] || ''));
      if (card.qtyOn) this.setQty(card, qty || 1);
    }

    /**
     * The picture of one colour value (V2-SPEC §2.8), for the card's chosen line – the same picture as the colour's
     * tile or pill in the window (snippets/babyset-card-v2.liquid): first KARLO-DECISIONS §11's „Slika boje“ (the
     * variant metafield custom.slika_boje of the first variant of that colour that has one; the shared product JSON
     * does not carry it, so it is read from the tile's or pill's picture, marked data-slika-boje), else the photo of
     * that colour's first variant (v1's variantPhoto: its own photo or its colour's swatch).
     */
    colourPicture(card, value) {
      const { product } = card;
      if (product.colourIndex === -1 || !value) return null;
      if (card.configEl) {
        const radio = [...card.configEl.querySelectorAll('[data-split-colour]')].find((item) => item.value === value);
        const label = radio && radio.id ? card.configEl.querySelector(`label[for="${CSS.escape(radio.id)}"]`) : null;
        const picture = label ? label.querySelector('[data-slika-boje] img, img[data-slika-boje]') : null;
        if (picture && picture.getAttribute('src')) {
          return { id: `slika-boje:${picture.getAttribute('src')}`, src: picture.getAttribute('src'), srcset: picture.getAttribute('srcset') || '' };
        }
      }
      const variant = product.variants.find((item) => !item.marta && item.options[product.colourIndex] === value);
      return variant ? this.variantPhoto(product, variant) : null;
    }

    /* ---------- v2: the start screen ---------- */

    initStart() {
      this.startEl = this.querySelector('[data-step="start"]');
      this.titleEl = this.querySelector('[data-title]');
      this.startButton = this.querySelector('[data-start-button]');
      this.startLabel = this.startButton ? this.startButton.querySelector('[data-start-label]') : null;
      this.ageError = this.querySelector('[data-start-error="age"]');
      this.forError = this.querySelector('[data-start-error="for"]');
      this.ageNote = this.querySelector('[data-age-note]');
      this.mixedNote = this.querySelector('[data-mixed-note]');
      this.startErrors = null;
      this.hasStart = Boolean(this.startEl) && (Boolean(this.sizeQuestion) || this.colourMode.enabled);
      this.initQty();
      if (!this.startButton) return;
      this.startButton.removeAttribute('aria-disabled');
      this.startButton.addEventListener('click', () => this.startFlow());
    }

    /** The quantity cards (tetra): their stepper (delegated), the quantities kept for Back from the cart. */
    initQty() {
      this.qtyCards = (this.cards || []).filter((card) => card.configEl && card.configEl.querySelector('[data-need="qty"]'));
      if (!this.qtyCards.length) return;
      let saved = {};
      try {
        const nav = window.performance.getEntriesByType ? window.performance.getEntriesByType('navigation')[0] : null;
        if (nav && nav.type === 'back_forward') saved = JSON.parse(window.sessionStorage.getItem('babyset2:qty') || '{}') || {};
        else window.sessionStorage.removeItem('babyset2:qty');
      } catch (error) {
        saved = {};
      }
      this.qtyCards.forEach((card) => {
        card.qtyOn = true;
        card.qty = Math.max(1, Math.min(QTY_MAX, Number(saved[card.handle]) || 1));
      });
      this.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-qty-step]') : null;
        if (!button || !this.sheet || !this.sheet.card.qtyOn) return;
        this.setQty(this.sheet.card, this.sheet.card.qty + Number(button.dataset.qtyStep));
      });
      this.addEventListener('change', (event) => {
        const input = event.target instanceof Element ? event.target.closest('[data-qty-input]') : null;
        if (!input || !this.sheet || !this.sheet.card.qtyOn) return;
        this.setQty(this.sheet.card, Number(input.value));
      });
    }

    qtyOf(card) {
      return card && card.qtyOn ? card.qty || 1 : 1;
    }

    setQty(card, value) {
      const qty = Math.max(1, Math.min(QTY_MAX, Math.round(Number(value)) || 1));
      card.qty = qty;
      try {
        const saved = JSON.parse(window.sessionStorage.getItem('babyset2:qty') || '{}') || {};
        saved[card.handle] = qty;
        window.sessionStorage.setItem('babyset2:qty', JSON.stringify(saved));
      } catch (error) {
        // private mode: the quantity only lives on this page
      }
      if (this.sheet && this.sheet.card === card) this.renderSheet();
      else this.renderQty(card);
    }

    /** The stepper's value, its − button, and "Još N kom. do X % popusta" (Marta's tiers, QTY_TIERS). */
    renderQty(card) {
      if (!card || !card.qtyOn || !card.configEl) return;
      const qty = this.qtyOf(card);
      const input = card.configEl.querySelector('[data-qty-input]');
      if (input && Number(input.value) !== qty) input.value = String(qty);
      const minus = card.configEl.querySelector('[data-qty-step="-1"]');
      if (minus) minus.disabled = qty <= 1;
      const plus = card.configEl.querySelector('[data-qty-step="1"]');
      if (plus) plus.disabled = qty >= QTY_MAX;
      const next = card.configEl.querySelector('[data-qty-next]');
      if (!next) return;
      const tier = QTY_TIERS.find(([count]) => qty < count);
      const reached = [...QTY_TIERS].reverse().find(([count]) => qty >= count);
      let text = '';
      if (tier) text = `Još ${tier[0] - qty} kom. do ${tier[1]} % popusta`;
      if (reached) text = tier ? `Popust ${reached[1]} %. ${text}` : `Ostvaren popust ${reached[1]} %`;
      if (next.textContent !== text) next.textContent = text;
    }

    /** "Počni slagati" until she has started (and the set is empty), then "Nastavi slagati"; the errors; S6, S9. */
    renderStart() {
      if (!this.startButton) return;
      const fresh = !this.started && !this.chosenPieces().length;
      const label = fresh ? this.texts.startButton || 'Počni slagati' : this.texts.continueButton || 'Nastavi slagati';
      if (this.startLabel && this.startLabel.textContent !== label) this.startLabel.textContent = label;
      const errors = this.startErrors || {};
      this.showStartError(this.ageError, Boolean(errors.age) && this.needsSetSize(), this.sizeQuestion);
      this.showStartError(this.forError, Boolean(errors.for) && this.needsFor(), this.forRoot);
      if (this.ageNote) this.ageNote.hidden = false;
      if (this.mixedNote) this.mixedNote.hidden = !this.mixedSizes();
    }

    /** An error under a start question, linked to its group (aria-describedby on the fieldset). */
    showStartError(error, on, group) {
      if (!error) return;
      error.hidden = !on;
      if (!group) return;
      const ids = (group.getAttribute('aria-describedby') || '').split(/\s+/).filter((id) => id && id !== error.id);
      if (on) ids.push(error.id);
      if (ids.length) group.setAttribute('aria-describedby', ids.join(' '));
      else group.removeAttribute('aria-describedby');
    }

    /**
     * The button: in "Boje bira Šigureca" the age and "Za" are required – what is missing gets its error and the focus
     * (no start). Otherwise to the step she came from ("Promijeni"), else to step 1.
     */
    startFlow() {
      if (this.martaMode) {
        const age = this.needsSetSize();
        const forChoice = this.needsFor();
        if (age || forChoice) {
          this.startErrors = { age, for: forChoice };
          this.renderStart();
          const target = age
            ? this.sizeInputs.length && this.sizeInputs[0].input
            : this.forRadios.find((radio) => !radio.disabled);
          if (target) target.focus();
          return;
        }
      }
      this.startErrors = null;
      this.started = true;
      const last = this.steps.length - 1;
      const target = this.returnTo != null && this.returnTo > 0 && this.returnTo <= last ? this.returnTo : 1;
      this.returnTo = null;
      this.goToStep(target);
    }

    /* ---------- 22. 10. 2026: gotovi setovi, „Složi u set”, čestitka ---------- */

    /** The „Gotovi set” tiles of the start screen (snippets/babyset-start-v2.liquid): one button each. */
    initPresets() {
      this.presets = (this.data.presets || [])
        .map((preset) => ({ ...preset, el: this.querySelector(`[data-preset="${CSS.escape(String(preset.id))}"]`) }))
        .filter((preset) => preset.el);
      this.presets.forEach((preset) => {
        const tile = preset.el.closest('.bs2-preset');
        preset.meta = tile ? tile.querySelector('[data-preset-meta]') : null;
        preset.el.addEventListener('click', () => this.applyPreset(preset));
      });
    }

    /** The cards of a ready set: each product once, in the first part that has it in stock (v1's rule). */
    presetCards(preset) {
      const used = new Set();
      const result = [];
      (preset.products || []).forEach((handle) => {
        if (used.has(handle)) return;
        const card = this.cards.find((item) => item.handle === handle && item.product.available && !this.isDisabled(item));
        if (!card) return;
        used.add(handle);
        result.push(card);
      });
      return result;
    }

    /**
     * „4 komada · od 85,00 € · −20 % ovaj vikend · gumica za mamu gratis” under each tile (its pieces at their lowest
     * prices; the ready sets' text only while their code is on).
     */
    updatePresets() {
      const deal = this.presetDealActive() ? this.presetDeal.label : '';
      (this.presets || []).forEach((preset) => {
        if (!preset.meta) return;
        const cards = this.presetCards(preset);
        const totals = this.computeTotals(cards);
        const text = cards.length ? `${pieces(cards.length)} · ${totals.approx ? 'od ' : ''}${this.format(totals.subtotal)}` : '';
        const gift = Boolean(text) && this.gift.enabled && totals.subtotal > this.gift.threshold;
        // a set missing a piece (sold out, or not in the chosen size) is no longer the ready set: no deal text
        const shown = text && this.presetComplete(preset) ? deal : '';
        const key = `${text}|${shown}|${gift}`;
        if (preset.metaKey === key) return;
        preset.metaKey = key;
        preset.meta.textContent = text;
        if (shown) preset.meta.append(' · ', el('strong', 'bs2-preset__deal', shown));
        if (gift) preset.meta.append(' · gumica za mamu gratis');
      });
    }

    /**
     * Every product of the „Gotovi set” block can go in the set now: none sold out (data total = the block's products,
     * available or not) and none ruled out by the chosen size.
     */
    presetComplete(preset) {
      const cards = this.presetCards(preset);
      const total = Number(preset.total) || (preset.products || []).length;
      return cards.length > 1 && cards.length === total;
    }

    /**
     * Why a ready set is not complete, for its notice ('' when it is): „Baby body s vezom inicijalom“ nema u veličini
     * 0–3 mjeseca. Dodaj ga u drugoj veličini (korak „Gornji dio“) pa vrijedi −20 % ovaj vikend. – or a piece that is
     * sold out. The code's text only while it is on.
     */
    presetMissingText(preset) {
      const have = new Set(this.presetCards(preset).map((card) => card.handle));
      const label = this.presetDealActive() ? this.presetDeal.label : '';
      const parts = [];
      (preset.products || []).forEach((handle) => {
        if (have.has(handle)) return;
        const card = this.cards.find((item) => item.handle === handle && item.product.available);
        if (!card) return;
        if ((card.reason === 'missing' || card.reason === 'soldout') && this.size) {
          const slot = this.slotById(card.slotId);
          const where = slot ? ` (korak „${slot.name}“)` : '';
          parts.push(
            `„${card.product.title}“ nema u veličini ${this.sizeLabel(this.size)}. Dodaj ga u drugoj veličini${where}${label ? ` pa vrijedi ${label}` : ''}.`
          );
        } else {
          parts.push(`„${card.product.title}“ trenutno nije dostupan${label ? `, pa ${label} za ovaj set ne vrijedi` : ''}.`);
        }
      });
      const total = Number(preset.total) || 0;
      if (!parts.length && total > (preset.products || []).length) {
        parts.push(`Jedan komad seta trenutno nije dostupan${label ? `, pa ${label} za ovaj set ne vrijedi` : ''}.`);
      }
      return parts.length ? ` ${parts.join(' ')}` : '';
    }

    /**
     * „Odaberi ovaj set”: the set is the ready set's pieces (the set before goes, „Poništi” brings it back) and Pregled
     * opens – a piece without its colour says „Odaberi boju” there. In „Boje bira Šigureca” the start questions come
     * first (startFlow's rule); „Nastavi slagati” then goes to Pregled. linked: opened by ?gotovi= on load (no focus
     * move, no new history entry; tracker step „<id>_link”).
     */
    applyPreset(preset, { linked = false } = {}) {
      if (this.sheet || this.busy) return;
      const cards = this.presetCards(preset);
      if (!cards.length) return;
      const before = this.chosenPieces().length ? this.snapshot() : null;
      this.cancelAdvance({ say: false });
      this.clearTransient();
      this.clearNotice();
      this.slots.forEach((slot) => {
        this.selection[slot.id] = [];
        this.setSlotMessage(slot.id, '');
      });
      this.skipped = new Set();
      let added = 0;
      cards.forEach((card) => {
        const slot = this.slotById(card.slotId);
        const list = this.selection[card.slotId];
        if (!slot || list.length >= slot.limit || ((!slot.multi || slot.max === 1) && list.length)) return;
        if (card.ef) this.efLoad(card);
        list.push(card.handle);
        added += 1;
      });
      this.origin = { step: linked ? `${String(preset.id).slice(0, 35)}_link` : String(preset.id).slice(0, 40) };
      const incomplete = this.chosenPieces().some(({ card }) => !this.pieceReady(card));
      const deal = this.presetDealActive() && this.presetDeal.label && this.matchedPreset() ? ` (${this.presetDeal.label})` : '';
      // a piece left out (not in the chosen size, sold out): say which one and how to get the ready set's deal back
      const missing = this.presetMissingText(preset);
      let text = `U setu je „${preset.name}“: ${pieces(added)}${deal}.${missing}`;
      if (incomplete) text += ' Komadima još odaberi boju – klikni „Uredi“.';
      const review = this.steps.length - 1;
      if (this.martaMode && (this.needsSetSize() || this.needsFor())) {
        this.returnTo = review;
        this.update();
        this.startFlow();
        this.notify({ text: `U setu je „${preset.name}“: ${pieces(added)}${deal}.${missing} Odgovori na pitanja pa nastavi.`, undoLabel: 'Poništi gotov set', before, kind: 'preset' });
        return;
      }
      this.returnTo = null;
      this.liveNote = text;
      this.goToStep(review, linked ? { push: false, replace: true, focus: false, scroll: false } : undefined);
      this.notify({ text, undoLabel: 'Poništi gotov set', before, kind: 'preset' });
    }

    /**
     * ?gotovi=<naziv-seta> (a link from Instagram or a message): the ready set it names – the block's name as a slug
     * („puffy-set”) or its id with or without „gotovi_” („puffy”) – or null. The parameter leaves the address
     * (replaceState; utm_* and the rest stay), so a reload does not open it again. Only on a fresh load.
     */
    linkedPreset() {
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (error) {
        return null;
      }
      if (!params.has('gotovi')) return null;
      const wanted = presetSlug(params.get('gotovi'));
      params.delete('gotovi');
      try {
        const query = params.toString();
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      } catch (error) {
        // the address keeps the parameter: a reload opens the same set again (harmless)
      }
      if (!wanted) return null;
      return (
        (this.presets || []).find((preset) => {
          const id = presetSlug(preset.id);
          return id === wanted || id === `gotovi-${wanted}` || presetSlug(preset.name) === wanted;
        }) || null
      );
    }

    /* ---------- 22. 10. 2026, late: the ready sets' hidden code (Marta's weekend sale) ---------- */

    /** The ready sets' code is on today (shop time, by the customer's clock – a cached page can outlive the last day). */
    presetDealActive() {
      const { enabled, start, end, offset } = this.presetDeal || {};
      if (!enabled) return false;
      const today = shopToday(offset);
      return (!start || today >= start) && (!end || today <= end);
    }

    /**
     * The complete ready set whose every piece is in this set (any colours, extra pieces allowed), or null. A ready set
     * that lacks a piece (sold out, or not in the chosen size – e.g. the body for 0–6 months) never matches: Shopify's
     * code wants its 4 pieces.
     */
    matchedPreset(chosen = this.chosenPieces()) {
      const handles = new Set(chosen.map(({ card }) => card.handle));
      return (
        (this.presets || []).find((preset) => {
          const cards = this.presetCards(preset);
          return this.presetComplete(preset) && cards.every((card) => handles.has(card.handle));
        }) || null
      );
    }

    /**
     * „Gotov set „Puffy set“: −20 % ovaj vikend – …” at the top of the set bar and above the add button of Pregled
     * (the coupon's places and look), shown only while the set holds a whole ready set and the code is on.
     */
    initPresetDeal() {
      this.presetDealLines = [];
      this.presetDealText = null;
      this.presetDealLast = null;
      if (!this.presetDeal.enabled || !this.presetDeal.label) return;
      const places = [];
      if (this.bar) {
        const first = this.bar.querySelector(':scope > .babyset-bar__notice') || this.bar.querySelector(':scope > .babyset-bar__main');
        if (first) places.push({ before: first, modifier: 'babyset-coupon--bar' });
      }
      this.addButtons.forEach((button) => {
        if (this.bar && this.bar.contains(button)) return;
        places.push({ before: button, modifier: 'babyset-coupon--summary' });
      });
      places.forEach(({ before, modifier }) => {
        const line = el('p', `babyset-coupon babyset-coupon--deal ${modifier}`);
        const icon = el('span', 'babyset-coupon__icon');
        icon.setAttribute('aria-hidden', 'true');
        icon.append(this.iconClone('__tag'));
        const text = el('span', 'babyset-coupon__text');
        line.append(icon, text);
        line.hidden = true;
        before.before(line);
        this.presetDealLines.push({ line, text });
      });
    }

    /**
     * The line's text: the whole ready set → its deal; the ready set the customer had until a piece was swapped or
     * removed → that the deal is for the whole ready set only (presetDealLast, cleared when the set is emptied or a
     * snapshot is restored); anything else → no line.
     */
    renderPresetDeal() {
      if (!this.presetDealLines || !this.presetDealLines.length) return;
      const active = this.presetDealActive();
      const preset = active ? this.matchedPreset() : null;
      if (preset) this.presetDealLast = preset;
      else if (!this.chosenPieces().length) this.presetDealLast = null;
      let text = '';
      if (preset) text = `Gotov set „${preset.name}“: ${this.presetDeal.label} – popust se obračunava u košarici.`;
      else if (active && this.presetDealLast) {
        text = `Set više nije cijeli „${this.presetDealLast.name}“ – ${this.presetDeal.label} vrijedi samo za cijeli gotov set.`;
      }
      if (this.presetDealText === text) return;
      this.presetDealText = text;
      this.presetDealLines.forEach(({ line, text: host }) => {
        line.hidden = !text;
        host.textContent = text;
      });
    }

    /** After a successful add: a set with every piece of a ready set gets the ready sets' code (never throws). */
    async applyPresetDeal(chosen) {
      if (!this.presetDealActive() || !this.matchedPreset(chosen)) return false;
      return this.putCode(this.presetDeal.code);
    }

    /**
     * „Složi u set” from a product page (snippets/slozi-u-set.liquid): ?komad=<handle>[&varijanta=<id>] puts that piece
     * in the set on load – with the colour picked on the product page while it is in stock here – and the start
     * screen says so; „Nastavi slagati” goes on. The parameters leave the address (replaceState), so a reload adds
     * nothing again. Only on a fresh load (a set restored after Back wins).
     */
    applyLinkedPiece() {
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (error) {
        return false;
      }
      const handle = params.get('komad');
      if (!handle) return false;
      const variantId = params.get('varijanta');
      params.delete('komad');
      params.delete('varijanta');
      try {
        const query = params.toString();
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      } catch (error) {
        // the address keeps the parameters: a reload adds the same piece again (harmless)
      }
      const card = this.cards.find((item) => item.handle === handle && item.product.available && !this.isDisabled(item));
      if (!card) return false;
      const variant = variantId ? this.findVariant(card, variantId) : null;
      if (variant && variant.available && !variant.marta) {
        card.variant = variant;
        card.ownVariant = variant;
        card.colourPicked = true;
        card.reason = null;
        if (card.product.colourIndex !== -1) card.picked.colour = true;
        if (card.product.options.length > 1) {
          card.picked.option = true;
          card.picked.variant = true;
        }
        this.syncSplit(card);
        this.setSizeFit(card);
        this.showVariantImage(card);
      }
      if (card.ef) this.efLoad(card);
      this.selection[card.slotId] = [card.handle];
      this.origin = { step: 'proizvod', product: handle.slice(0, 100) };
      const name = card.variant && card.combo ? `${card.product.title} (${card.combo})` : card.product.title;
      this.notify({ text: `${name} je već u setu. Klikni „Nastavi slagati“ i dodaj ostale komade.` });
      this.update();
      return true;
    }

    /** „Poruka za čestitku” in Pregled: kept as typed, cleaned only for the cart (cardNoteText). */
    initCardNote() {
      const card = this.data.card || {};
      this.cardNoteOn = Boolean(card.enabled);
      this.cardNoteProperty = card.property || 'Poruka za čestitku';
      this.cardNoteMax = Math.max(1, Math.round(Number(card.max) || 200));
      this.cardNote = '';
      this.cardNoteInput = this.querySelector('[data-card-note]');
      this.cardNoteCount = this.querySelector('[data-card-note-count]');
      if (!this.cardNoteInput) return;
      this.cardNoteInput.addEventListener('input', () => {
        this.cardNote = this.cardNoteInput.value.slice(0, this.cardNoteMax);
        this.renderCardNote();
        this.saveState();
      });
    }

    renderCardNote() {
      if (this.cardNoteInput && this.cardNoteInput.value !== this.cardNote) this.cardNoteInput.value = this.cardNote;
      if (this.cardNoteCount) this.cardNoteCount.textContent = `${this.cardNote.length} / ${this.cardNoteMax}`;
    }

    cardNoteText() {
      return this.cardNoteOn && this.cardNoteInput ? this.cleanText(this.cardNote, this.cardNoteMax) : '';
    }

    /* ---------- v2: the steps ---------- */

    /**
     * The steps: 0 = the start screen, 1…M = the parts (one at a time), M + 1 = Pregled. The stepper ("Natrag", one
     * button per part, "Pregled") and the bar's step button move between them; every stepper item works at any time.
     * The bar's list ends with "Pregled seta" on the steps; "Promijeni" in a settings line goes to the start screen.
     */
    initSteps() {
      this.stepper = this.querySelector('[data-stepper]');
      this.stepBack = this.querySelector('[data-stepper-back]');
      this.stage = this.querySelector('[data-stage]');
      this.reviewEl = this.querySelector('[data-step="review"]');
      this.reviewList = this.querySelector('[data-review-list]');
      this.stepButton = this.querySelector('[data-step-button]');
      this.barReview = this.querySelector('[data-bar-review]');
      const parts = this.slots
        .filter((slot) => slot.el)
        .map((slot) => ({ key: slot.id, slot, el: slot.el, heading: slot.el.querySelector('[data-step-heading]') }));
      this.steps = [
        { key: 'start', el: this.startEl, heading: this.titleEl },
        ...parts,
        { key: 'review', el: this.reviewEl, heading: this.reviewEl ? this.reviewEl.querySelector('[data-step-heading]') : null },
      ];
      this.stepperItems = parts.map((step, i) => {
        const item = this.stepper ? this.stepper.querySelector(`[data-stepper-item="${CSS.escape(step.key)}"]`) : null;
        const button = item ? item.querySelector('button') : null;
        if (button) button.addEventListener('click', () => this.goToStep(i + 1));
        return { step, item, button, dot: item ? item.querySelector('[data-stepper-dot]') : null };
      });
      this.stepperReview = this.stepper ? this.stepper.querySelector('[data-stepper-review]') : null;
      if (this.stepperReview) this.stepperReview.addEventListener('click', () => this.goToStep(this.steps.length - 1));
      if (this.stepBack) this.stepBack.addEventListener('click', () => this.goToStep(this.stepIndex - 1));
      if (this.stepButton) {
        this.stepButton.removeAttribute('aria-disabled');
        this.stepButton.addEventListener('click', () => this.onStepButton());
      }
      if (this.barReview) {
        this.barReview.addEventListener('click', () => {
          this.closePanel();
          this.goToStep(this.steps.length - 1);
        });
      }
      // every settings line ("Veličina seta: … · Boje biraš ti · Promijeni") is filled by renderSettingsLines
      this.addEventListener('click', (event) => {
        const change = event.target && event.target.closest ? event.target.closest('[data-settings-change]') : null;
        if (change && this.contains(change)) this.changeSettings();
      });
      this.baseTitle = document.title;
      this.onStageResize = () => this.fitStage();
      window.addEventListener('resize', this.onStageResize, { passive: true });
    }

    /**
     * The settings line (V2-SPEC §2.3, T7–T9) on every step (above the grid from 750 px, under it on phones), on Pregled
     * and at the top of the bar's list: "Veličina seta: 3–6 mjeseci · Boje biraš ti · Promijeni".
     */
    renderSettingsLines() {
      const parts = [];
      if (this.useGlobalSize) {
        if (!this.size) parts.push('Veličinu biraš za svaki komad');
        else if (this.mixedSizes()) parts.push('Veličina: po komadu');
        else parts.push(`Veličina seta: ${this.sizeLabel(this.size)}`);
      }
      if (this.colourMode.enabled) {
        parts.push(this.martaMode ? `Boje bira Šigureca${this.forChoice ? ` · za: ${this.forChoice}` : ''}` : 'Boje biraš ti');
      }
      const text = parts.join(' · ');
      // T9: "Promijeni" + the hidden end " dob bebe i tko bira boje"
      const what = [this.useGlobalSize ? 'dob bebe' : '', this.colourMode.enabled ? 'tko bira boje' : ''].filter(Boolean).join(' i ');
      const show = Boolean(text) && this.hasStart;
      const key = show ? `${text}\u0001${what}` : '';
      this.querySelectorAll('[data-settings-line]').forEach((line) => {
        if (line.dataset.key === key) return;
        line.dataset.key = key;
        line.hidden = !show;
        line.textContent = '';
        if (!show) return;
        line.append(el('span', 'bs2-settings__text', `${text} · `));
        const button = el('button', 'bs2-settings__change link', 'Promijeni');
        button.type = 'button';
        button.dataset.settingsChange = '';
        button.append(el('span', 'visually-hidden', ` ${what}`));
        line.append(button);
      });
      if (this.barReview) {
        const step = (this.steps || [])[this.stepIndex];
        this.barReview.hidden = !step || !step.slot;
      }
    }

    /** "Promijeni" (a settings line): the start screen; its button ("Nastavi slagati") comes back to this step. */
    changeSettings() {
      const from = this.stepIndex;
      this.closePanel();
      this.returnTo = from > 0 ? from : null;
      this.goToStep(0);
    }

    /** "Uredi" in the bar's list or on Pregled: the piece's window in edit mode (the bar's list closes first, v1). */
    editPiece(card, opener) {
      if (this.sheet || this.busy) return;
      if (this.efThen(card, opener, () => this.editPiece(card, opener))) return;
      this.openSheet(card, 'edit', opener);
    }

    /**
     * Show a step (V2-SPEC §2.3): the others are hidden, the stepper and the bar follow, the page title changes, a
     * history entry is added when the customer made the change (push), the builder's top comes to the top of the
     * window (instant with reduced motion) and the focus goes to the step's heading. A step change ends the notice;
     * an open window does not stay over another step (the piece window is cancelled, "Više o proizvodu" closes).
     * The pictures of the step's windows start loading while the customer looks at the step (warmStep).
     * A pending advance (scheduleAdvance) is dropped. An optional part left empty on the way forward counts as skipped.
     */
    goToStep(index, { push = true, replace = false, focus = true, scroll = true, fromReview = false } = {}) {
      const steps = this.steps || [];
      if (!steps.length) return;
      let target = Math.max(0, Math.min(Number(index) || 0, steps.length - 1));
      if (target === 0 && !this.hasStart) target = Math.min(1, steps.length - 1);
      this.cancelAdvance({ say: false });
      if (this.sheet) this.cancelSheet({ focus: false });
      this.closeDialog();
      const changed = target !== this.stepIndex;
      const left = steps[this.stepIndex];
      if (changed && target > this.stepIndex && left && left.slot && !left.slot.required && !this.chosenCards(left.slot.id).length) {
        this.skipped.add(left.slot.id);
      }
      this.stepIndex = target;
      this.fromReview = Boolean(fromReview);
      if (target > 0) this.started = true;
      if (target > 0) tracker.started(this.origin);
      const step = steps[target];
      this.dataset.current = target === 0 ? 'start' : step.key === 'review' ? 'review' : 'step';
      steps.forEach((item, i) => {
        if (i > 0 && item.el) item.el.hidden = i !== target;
      });
      if (this.stepper) this.stepper.hidden = target === 0;
      this.closePanel();
      if (changed) this.clearNotice();
      this.update();
      this.setDocumentTitle();
      if (replace) this.writeHistory(target, true);
      else if (push && changed) this.writeHistory(target, false);
      this.fitStage();
      if (focus) this.focusStep(target);
      if (scroll) this.scrollToStep(target);
      this.warmStep(target);
    }

    /**
     * The window's pictures of a step (the palette's and the photo tiles' lazy images in the hidden config blocks)
     * start loading while the customer looks at the step: after the first row's card photos (at most 1.5 s), in the
     * browser's idle time, the photo tiles first. So a window opens with its pictures, not with blank tiles, also on a
     * slow line. Only the shown step (V2-SPEC Q30: no image of a hidden step loads before it is shown); each step once;
     * with Save-Data only the photo tiles.
     */
    warmStep(index) {
      const step = (this.steps || [])[index];
      if (!step || !step.slot || !step.el) return;
      // dev-test-19: the step's EasyFlow products read their fields while she looks at the step
      this.efWarm(step.key);
      if (!this.warmedSteps) this.warmedSteps = new Set();
      if (this.warmedSteps.has(step.key)) return;
      this.warmedSteps.add(step.key);
      const saveData = Boolean(navigator.connection && navigator.connection.saveData);
      const cards = this.cards.filter((card) => card.slotId === step.key && card.configEl);
      const tiles = [];
      const palettes = [];
      cards.forEach((card) => {
        card.configEl.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          if (img.closest('.bs2-tile')) tiles.push(img);
          else if (!saveData) palettes.push(img);
        });
      });
      const images = [...tiles, ...palettes];
      if (!images.length) return;
      const firstRow = cards
        .slice(0, 4)
        .map((card) => card.el && card.el.querySelector('.babyset-card__img'))
        .filter((img) => img && !img.complete);
      const loaded = Promise.all(
        firstRow.map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
        )
      );
      const idle = (fn) => (typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback(fn, { timeout: 2000 }) : window.setTimeout(fn, 150));
      Promise.race([loaded, wait(1500)]).then(() =>
        idle(() => {
          images.forEach((img) => {
            img.loading = 'eager';
          });
        })
      );
    }

    stepIndexOf(key) {
      return (this.steps || []).findIndex((step) => step.key === key);
    }

    isReview() {
      const step = (this.steps || [])[this.stepIndex];
      return Boolean(step) && step.key === 'review';
    }

    /** The focus on the step's heading (the h1 on the start screen), without scrolling. */
    focusStep(index) {
      const step = (this.steps || [])[index];
      if (step && step.heading) step.heading.focus({ preventScroll: true });
    }

    /** The builder's top (the stepper row; the start screen) to the top of the window. */
    scrollToStep(index) {
      const top = this.stepScrollTop(index);
      if (Math.abs(top - window.scrollY) < 2) return;
      window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /** Where a step change scrolls the page to (scrollToStep): the builder's top at the top of the window. */
    stepScrollTop(index) {
      const anchor = index === 0 ? this.startEl || this : this.stepper || this;
      let top = anchor.getBoundingClientRect().top + window.scrollY - this.stickyTop();
      // the start screen right under the page's header: the header comes back into view too
      if (index === 0 && top < window.innerHeight / 2) top = 0;
      return Math.max(0, top);
    }

    /**
     * The room a header that really sticks takes at the top of the window. Dawn's header section is position: sticky
     * without a top in her theme (it scrolls away), which v1's headerBottom() counts as sticky while the header is in
     * view; a sticky header needs a top (or position: fixed).
     */
    stickyTop() {
      const header = document.querySelector('.section-header');
      if (!header) return 0;
      const style = window.getComputedStyle(header);
      const sticks = style.position === 'fixed' || (style.position === 'sticky' && style.top !== 'auto');
      return sticks ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    }

    /** --bs2-stage-min: on a short step the bar still sits at the bottom of the window, and it does not jump. */
    fitStage() {
      if (!this.stage) return;
      if (!this.stepIndex) {
        this.style.removeProperty('--bs2-stage-min');
        return;
      }
      const stepper = this.stepper ? this.stepper.offsetHeight : 0;
      const bar = this.bar ? this.bar.offsetHeight : 0;
      const min = Math.max(0, window.innerHeight - stepper - bar - (this.bottomOffset || 0));
      this.style.setProperty('--bs2-stage-min', `${min}px`);
    }

    /** Done: at least one piece in the part, or skipped ("Preskočeno"). */
    stepDone(index) {
      const step = (this.steps || [])[index];
      if (!step || !step.slot) return false;
      return this.chosenCards(step.key).length > 0 || this.skipped.has(step.key);
    }

    /**
     * The stepper: the current item (aria-current="step"); a part with a piece shows ✓ ("– gotovo"), a skipped one the
     * skip icon ("– preskočeno"), the others their number; "Natrag"'s target.
     */
    renderStepper() {
      if (!this.stepper || !this.steps) return;
      const total = this.steps.length - 2;
      this.stepperItems.forEach(({ step, item, button, dot }, i) => {
        if (!item || !button) return;
        const index = i + 1;
        const chosen = this.chosenCards(step.key).length > 0;
        const skipped = !chosen && this.skipped.has(step.key);
        const current = index === this.stepIndex;
        item.classList.toggle('is-current', current);
        item.classList.toggle('is-done', chosen);
        item.classList.toggle('is-skipped', skipped);
        if (current) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
        if (dot) {
          const mark = chosen ? 'done' : skipped ? 'skipped' : 'number';
          if (dot.dataset.mark !== mark) {
            dot.dataset.mark = mark;
            if (mark === 'skipped') dot.innerHTML = SKIP_ICON;
            else dot.textContent = mark === 'done' ? '✓' : String(index);
          }
        }
        let state = '';
        if (chosen) state = ' – gotovo';
        else if (skipped) state = ' – preskočeno';
        button.setAttribute('aria-label', `Korak ${index} od ${total}: ${step.slot.name}${state}`);
      });
      const review = this.isReview();
      if (this.stepperReview) {
        this.stepperReview.closest('li').classList.toggle('is-current', review);
        if (review) this.stepperReview.setAttribute('aria-current', 'step');
        else this.stepperReview.removeAttribute('aria-current');
      }
      if (this.stepBack) {
        const target = this.stepIndex - 1;
        this.stepBack.hidden = this.stepIndex === 0 || (target === 0 && !this.hasStart);
        let label = 'Natrag';
        if (target === 0) label = 'Natrag na početak';
        else if (target >= 1) label = `Natrag: korak ${target} od ${total}, ${this.steps[target].slot.name}`;
        this.stepBack.setAttribute('aria-label', label);
      }
    }

    /**
     * The bar's main button during the steps (V2-SPEC §2.10): "Odaberi: Dekica" (a required part without a piece:
     * locked look, still works), "Dalje" (a piece in the part), "Preskoči" (an empty optional part), "Pregled seta" (the
     * last part, or a part opened from Pregled). On Pregled v1's add button takes its place.
     */
    renderStepButton() {
      const button = this.stepButton;
      const steps = this.steps || [];
      const step = steps[this.stepIndex];
      if (!button || !step) return;
      const review = step.key === 'review';
      button.hidden = review || step.key === 'start';
      this.addButtons.forEach((add) => {
        if (this.bar && this.bar.contains(add)) add.hidden = !review;
      });
      if (button.hidden) return;
      const { slot } = step;
      const total = steps.length - 2;
      const index = this.stepIndex;
      const cards = this.chosenCards(slot.id);
      const inStock = this.cards.some((card) => card.slotId === slot.id && card.product.available);
      const next = index < total ? steps[index + 1].slot : null;
      let action = 'next';
      let label = 'Dalje';
      let end = next ? `: korak ${index + 1} od ${total}, ${next.name}` : '';
      if (slot.required && !cards.length) {
        action = 'gate';
        label = inStock ? `Odaberi: ${slot.name}` : `Trenutno nije dostupno: ${slot.name}`;
        end = '';
      } else if (this.fromReview || !next) {
        action = cards.length || this.fromReview ? 'review' : 'skip';
        label = action === 'review' ? 'Pregled seta' : 'Preskoči';
        end = action === 'review' ? '' : ': dalje na pregled seta';
      } else if (!cards.length) {
        action = 'skip';
        label = 'Preskoči';
        end = `: dalje na korak ${index + 1} od ${total}, ${next.name}`;
      }
      const key = `${action}|${label}|${end}`;
      if (button.dataset.key !== key) {
        button.dataset.key = key;
        button.dataset.action = action;
        button.textContent = label;
        if (end) button.append(el('span', 'visually-hidden', end));
      }
      button.classList.toggle('button--secondary', action === 'gate' || action === 'skip');
      button.classList.toggle('is-locked', action === 'gate');
    }

    /** The step button; a second click within 400 ms is ignored (one step per double click). */
    onStepButton() {
      const now = window.performance.now();
      if (this.lastStepPress && now - this.lastStepPress < 400) return;
      this.lastStepPress = now;
      const step = this.steps[this.stepIndex];
      if (!step || !step.slot) return;
      const action = this.stepButton.dataset.action;
      const last = this.steps.length - 1;
      if (action === 'gate') {
        this.revealGate(step.slot);
        return;
      }
      if (action === 'skip' && !this.chosenCards(step.slot.id).length) this.skipped.add(step.slot.id);
      if (action === 'review') {
        this.goToStep(last);
        return;
      }
      this.goToStep(Math.min(this.stepIndex + 1, last));
    }

    /**
     * "Odaberi: Dekica": the grid comes into view, the first card's button gets the focus, the bar says why (T10).
     * The notice makes the bar taller (170 px on a 320 px phone), so the card comes into view with its button above the
     * bar for every input (revealCard; V2-SPEC §2.4, §4.6), not only for the keyboard (raiseAboveBar).
     */
    revealGate(slot) {
      const card = this.cards.find((item) => item.slotId === slot.id && item.product.available && item.addButton);
      this.notify({ text: 'Ovaj dio je obavezan – odaberi jedan komad.' });
      this.update();
      // the notice row makes the bar taller now (its ResizeObserver tells later)
      this.updatePageBarSpace();
      if (card) {
        this.revealCard(card.el, card.addButton);
        card.addButton.focus({ preventScroll: true });
      } else if (slot.el) {
        this.revealInView(slot.el.querySelector('[data-slot-empty]') || slot.el);
      }
    }

    /**
     * A card into the room between the top of the window and the set bar, with its button above the bar (V2-SPEC
     * §4.6): the whole card when it fits (as v1's revealInView); a card taller than the room (a small phone, the bar
     * grown by a notice) with its button just above the bar and the top of its photo off the screen – v1's revealInView
     * shows such a card from its top and leaves the button under the bar. One scroll, only as far as needed: smooth for
     * touch and mouse, at once for the keyboard and with reduced motion. `from`: the scroll position the room is
     * measured at (goToMissing: the step's top, so the step change and the reveal are one scroll); else the current one.
     */
    revealCard(box, control, { from = null } = {}) {
      if (!box || !control || !box.isConnected) return;
      const scroller = document.scrollingElement || document.documentElement;
      const max = Math.max(0, scroller.scrollHeight - window.innerHeight);
      const base = from == null ? window.scrollY : Math.min(Math.max(0, from), max);
      // how far the page moves to get to `base`: the card's place on the screen there is its place now minus this
      const shift = base - window.scrollY;
      const top = this.stickyTop() + 16;
      const bottom = window.innerHeight - (this.pageBarSpace || 0) - 12;
      const rect = box.getBoundingClientRect();
      const button = control.getBoundingClientRect();
      let delta = 0;
      if (rect.height <= bottom - top) {
        if (rect.top - shift < top) delta = rect.top - shift - top;
        else if (rect.bottom - shift > bottom) delta = rect.bottom - shift - bottom;
      } else if (button.top - shift < top || button.bottom - shift > bottom) {
        delta = button.bottom - shift - bottom;
      }
      // when the page moves for the card: whole pixels, rounded up – a browser rounds the scroll position down, so the
      // button would end a fraction of a pixel under `bottom` and the keyboard's raiseAboveBar would move the page again
      const target = Math.max(0, delta ? Math.ceil(base + delta) : base);
      if (Math.abs(target - window.scrollY) < 1) return;
      window.scrollTo({
        top: target,
        behavior: reducedMotion() || this.inputModality === 'keyboard' ? 'auto' : 'smooth',
      });
    }

    /** Pregled: the list of the pieces with their buttons, the empty parts (v1's review list), the gift, the value. */
    renderReview() {
      if (this.reviewList && this.isReview()) this.renderList(this.reviewList, { review: true });
    }

    /* ---------- v2: history (one owner: every history call is here) ---------- */

    /**
     * Each step change by the customer adds an entry (#korak-N, #pregled; no hash for the start screen); Back and
     * Forward move between the steps. Windows add no entries. No history in the theme editor.
     */
    initHistory() {
      this.historyOn = !(window.Shopify && window.Shopify.designMode) && Boolean(window.history && window.history.pushState);
      if (!this.historyOn) return;
      try {
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      } catch (error) {
        // not every browser lets the page set it
      }
      this.popHandler = () => this.onPopState();
      window.addEventListener('popstate', this.popHandler);
    }

    writeHistory(index, replace) {
      if (!this.historyOn) return;
      const hash = this.stepHash(index);
      const url = `${window.location.pathname}${window.location.search}${hash}`;
      const state = { bs2: 1, step: hash.replace('#', '') };
      try {
        if (replace) window.history.replaceState(state, '', url);
        else window.history.pushState(state, '', url);
      } catch (error) {
        // a sandboxed preview: the steps still work, without entries
      }
    }

    /**
     * Back / Forward: an open window is cancelled ("Više o proizvodu" closes) and the bar's list closes, then the
     * hash's step shows (no new entry).
     */
    onPopState() {
      const index = this.stepFromHash();
      if (index == null) return;
      if (this.sheet) this.cancelSheet({ focus: false });
      this.closeDialog();
      this.closePanel();
      this.goToStep(index, { push: false });
    }

    /** "Više o proizvodu" closes (V2-SPEC §2.16, §5.3: no window stays open over a step it does not belong to). */
    closeDialog() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    /**
     * After "Više o proizvodu" has closed (v1's own handler has given the focus back to its opener): when the focus is
     * lost – the opener is on a step that is hidden now (the browser's Back with the window open) – the step's heading
     * gets it (WCAG 2.4.3). When a piece window has opened meanwhile (the theme editor's preview), the page stays locked
     * under it: v1's handler unlocks the page whenever its window closes.
     */
    initDialogFocus() {
      if (!this.dialog) return;
      this.dialog.addEventListener('close', () => {
        if (this.sheet) {
          if (!this.sheet.inline) document.documentElement.classList.add('babyset-dialog-open');
          return;
        }
        const active = document.activeElement;
        const lost =
          !active ||
          active === document.body ||
          active === document.documentElement ||
          this.dialog.contains(active) ||
          !active.getClientRects().length;
        if (lost) this.focusStep(this.stepIndex);
      });
    }

    /**
     * The bar's list closes when the focus leaves the bar (WCAG 2.4.11 Focus Not Obscured): the list is not modal, and
     * Shift+Tab from its first control went to the step behind it, which the opaque list covered. A move inside the bar
     * (the list, "Tvoj set", the step button, the notice's "Poništi") keeps it open, and so does the browser window
     * losing the focus (no relatedTarget). The focus stays where it went. v1's openPanel and closePanel are kept as
     * they are (v1's own list has the same gap: work/v2/BUILD-LOG.md, fix round 1).
     */
    initPanelFocus() {
      if (!this.bar || !this.panel) return;
      this.bar.addEventListener('focusout', (event) => {
        const next = event.relatedTarget;
        if (this.panel.hidden || !(next instanceof Node) || this.bar.contains(next)) return;
        // once the focus has moved (while focusout runs, the page's active element is not the new control yet)
        window.setTimeout(() => {
          const active = document.activeElement;
          if (this.panel.hidden || (active && this.bar.contains(active))) return;
          this.closePanel();
        }, 0);
      });
    }

    stepHash(index) {
      const key = this.stepKey(index);
      return key ? `#${key}` : '';
    }

    /** '' (start), 'korak-N', 'pregled'. */
    stepKey(index) {
      const last = (this.steps || []).length - 1;
      if (!index || index <= 0) return '';
      if (index >= last) return 'pregled';
      return `korak-${index}`;
    }

    /** The step of a key ('', 'korak-N', 'pregled'); null for anything else. */
    stepFromKey(key) {
      if (key == null) return null;
      const value = String(key).replace(/^#/, '');
      if (!value) return 0;
      if (value === 'pregled') return this.steps.length - 1;
      const match = /^korak-(\d+)$/.exec(value);
      if (!match) return null;
      const index = Number(match[1]);
      return index >= 1 && index <= this.steps.length - 2 ? index : null;
    }

    /** The step of the URL's hash; null for an unknown hash (e.g. Dawn's skip link #MainContent). */
    stepFromHash() {
      return this.stepFromKey(window.location.hash || '');
    }

    /** D1–D3: "Korak 2 od 5: Za glavu – <page>", "Pregled seta – <page>", the page title on the start screen. */
    setDocumentTitle() {
      const step = (this.steps || [])[this.stepIndex];
      const base = this.baseTitle || document.title;
      let title = base;
      if (step && step.key === 'review') title = `${this.texts.reviewHeading || 'Pregled seta'} – ${base}`;
      else if (step && step.slot) title = `Korak ${this.stepIndex} od ${this.steps.length - 2}: ${step.slot.name} – ${base}`;
      if (document.title !== title) document.title = title;
    }

    /**
     * The first screen: after Back (a restored set) the hash's step, else the saved one; otherwise the start screen
     * (v1's rule: a reload or a new visit starts empty) – the hash is cleared. No focus move on load.
     */
    routeOnLoad(restored) {
      let index = 0;
      if (restored) {
        const fromHash = this.stepFromHash();
        index = fromHash != null && fromHash > 0 ? fromHash : restored.step != null ? restored.step : 0;
        this.started = restored.started || index > 0;
        this.returnTo = restored.returnTo;
      }
      this.goToStep(index, { push: false, replace: true, focus: false, scroll: false });
    }

    /* ---------- sizes chosen per piece ---------- */

    /** A piece of the set has a size of its own: chosen on its card (no set size, or another one than the set size). */
    mixedSizes() {
      return this.chosenPieces().some(({ card }) => {
        if (!card.variant || !card.variant.sizeKey) return false;
        return this.size ? card.override : true;
      });
    }

    /* ---------- summary, bar + cart ---------- */

    initSummary() {
      this.bar = this.querySelector('[data-bar]');
      this.panel = this.querySelector('[data-bar-panel]');
      this.backdrop = this.querySelector('[data-bar-backdrop]');
      this.toggle = this.querySelector('[data-bar-toggle]');
      this.live = this.querySelector('[data-live]');
      this.addButtons = [...this.querySelectorAll('[data-add]')];
      this.errors = [...this.querySelectorAll('[data-error]')];
      this.lists = [...this.querySelectorAll('[data-summary-list]')];
      this.thumbsHost = this.querySelector('[data-thumbs]');
      this.addButtons.forEach((button) => button.addEventListener('click', () => this.addToCart()));

      if (this.toggle && this.panel) {
        this.toggle.addEventListener('click', () => (this.panel.hidden ? this.openPanel() : this.closePanel(true)));
        const close = this.panel.querySelector('[data-bar-close]');
        if (close) close.addEventListener('click', () => this.closePanel(true));
        if (this.backdrop) this.backdrop.addEventListener('click', () => this.closePanel(true));
        this.keyHandler = (event) => {
          if (event.key === 'Escape' && this.panel && !this.panel.hidden) this.closePanel(true);
        };
        document.addEventListener('keydown', this.keyHandler);
      }

      // The whole bar, not only its main row: the "Uklonjeno iz seta … Poništi" notice and the error row above and
      // below it make the bar taller, and the page's scroll-padding must follow (else Tab lands under the bar).
      // The list of pieces is positioned above the bar and does not count.
      if (this.bar && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.style.setProperty('--babyset-bar-height', `${this.bar.offsetHeight}px`);
          if (this.updatePageBarSpace()) this.keepFocusAboveBar();
        });
        this.resizeObserver.observe(this.bar);
      }
    }

    /**
     * Height the sticky set bar (plus Shopify's preview bar under it) covers at the bottom of the screen.
     * Returns true when that space grew.
     */
    updatePageBarSpace() {
      const before = this.pageBarSpace || 0;
      const height = this.bar && this.bar.offsetHeight > 0 ? this.bar.offsetHeight + (this.bottomOffset || 0) : 0;
      this.setPageBarSpace(height);
      return height > before;
    }

    /**
     * The bar just grew (a notice or an error row appeared): the control the keyboard is on moves up above the
     * taller bar, like a newly focused one. A control further down, off the screen, is left where it is.
     */
    keepFocusAboveBar() {
      const active = document.activeElement;
      if (!active || active.getBoundingClientRect().top >= window.innerHeight) return;
      this.raiseAboveBar(active);
    }

    /**
     * Keyboard focus only (isKeyboardFocus: mouse and touch focus is left alone), not in the bar itself: what the
     * customer sees of the focused control comes above the set bar.
     * - a card's "Dodaj u set": the whole card when it fits above the bar, else the button;
     * - a visually hidden size / colour input: its chip (label);
     * - anything else (a field, a select): the control itself (the bar grew under it).
     * Nothing inside a window (modal, above the bar).
     */
    raiseAboveBar(control) {
      if (!control || !control.closest || !this.contains(control) || (this.bar && this.bar.contains(control))) return;
      if (this.dialog && this.dialog.contains(control)) return;
      if (control.closest('dialog')) return;
      if (!this.isKeyboardFocus(control)) return;
      // false when the box is taller than the room above the bar
      const raise = (box) => {
        const room = window.innerHeight - (this.pageBarSpace || 0) - 12;
        const rect = box.getBoundingClientRect();
        if (rect.height >= room) return false;
        if (rect.bottom > room) window.scrollBy({ top: rect.bottom - room, behavior: 'auto' });
        return true;
      };
      const card = control.matches('[data-add-piece]') ? control.closest('[data-card]') : null;
      if (card) {
        if (raise(card)) return;
        raise(control);
        return;
      }
      if (control.classList.contains('visually-hidden') && control.id) {
        const label = this.querySelector(`label[for="${CSS.escape(control.id)}"]`);
        if (label && raise(label)) return;
      }
      raise(control);
    }

    /**
     * The page scrolls a focused control above the set bar (scroll-padding on the page = WCAG 2.4.11: Tab never
     * lands on a field hidden under the bar). Shopify's hCaptcha badge (after the newsletter field) moves above
     * the bar too (CSS: html.babyset-page-bar), so it never covers the main button.
     */
    setPageBarSpace(height) {
      const root = document.documentElement;
      if (this.pageBarSpace === height) return;
      this.pageBarSpace = height;
      if (!height) {
        root.style.removeProperty('scroll-padding-bottom');
        root.style.removeProperty('--babyset-page-bar');
        root.classList.remove('babyset-page-bar');
        return;
      }
      root.style.scrollPaddingBottom = `${height + 16}px`;
      root.style.setProperty('--babyset-page-bar', `${height}px`);
      root.classList.add('babyset-page-bar');
    }

    /**
     * Shopify shows its own preview bar at the bottom of preview links (unpublished theme). It sits in the
     * top layer and would cover the set bar and its button, so the set bar moves up by the preview bar's
     * height while it is on screen. On the published theme there is no preview bar and the offset is 0.
     */
    initPreviewBarOffset() {
      const measure = () => {
        const bar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
        let offset = 0;
        if (bar) {
          const rect = bar.getBoundingClientRect();
          const viewport = window.innerHeight;
          const style = window.getComputedStyle(bar);
          const visible = rect.height > 0 && rect.width > 0 && style.display !== 'none' && style.visibility !== 'hidden';
          if (visible && rect.bottom >= viewport - 2 && rect.top > viewport / 2) offset = Math.round(viewport - rect.top);
        }
        if (offset !== this.bottomOffset) {
          this.bottomOffset = offset;
          this.style.setProperty('--babyset-bottom-offset', `${offset}px`);
          this.classList.toggle('has-preview-bar', offset > 0);
          this.updatePageBarSpace();
        }
      };
      const watch = () => {
        const bar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
        if (bar && bar !== this.watchedPreviewBar) {
          this.watchedPreviewBar = bar;
          if (this.pbarResize) this.pbarResize.observe(bar);
        }
        measure();
      };
      if ('ResizeObserver' in window) this.pbarResize = new ResizeObserver(measure);
      if ('MutationObserver' in window) {
        this.pbarMutation = new MutationObserver(watch);
        this.pbarMutation.observe(document.body, { childList: true });
      }
      this.onWindowResize = measure;
      window.addEventListener('resize', measure, { passive: true });
      watch();
      // The preview bar is injected after load and can change without a resize – check again for a while.
      let checks = 0;
      this.pbarTimer = window.setInterval(() => {
        watch();
        checks += 1;
        if (checks > 30) window.clearInterval(this.pbarTimer);
      }, 1000);
    }

    /**
     * The list opens above the bar. When the bar has settled at the end of the builder (higher up the screen), the
     * list gets only the room above it (and scrolls inside), so its heading and × never go off the top.
     */
    fitPanel() {
      if (!this.panel || this.panel.hidden || !this.bar) return;
      const room = Math.floor(this.bar.getBoundingClientRect().top) - 8;
      this.style.setProperty('--babyset-panel-room', `${Math.max(160, room)}px`);
    }

    openPanel() {
      if (!this.panel) return;
      this.panel.hidden = false;
      if (this.backdrop) this.backdrop.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.bar.classList.add('is-open');
      this.classList.add('is-panel-open');
      this.fitPanel();
      if (!this.onPanelScroll) {
        this.onPanelScroll = () => this.fitPanel();
        window.addEventListener('scroll', this.onPanelScroll, { passive: true });
      }
      // the focus goes into the list (it comes before the toggle in the page), so Tab walks its pieces
      if (!this.panel.hasAttribute('tabindex')) this.panel.setAttribute('tabindex', '-1');
      this.panel.focus({ preventScroll: true });
    }

    closePanel(returnFocus) {
      if (!this.panel || this.panel.hidden) return;
      if (this.onPanelScroll) {
        window.removeEventListener('scroll', this.onPanelScroll);
        this.onPanelScroll = null;
      }
      // focus inside the closing list would be lost to the page: back to the toggle
      if (this.panel.contains(document.activeElement)) returnFocus = true;
      this.panel.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.bar.classList.remove('is-open');
      this.classList.remove('is-panel-open');
      if (returnFocus) this.toggle.focus();
    }

    /**
     * Subtotal, discount and total of a list of cards. The (optional, off by default) discount follows Shopify's
     * automatic "Amount off products" rule: percent of every eligible piece (rounded per piece) once the
     * eligible pieces reach the minimum subtotal.
     */
    computeTotals(cards) {
      const { percent, minimum } = this.discount;
      let subtotal = 0;
      let eligible = 0;
      let approx = false;
      cards.forEach((card) => {
        const price = this.piecePrice(card);
        if (!card.variant && card.product.priceVaries) approx = true;
        subtotal += price;
        if (card.product.eligible) eligible += price;
      });
      const reached = percent > 0 && cards.length > 0 && eligible > 0 && eligible >= minimum;
      const discount = reached
        ? cards
            .filter((card) => card.product.eligible)
            .reduce((sum, card) => sum + Math.round((this.piecePrice(card) * percent) / 100), 0)
        : 0;
      return {
        subtotal,
        eligible,
        discount,
        total: subtotal - discount,
        approx,
        reached,
        remaining: Math.max(0, minimum - eligible),
      };
    }

    update() {
      // While a window is open only the window follows the choices: the grid card, the bar, the lists, the live region
      // and saveState wait until it closes (closeSheet then runs a full update).
      if (this.sheet) {
        this.renderSheet();
        return;
      }
      this.renderColourMode();
      this.cards.forEach((card) => {
        this.renderControls(card);
        this.renderCard(card);
      });
      this.slots.forEach((slot) => this.renderSlotLimit(slot));
      this.updateSizeChips();
      this.updateSummary();
      this.renderStart();
      this.updatePresets();
      this.renderStepper();
      this.renderReview();
      this.renderSettingsLines();
      this.saveState();
    }

    /** "Odaberi jednu" / "Najviše 3 komada" -> "Odabrano 2 od najviše 3". */
    renderSlotLimit(slot) {
      if (!slot.limitEl) return;
      const count = this.chosenCards(slot.id).length;
      let text;
      if (!slot.multi) text = count ? 'Odabrano 1 od 1' : 'Odaberi jednu';
      // Karlo 4. 10.: no „Najviše 10 komada” over a part of 2 products – only the count once something is chosen
      else if (!slot.capped) text = count ? `Odabrano ${count}` : '';
      else if (slot.max === 1) text = count ? 'Odabrano 1 od najviše 1' : 'Najviše 1 komad';
      else text = count ? `Odabrano ${count} od najviše ${slot.max}` : `Najviše ${pieces(slot.max)}`;
      if (slot.limitEl.textContent !== text) slot.limitEl.textContent = text;
      slot.limitEl.hidden = !text;
      slot.limitEl.classList.toggle('is-full', slot.multi && slot.capped && count >= slot.max && slot.max > 1);
    }

    /**
     * Split card (trakica): the colour buttons show her colour (never "Šigureca bira"), a colour without any stock in
     * the chosen size is crossed out, and a size without her colour is marked in the list.
     */
    renderSplit(card, selected) {
      const { product } = card;
      this.renderPersonalPrice(card);
      const inSize = (variant) => !card.splitSize || this.sizeGroupKey(product, variant) === card.splitSize;
      card.splitColours.forEach((radio) => {
        radio.checked = radio.value === card.splitColour && (card.picked.colour || selected || this.hidesColour(card));
        const any = product.variants.some(
          (variant) => !variant.marta && variant.available && variant.options[product.colourIndex] === radio.value && inSize(variant)
        );
        radio.disabled = !product.available || !product.variants.some(
          (variant) => !variant.marta && variant.available && variant.options[product.colourIndex] === radio.value
        );
        const label = this.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        if (product.sized) {
          if (label) label.classList.toggle('is-unavailable', !any);
        } else {
          // Karlo 3. 10. (tetra): a fabric not made in the chosen size is left out, not crossed out
          const out = Boolean(card.splitSize) && !any;
          if (label) label.hidden = out;
          if (out) radio.disabled = true;
        }
      });
      if (card.splitValueEl) {
        const text = card.splitColour ? this.optionLabel(card.splitColour) : '';
        if (card.splitValueEl.textContent !== text) card.splitValueEl.textContent = text;
      }
      // marked while the piece (in the set, or asked for with "Dodaj u set") still has no colour
      if (card.pillsEl) {
        card.pillsEl.classList.toggle('is-missing', (selected || card.wantsAdd) && !card.splitColour && !this.hidesColour(card));
      }
      if (card.select && card.sizeGroups && card.sizeGroups.split) {
        const marta = this.hidesColour(card);
        [...card.select.options].forEach((option) => {
          if (!option.value) return;
          const list = card.sizeGroups.groups.get(option.value.slice(2)) || [];
          const key = option.value.slice(2);
          const anyColour = list.some((variant) => variant.available);
          const martaHere = product.variants.some((variant) => variant.marta && variant.available && this.sizeGroupKey(product, variant) === key);
          const colourHere = card.splitColour
            ? list.some((variant) => variant.available && variant.options[product.colourIndex] === card.splitColour)
            : anyColour;
          const on = marta ? anyColour || martaHere : colourHere;
          const label = product.sized ? parseSize(key).label + (on ? '' : ' – rasprodano') : key + (on ? '' : ' – nema u toj boji');
          if (option.textContent !== label) option.textContent = label;
          option.disabled = marta ? !on : !anyColour;
        });
      }
    }

    /**
     * The grid card (V2-SPEC §2.5): the button's text and state, the ✓ on the photo, the chosen line with its colour
     * picture, the status (v1's statuses, the size in its short form), the Šigureca note, the price and its Delmis
     * Cjenici lines, the photo (v1's card.shown). The option controls are rendered in the window (renderControls).
     */
    renderCard(card) {
      const slot = this.slotById(card.slotId);
      const { product, variant } = card;
      const selected = this.isChosen(card);
      const soldOutAll = card.reason === 'soldout-all';
      const single = Boolean(slot) && (!slot.multi || slot.max === 1);
      const holder = single && !selected ? this.chosenCards(slot.id)[0] || null : null;
      const full = Boolean(slot) && !single && !selected && this.selection[slot.id].length >= slot.limit;
      const elsewhere = selected ? null : this.chosenElsewhere(card);
      const incomplete = selected && !this.pieceReady(card);
      card.el.classList.toggle('is-selected', selected);
      card.el.classList.toggle('is-replace', Boolean(holder));
      card.el.classList.toggle('is-disabled', soldOutAll);
      card.el.classList.toggle('is-full', full);
      card.el.classList.toggle('is-elsewhere', Boolean(elsewhere));
      card.el.classList.toggle('is-incomplete', incomplete);
      card.el.classList.toggle('is-override', card.override);
      card.el.classList.toggle('is-error', this.isErrorCard(card));

      // the one button: "Dodaj u set", "Zamijeni" (a part of one piece holds another), "U setu · Uredi"
      if (card.addButton) {
        let text = 'Dodaj u set';
        let end = `: ${product.title}`;
        if (selected) text = 'U setu · Uredi';
        else if (holder) {
          text = 'Zamijeni';
          end = `: ${product.title}. U setu je: ${holder.product.title}.`;
        }
        if (card.addText && card.addText.textContent !== text) card.addText.textContent = text;
        if (card.addEnd && card.addEnd.textContent !== end) card.addEnd.textContent = end;
        card.addButton.classList.toggle('button--secondary', !selected);
        card.addButton.classList.toggle('is-muted', full || Boolean(elsewhere));
        card.addButton.disabled = soldOutAll;
      }

      // Card photo (v1): swap only when the piece's photo changed; always with a full srcset of that same photo.
      // A colour without its own photo shows the colour tile instead (never another colour's photo).
      const { shown } = card;
      const swatch = Boolean(shown && shown.swatch);
      if (card.media) card.media.classList.toggle('is-swatch', swatch);
      if (swatch && card.swatchName) {
        const swatchVariant = this.findVariant(card, shown.variantId);
        card.swatchName.textContent = this.swatchLabel(card.product, swatchVariant);
      }
      if (!swatch && card.img && shown && String(shown.id) !== String(card.renderedImageId)) {
        card.renderedImageId = String(shown.id);
        if (shown.srcset) card.img.setAttribute('srcset', shown.srcset);
        else card.img.removeAttribute('srcset');
        card.img.src = shown.src;
      }

      if (card.priceEl) {
        const from = this.fromPrices(card);
        const price = variant ? variant.price : from.price;
        const compare = variant ? variant.compare : from.compare;
        const prefix = !variant && from.varies ? 'od ' : '';
        card.priceEl.textContent = '';
        const current = el('span', compare > price ? 'babyset-card__sale' : '', `${prefix}${this.format(price)}`);
        card.priceEl.append(current);
        if (compare > price) {
          const hidden = el('span', 'visually-hidden', ' Redovna cijena: ');
          const s = el('s', 'babyset-card__compare', this.format(compare));
          card.priceEl.append(hidden, s);
        }
      }
      if (card.legalEl) {
        // The lines of the theme's price-information snippet for the displayed price (one line each).
        const shownVariant = variant || this.fromPrices(card).shown;
        const lines = shownVariant ? shownVariant.legal.map(glueCurrency) : [];
        const key = lines.join('\n');
        if (card.legalEl.dataset.lines !== key) {
          card.legalEl.dataset.lines = key;
          card.legalEl.textContent = '';
          lines.forEach((line) => card.legalEl.append(el('span', 'babyset-card__legal-line', line)));
        }
        card.legalEl.hidden = lines.length === 0;
      }

      // the chosen line (in the set): the colour's picture, the options, the Šigureca note and the filled fields
      if (card.chosenEl) {
        let text = '';
        let picture = null;
        if (selected) {
          const parts = [];
          const label = this.pieceLabel(card);
          if (label) parts.push(label);
          const note = this.martaNote(card);
          if (note) parts.push(note);
          this.filledFields(card).forEach((field) => parts.push(`${field.name}: ${field.value}`));
          text = parts.join(' · ');
          if (product.colourIndex !== -1 && variant && !variant.marta && !this.hidesColour(card)) {
            picture = this.colourPicture(card, variant.options[product.colourIndex]);
          }
        }
        card.chosenEl.hidden = !text;
        if (card.chosenText && card.chosenText.textContent !== text) card.chosenText.textContent = text;
        if (card.chosenPicture) {
          setImage(card.chosenPicture, picture, '20px');
          card.chosenPicture.hidden = !picture;
        }
      }

      // the status line: v1's statuses, the size in its short form ("3–6 mj."), the full form for screen readers
      if (card.statusEl) {
        const sizeText = (key, short) => {
          const size = key ? this.sizes.get(key) : null;
          if (!size) return '';
          return short ? size.short : size.label;
        };
        let short = '';
        let full = '';
        const noCombo = product.split && !variant && card.splitColour && card.splitSize && !this.hidesColour(card);
        if (card.reason === 'soldout-all') short = 'Rasprodano';
        else if (noCombo) {
          const colour = this.optionLabel(card.splitColour);
          short = `${colour} – rasprodano u veličini ${parseSize(card.splitSize).short}`;
          full = `${colour} – rasprodano u veličini ${parseSize(card.splitSize).label}`;
        } else if (card.reason === 'missing') {
          short = `Nema u veličini ${sizeText(this.size, true)}`;
          full = `Nema u veličini ${sizeText(this.size)}`;
        } else if (card.reason === 'soldout') {
          short = `Rasprodano u veličini ${sizeText(this.size, true)}`;
          full = `Rasprodano u veličini ${sizeText(this.size)}`;
        } else if (elsewhere) short = `Već u setu – dio „${elsewhere.name}“`;
        else if (incomplete) {
          short = !variant ? `Odaberi ${this.missingOption(card)}` : this.fieldPrompt(this.missingField(card), false);
        } else if (card.combo && variant && !selected) {
          short = `„${card.combo}“ nije dostupno u veličini ${sizeText(this.size, true)}`;
          full = `„${card.combo}“ nije dostupno u veličini ${sizeText(this.size)}`;
        } else if (card.near && variant) {
          short = `Najbliža veličina: ${sizeText(variant.sizeKey, true)}`;
          full = `Najbliža veličina: ${sizeText(variant.sizeKey)}`;
        } else if (card.override && variant) {
          short = `Druga veličina: ${sizeText(variant.sizeKey, true)}`;
          full = `Druga veličina: ${sizeText(variant.sizeKey)}`;
        }
        if (!full) full = short;
        const key = `${short}\u0001${full}`;
        if (card.statusEl.dataset.key !== key) {
          card.statusEl.dataset.key = key;
          card.statusEl.textContent = '';
          if (short === full) card.statusEl.textContent = short;
          else {
            const visible = el('span', '', short);
            visible.setAttribute('aria-hidden', 'true');
            card.statusEl.append(visible, el('span', 'visually-hidden', full));
          }
        }
        card.statusEl.classList.toggle('is-prompt', Boolean(noCombo) || incomplete);
      }

      // the Šigureca note ("Boju bira Šigureca"), while the piece is not in the set (the chosen line says it then)
      if (card.martaEl) {
        const note = selected ? '' : this.martaNote(card);
        card.martaEl.hidden = !note;
        const text = card.martaEl.querySelector('[data-marta-text]');
        if (note && text && text.textContent !== note) text.textContent = note;
      }
    }

    updateSummary() {
      const chosen = this.chosenPieces();
      const totals = this.computeTotals(chosen.map(({ card }) => card));
      this.totals = totals;
      const giftNow = this.giftState(totals, chosen.length);
      this.giftNow = giftNow;
      const prefix = totals.approx ? 'od ' : '';
      const totalText = `${prefix}${this.format(totals.total)}`;
      const countText = pieces(chosen.length);

      this.querySelectorAll('[data-count]').forEach((node) => {
        node.textContent = countText;
      });
      this.querySelectorAll('[data-total]').forEach((node) => {
        node.textContent = totalText;
      });
      this.querySelectorAll('[data-subtotal]').forEach((node) => {
        node.textContent = `${prefix}${this.format(totals.subtotal)}`;
      });
      this.querySelectorAll('[data-strike]').forEach((node) => {
        node.hidden = totals.discount === 0;
        node.textContent = totals.discount ? this.format(totals.subtotal) : '';
      });
      this.querySelectorAll('[data-discount-row]').forEach((row) => {
        row.hidden = this.discount.percent === 0;
        row.classList.toggle('is-pending', !totals.reached);
        row.querySelector('[data-discount-percent]').textContent = `(${this.percentText()})`;
        row.querySelector('[data-discount-amount]').textContent = totals.reached
          ? `${MINUS}${this.format(totals.discount)}`
          : `od ${this.format(this.discount.minimum)}`;
      });
      this.updateDiscountMeters(totals, chosen.length);
      this.updateGift(giftNow);
      this.lists.forEach((list) => this.renderList(list, { review: false }));
      this.updateThumbs(countText);
      this.renderCoupon();
      this.renderPresetDeal();

      // Button state: a locked button stays focusable and tappable – it takes the customer to what is missing.
      // (No aria-disabled: it is not disabled, its label says where it goes.)
      const missing = this.slots.filter((slot) => slot.required && !this.chosenCards(slot.id).length);
      // a required part with nothing in stock (every dekica sold out): no set can be made at the moment
      const soldOut = missing.filter((slot) => !this.cards.some((card) => card.slotId === slot.id && card.product.available));
      const needsSize = chosen.filter(({ card }) => !card.variant);
      const needsName = chosen.filter(({ card }) => this.needsPersonal(card));
      let label = this.data.buttonText;
      let ready = true;
      if (soldOut.length) {
        ready = false;
        label = `Trenutno nije dostupno: ${soldOut.map((slot) => slot.name).join(', ')}`;
      } else if (missing.length) {
        ready = false;
        label = `Odaberi još: ${missing.map((slot) => slot.name).join(', ')}`;
      } else if (this.needsFor()) {
        ready = false;
        label = `Odaberi: ${this.forRadios.map((radio) => radio.value).join(' / ')}`;
      } else if (this.needsSetSize()) {
        ready = false;
        label = 'Odaberi veličinu za cijeli set';
      } else if (needsSize.length) {
        ready = false;
        const names = [...new Set(needsSize.map(({ slot }) => slot.name))];
        const words = [...new Set(needsSize.map(({ card }) => this.missingOption(card)))];
        const word = words.length === 1 ? words[0] : 'boju i veličinu';
        label =
          word === 'veličinu' && this.useGlobalSize && !this.size ? 'Odaberi veličinu' : `Odaberi ${word}: ${names.join(', ')}`;
      } else if (needsName.length) {
        ready = false;
        label = this.fieldPrompt(this.missingField(needsName[0].card), false);
      } else if (!chosen.length) {
        ready = false;
        label = 'Odaberi barem jedan komad';
      } else if (this.discount.minimumRequired && totals.subtotal < this.discount.minimum) {
        ready = false;
        label = `Još ${this.format(this.discount.minimum - totals.subtotal)} do minimalnog iznosa seta`;
      }
      this.ready = ready;
      if (!this.busy) {
        this.addButtons.forEach((button) => {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.classList.toggle('button--secondary', !ready);
          button.classList.toggle('is-locked', !ready);
          button.textContent = label;
        });
      }

      // One polite live region: "Tvoj set · 3 komada · 92,80 € · Gumica za mamu je gratis"
      let liveText = `Tvoj set · ${countText} · ${totalText}`;
      if (totals.reached) liveText += ` · Ostvaren popust ${this.percentText()} na set`;
      if (giftNow.reached) liveText += ` · ${stripEmoji(this.gift.reached)}`;
      if (this.noticeText) liveText = `${this.noticeText} ${liveText}`;
      // v2: "Dodano u set: …" (N1) and the like come first, once
      if (this.liveNote) {
        liveText = `${this.liveNote} ${liveText}`;
        this.liveNote = '';
      }
      window.clearTimeout(this.liveTimer);
      this.liveTimer = window.setTimeout(() => {
        if (this.live && this.live.textContent !== liveText) this.live.textContent = liveText;
      }, 350);
      this.renderStepButton();
    }

    updateDiscountMeters(totals, count) {
      const { percent, minimum, minimumRequired } = this.discount;
      const meters = this.querySelectorAll('[data-discount]');
      let mode = 'none';
      if (percent > 0) mode = 'discount';
      else if (minimumRequired && minimum > 0) mode = 'minimum';
      meters.forEach((meter) => {
        meter.hidden = mode === 'none';
        if (mode === 'none') return;
        const text = meter.querySelector('[data-discount-text]');
        const fill = meter.querySelector('[data-discount-fill]');
        let ratio;
        let reached;
        let message;
        if (mode === 'discount') {
          reached = totals.reached;
          ratio = minimum > 0 ? Math.min(1, totals.eligible / minimum) : count > 0 ? 1 : 0;
          message = reached
            ? `Ostvaren popust ${this.percentText()} na${NBSP}set${NBSP}(${MINUS}${this.format(totals.discount)})`
            : minimum > 0
              ? `Još ${this.format(totals.remaining)} do ${this.percentText()} popusta na set`
              : `Popust ${this.percentText()} na set`;
        } else {
          reached = totals.subtotal >= minimum;
          ratio = Math.min(1, totals.subtotal / minimum);
          message = reached
            ? 'Dosegnut je minimalni iznos seta'
            : `Još ${this.format(minimum - totals.subtotal)} do minimalnog iznosa seta`;
        }
        meter.classList.toggle('is-reached', reached);
        text.textContent = message;
        fill.style.width = `${Math.round(ratio * 1000) / 10}%`;
      });
    }

    iconClone(key) {
      const source = this.querySelector(`[data-icons] [data-icon-for="${CSS.escape(key)}"] svg`);
      return source ? source.cloneNode(true) : document.createTextNode('');
    }

    /** Small pictures of the pieces in the bar (desktop, when the bar is wide enough – CSS): every piece, the missing dekica, the gift. */
    updateThumbs(countText) {
      if (this.thumbsHost) {
        const items = [];
        this.slots.forEach((slot) => {
          const cards = this.chosenCards(slot.id);
          if (!cards.length && slot.required) {
            items.push({ key: `empty:${slot.id}`, slot, required: true, dropped: this.dropped.has(slot.id) });
          }
          cards.forEach((card) =>
            items.push({ key: this.cardKey(card), slot, card, image: this.pieceImage(card), error: this.isErrorCard(card) })
          );
        });
        if (this.giftNow && this.giftNow.reached) items.push({ key: 'gift', gift: true, image: this.giftImage() });
        const shown = items.slice(0, MAX_THUMBS);
        const rest = items.length - shown.length;
        const signature = JSON.stringify([
          shown.map((item) => [item.key, item.image ? item.image.id || item.image.src : '', item.dropped, item.error]),
          rest,
        ]);
        if (this.thumbsHost.dataset.signature !== signature) {
          this.thumbsHost.dataset.signature = signature;
          this.thumbsHost.textContent = '';
          shown.forEach((item) => {
            const thumb = el('span', 'babyset-thumb');
            if (item.required) thumb.classList.add('is-required');
            if (item.card || item.gift) thumb.classList.add('is-filled');
            if (item.gift) thumb.classList.add('is-gift');
            if (item.dropped || item.error) thumb.classList.add('is-dropped');
            const icon = el('span', 'babyset-thumb__icon');
            icon.append(this.iconClone(item.gift ? '__gift' : item.slot.id));
            const img = el('span', 'babyset-thumb__img');
            thumb.append(icon, img);
            setImage(img, item.image || null, SIZES.thumb);
            this.thumbsHost.append(thumb);
          });
          if (rest > 0) this.thumbsHost.append(el('span', 'babyset-thumb babyset-thumb--more', `+${rest}`));
        }
      }
      if (this.toggle) {
        const names = this.chosenPieces().map(({ card }) => card.product.title);
        this.toggle.setAttribute(
          'aria-label',
          `Tvoj set, ${countText}${names.length ? `: ${names.join(', ')}` : ''}. Prikaži komade seta`
        );
      }
    }

    /**
     * The set's list (V2-SPEC §2.10, §2.11). In the bar (review: false): each piece with "Uredi" and "×", each empty part
     * with "Odaberi", the gift row. On Pregled (review: true): each piece with "Uredi" and "Ukloni", the empty parts with
     * "Odaberi", no gift row (Pregled shows the gift box). A piece that still misses something says so and is marked.
     * The focus stays where it was: the same button by its key, else the first button of the row now in its place
     * (V2-SPEC §4.3).
     */
    renderList(list, { review }) {
      if (!list) return;
      const attr = review ? 'reviewAction' : 'listAction';
      const selector = review ? '[data-review-action]' : '[data-list-action]';
      const active = document.activeElement;
      // a tapped button in Safari never gets the focus: its click says where the focus belongs (listFocus)
      const intent = this.listFocus && this.listFocus.list === list ? this.listFocus : null;
      if (intent) this.listFocus = null;
      const inList = Boolean(active) && list.contains(active);
      const hadFocus = inList || Boolean(intent);
      const focusKey = intent ? intent.key : inList && active.dataset ? active.dataset[attr] : null;
      const focusRow = intent ? intent.row : inList && active.closest('li') ? [...list.children].indexOf(active.closest('li')) : -1;
      list.textContent = '';
      const itemClass = review ? 'babyset-list__item babyset-review__item' : 'babyset-list__item';
      const action = (text, key, end, extra = '') => {
        const button = el('button', `babyset-list__action link bs2-list__action${extra ? ` ${extra}` : ''}`, text);
        button.type = 'button';
        button.dataset[attr] = key;
        // the name starts with the visible word ("Uredi: Body s printom")
        if (end) button.append(el('span', 'visually-hidden', end));
        return button;
      };
      this.slots.forEach((slot) => {
        const cards = this.chosenCards(slot.id);
        cards.forEach((card) => {
          const key = this.cardKey(card);
          const row = list.children.length;
          const li = el('li', itemClass);
          const media = el('span', 'babyset-list__media');
          const image = this.pieceImage(card);
          if (image) setImage(media, image, SIZES.list);
          else media.append(this.iconClone(slot.id));
          const text = el('span', 'babyset-list__text');
          text.append(el('span', 'babyset-list__slot', slot.name));
          text.append(el('span', 'babyset-list__title', card.product.title));
          const details = [];
          if (!card.variant) details.push(`Odaberi ${this.missingOption(card)}`);
          else if (this.hidesColour(card)) {
            // "0–3 mjeseca · Boju bira Šigureca" (trakica), just "Boju bira Šigureca" (colour-only pieces)
            if (this.pieceLabel(card)) details.push(this.pieceLabel(card));
            details.push('Boju bira Šigureca');
          } else if (this.pieceLabel(card)) details.push(this.pieceLabel(card));
          this.filledFields(card).forEach((field) => details.push(`${field.name}: ${field.value}`));
          const missing = this.missingField(card);
          if (missing) details.push(this.fieldPrompt(missing, false));
          if (details.length) text.append(el('span', 'babyset-list__variant', details.join(' · ')));
          if (this.isErrorCard(card)) li.classList.add('is-error');
          if (!this.pieceReady(card)) li.classList.add('is-incomplete');
          const price = el('span', 'babyset-list__price', this.format(this.piecePrice(card)));
          const actions = el('span', 'bs2-list__actions');
          const opener = { kind: review ? 'review' : 'list', key, row };
          const edit = action('Uredi', `edit:${key}`, `: ${card.product.title}`);
          edit.addEventListener('click', () => this.editPiece(card, opener));
          actions.append(edit);
          text.append(actions);
          if (review) {
            // Pregled: "Uredi" and "Ukloni" under the piece's name (the gift box follows the list)
            const remove = action('Ukloni', `remove:${key}`, ` iz seta: ${card.product.title}`);
            remove.addEventListener('click', () => {
              this.listFocus = { list, key: `remove:${key}`, row };
              this.removePiece(slot.id, card.handle);
            });
            actions.append(remove);
            li.append(media, text, price);
          } else {
            // the bar's list: "×" at the row's end (v1)
            const end = el('button', 'babyset-list__action babyset-list__remove');
            end.type = 'button';
            end.dataset[attr] = `remove:${key}`;
            end.setAttribute('aria-label', `Ukloni iz seta: ${card.product.title}`);
            end.append(this.iconClone('__close'));
            end.addEventListener('click', () => {
              this.listFocus = { list, key: `remove:${key}`, row };
              this.removePiece(slot.id, card.handle);
            });
            li.append(media, text, price, end);
          }
          list.append(li);
        });
        if (cards.length) return;
        const li = el('li', `${itemClass} is-empty`);
        const dropped = this.dropped.has(slot.id);
        if (slot.required || dropped) li.classList.add('is-missing');
        const media = el('span', 'babyset-list__media');
        media.append(this.iconClone(slot.id));
        const text = el('span', 'babyset-list__text');
        text.append(el('span', 'babyset-list__slot', slot.name));
        let title = slot.required ? 'Obavezno – još nije odabrano' : 'Nije odabrano';
        if (dropped) title = `Uklonjeno – nema u veličini ${this.sizeLabel(this.size)}`;
        else if (!slot.required && this.skipped.has(slot.id)) title = 'Preskočeno';
        text.append(el('span', 'babyset-list__title', title));
        // B3: "Odaberi" (name "Odaberi: Za nožice") goes to the part's step
        const choose = action('Odaberi', `choose:${slot.id}`, `: ${slot.name}`);
        choose.addEventListener('click', () => this.focusSlot(slot.id));
        li.append(media, text, el('span', 'babyset-list__price'), choose);
        list.append(li);
      });

      if (!review && this.gift.enabled && this.giftNow) {
        const reached = this.giftNow.reached;
        const li = el('li', `${itemClass} babyset-list__item--gift${reached ? '' : ' is-empty'}`);
        const media = el('span', 'babyset-list__media');
        const image = reached ? this.giftImage() : null;
        if (image) setImage(media, image, SIZES.list);
        else media.append(this.iconClone('__gift'));
        const text = el('span', 'babyset-list__text');
        text.append(el('span', 'babyset-list__slot', this.gift.property));
        let title = `Gratis za set iznad ${this.formatShort(this.gift.threshold)}`;
        if (reached) {
          title = this.giftRadios.length
            ? `${this.gift.name} – ${this.giftChoice ? this.giftColourLabel() : 'odaberi boju'}`
            : this.gift.name;
        }
        text.append(el('span', 'babyset-list__title', title));
        const price = el('span', 'babyset-list__price', reached ? 'Gratis' : '');
        let giftAction = el('span', 'babyset-list__action');
        if (reached && this.giftRadios.length) {
          giftAction = el('button', 'babyset-list__action link', this.giftChoice ? 'Promijeni' : 'Odaberi');
          giftAction.type = 'button';
          giftAction.dataset[attr] = 'gift';
          // the name starts with the visible word (V2-SPEC §3.1): "Promijeni boju gumice za mamu"
          giftAction.append(el('span', 'visually-hidden', ' boju gumice za mamu'));
          giftAction.addEventListener('click', () => this.focusGift());
        }
        li.append(media, text, price, giftAction);
        list.append(li);
      }

      if (hadFocus) {
        // the same button again; when its piece is gone, the first button of the row now in its place, so the focus
        // stays in the list instead of falling back to the page
        const rows = [...list.children];
        const again =
          (focusKey && list.querySelector(`[data-${review ? 'review' : 'list'}-action="${CSS.escape(focusKey)}"]`)) ||
          (rows.length ? rows[Math.min(Math.max(focusRow, 0), rows.length - 1)].querySelector(selector) : null) ||
          (this.panel && this.panel.contains(list) ? this.panel.querySelector('[data-bar-close]') : null);
        if (again) again.focus({ preventScroll: true });
      }
    }

    /** Message in the bar / review step (role="alert"); cartLink adds "Otvori košaricu". */
    showError(text, { cartLink = false } = {}) {
      this.errors.forEach((node) => {
        node.textContent = text;
        if (text && cartLink) {
          const link = el('a', 'babyset-error__link link', 'Otvori košaricu');
          link.href = this.data.cartUrl;
          node.append(' ', link);
        }
        node.hidden = !text;
      });
    }

    clearError() {
      if (this.errorPiece) this.setSlotMessage(this.errorPiece.slotId, '');
      this.errorPiece = null;
      if (this.errors && this.errors.some((node) => !node.hidden)) this.showError('');
    }

    cartJsUrl(route, fallback) {
      return `${route || fallback}.js`;
    }

    async fetchCart() {
      const response = await fetch(this.cartJsUrl(this.data.cartUrl, '/cart'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`cart ${response.status}`);
      return response.json();
    }

    /** The cart lines of one set (hidden _babyset_id); null when the cart cannot be read. */
    async setLines(setId) {
      try {
        const cart = await this.fetchCart();
        return (cart.items || []).filter((item) => item.properties && item.properties._babyset_id === setId);
      } catch (error) {
        return null;
      }
    }

    /**
     * Which piece did Shopify refuse (422)? Its message usually names the product ("You can only add 1 … to the
     * cart", in the shop's language). When it does not ("The maximum quantity of this item is already in your
     * cart."), it is one of the pieces that are already in the cart: one such piece is named, several are all
     * named – never a guess that blames the wrong one.
     * Returns { hit, candidates }: hit = the one piece (or null).
     */
    async findUnavailable(chosen, description) {
      const text = String(description || '').toLowerCase();
      if (text) {
        let named = chosen.filter(({ card }) => text.includes(String(card.product.title).toLowerCase()));
        if (named.length > 1) {
          // "Kapica – 0-3 mjeseca": the variant decides between pieces with similar titles
          const withVariant = named.filter(
            ({ card }) => card.variant && !card.product.defaultOnly && text.includes(String(card.variant.title).toLowerCase())
          );
          if (withVariant.length) named = withVariant;
        }
        if (named.length > 1) {
          // "Body s printom" beats "Body"
          const longest = Math.max(...named.map(({ card }) => String(card.product.title).length));
          const top = named.filter(({ card }) => String(card.product.title).length === longest);
          if (top.length === 1) named = top;
        }
        if (named.length === 1) return { hit: named[0], candidates: named };
      }
      try {
        const cart = await this.fetchCart();
        const inCart = new Set((cart.items || []).map((item) => String(item.variant_id)));
        const candidates = chosen.filter(({ card }) => card.variant && inCart.has(String(card.variant.id)));
        return { hit: candidates.length === 1 ? candidates[0] : null, candidates };
      } catch (error) {
        return { hit: null, candidates: [] };
      }
    }

    pieceName({ card }) {
      const variant = this.pieceLabel(card);
      return `„${card.product.title}“${variant ? ` (${variant})` : ''}`;
    }

    /**
     * The cart attribute "Poklon za mamu" is always written after an add: the gifts of every set in the cart that
     * is still above its threshold, or empty (= removed) – never an old colour from an earlier set.
     * assets/babyset-cart.js does it from the cart itself; without it (script blocked) the builder writes this set.
     */
    async syncCart(gift) {
      try {
        if (window.BabysetCart && typeof window.BabysetCart.sync === 'function') {
          await window.BabysetCart.sync();
          return;
        }
        await fetch(this.cartJsUrl(this.data.cartUpdateUrl, '/cart/update'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [this.gift.property]: gift || '' } }),
        });
      } catch (error) {
        // The gift is on the dekica line as well; the cart attribute is only a summary.
      }
    }

    /** The cart line items of a set: every piece with "Set: Babyset", her field names and the hidden set id. */
    cartItems(chosen, { setId, gift, carrier }) {
      return chosen.map((piece) => {
        const { card } = piece;
        const properties = { Set: 'Babyset' };
        if (this.martaMode) {
          // "Boje neka izabere Šigureca": she picks colours and prints. The variant "Šigureca bira" says it in the
          // order itself ("Boja: Šigureca bira"); without one a colour in the variant's name is only a stand-in.
          // "Boje" goes on the lines whose card says she picks (martaNote), not on a piece with nothing to pick
          // (vezica "Obična", tetra pelene).
          const mode = this.colourMode;
          if (!card.variant.marta && this.martaNote(card)) {
            properties[mode.property] = card.product.colourIndex !== -1 ? mode.valueVariant : mode.value;
          }
          if (this.forChoice) properties[mode.forProperty] = this.forChoice;
          if (this.size) properties[mode.sizeProperty] = this.sizeLabel(this.size);
        }
        this.filledFields(card).forEach((field) => {
          // dev-test-19: an EasyFlow field under EasyFlow's exact name (untrimmed), as from her product page
          properties[field.cartName || field.name] = field.value;
        });
        if (gift && piece === carrier) {
          properties[this.gift.property] = gift;
          properties._babyset_prag = String(this.gift.threshold);
        }
        // 22. 10.: the message for the card, once per set (with the gift's piece, the dekica)
        const note = this.cardNoteText();
        if (note && piece === carrier) properties[this.cardNoteProperty] = note;
        properties._babyset_id = setId;
        return { id: card.variant.id, quantity: this.qtyOf(card), properties };
      });
    }

    setBusy(text) {
      this.busy = true;
      // not `disabled`: a disabled button loses the keyboard focus (and after an error it would stay lost)
      this.addButtons.forEach((button) => {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
        button.textContent = text;
      });
    }

    endBusy() {
      this.busy = false;
      this.addButtons.forEach((button) => {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
      });
      this.update();
    }

    /**
     * One request with all pieces. Every piece carries "Set: Babyset" and its filled-in fields under her names
     * ("Izaberi slovo: M", "Odaberi print: Upeklo"). Above the threshold the set's first required piece (the
     * dekica) also carries "Poklon za mamu: Gumica – Mint" – once per set, so the order never reads like several
     * gifts – plus the hidden threshold (_babyset_prag, in cents) for assets/babyset-cart.js, which then writes the
     * cart attribute from all sets in the cart (and clears it when no set has a gift any more).
     * "_babyset_id" (hidden) keeps the lines of one set together and apart from the same pieces bought again.
     *
     * A connection that drops after Shopify added the set: the builder asks the cart for this set id before it
     * reports a failure. The next click always checks the cart for that set id first – also when the customer
     * changed the set in the meantime (a piece, a field, the gift colour): the set in the cart then becomes the
     * builder's set (settlePendingSet), so it is never in the cart twice and never keeps an old colour.
     */
    async addToCart() {
      if (this.busy) return;
      // dev-test-19: a piece restored after Back may still wait for its EasyFlow fields (at most EF_TIMEOUT_MS)
      const waiting = (this.cards || []).filter((card) => this.isChosen(card) && this.efPending(card));
      if (waiting.length) {
        if (this.efCartWait) return;
        this.efCartWait = true;
        try {
          await Promise.all(waiting.map((card) => this.efLoad(card)));
        } finally {
          this.efCartWait = false;
        }
        this.update();
        if (this.busy) return;
      }
      if (!this.ready) {
        this.goToMissing();
        return;
      }
      const chosen = this.chosenPieces();
      const gift = this.giftValue();
      const carrier = chosen.find(({ slot }) => slot.required) || chosen[0];
      const context = {
        chosen,
        gift,
        carrier,
        expected: this.totals ? this.totals.subtotal : 0,
        giftReached: Boolean(this.giftNow && this.giftNow.reached),
      };
      // the pieces (not the gift): the same set again after an unanswered add
      const signature = JSON.stringify(this.cartItems(chosen, { setId: '', gift: null, carrier }));
      this.clearTransient();
      this.lateGift = null;
      this.setBusy('Dodajem set…');
      // the set of the unanswered add was in the cart with other pieces and has been taken out (for the message)
      let replaced = false;
      try {
        const pending = this.pendingAdd;
        if (pending) {
          const lines = await this.setLines(pending.setId);
          // the cart cannot be read: adding now could put the set in twice – nothing is sent, the message stays
          if (!lines) throw new Error('unknown');
          if (lines.length) {
            const items = this.cartItems(chosen, { setId: pending.setId, gift, carrier });
            const settled = await this.settlePendingSet(pending.setId, lines, items);
            if (settled.lines) {
              await this.finishAdd({ ...context, setId: pending.setId }, settled.lines);
              return;
            }
            replaced = Boolean(settled.removed);
          }
          this.pendingAdd = null;
        }
        // the same pieces again (not in the cart): the same set id, so a late answer still finds it
        const setId = pending && pending.signature === signature ? pending.setId : randomId();
        context.setId = setId;
        const items = this.cartItems(chosen, { setId, gift, carrier });
        this.pendingAdd = { setId, signature };
        let response;
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = controller ? window.setTimeout(() => controller.abort(), 30000) : null;
        try {
          response = await fetch(this.cartJsUrl(this.data.cartAddUrl, '/cart/add'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
            signal: controller ? controller.signal : undefined,
          });
        } catch (networkError) {
          // The request may have reached Shopify although its answer never reached the page: ask the cart.
          const lines = await this.setLines(setId);
          if (lines && lines.length) {
            await this.finishAdd(context, lines);
            return;
          }
          throw new Error(lines ? 'network' : 'unknown');
        } finally {
          if (timer) window.clearTimeout(timer);
        }
        if (!response.ok) {
          let status = response.status;
          let description = '';
          try {
            const body = await response.json();
            status = body.status || status;
            description = body.description || body.message || '';
          } catch (error) {
            // Non-JSON error body: keep the HTTP status.
          }
          // a refusal (4xx) added nothing; after a server error (5xx) the cart is checked on the retry
          if (response.status < 500) this.pendingAdd = null;
          const failure = new Error(String(status));
          failure.description = String(description);
          throw failure;
        }
        let body = null;
        try {
          body = await response.json();
        } catch (error) {
          body = null;
        }
        await this.finishAdd(context, body && Array.isArray(body.items) ? body.items : null);
      } catch (error) {
        let message = 'Dodavanje u košaricu nije uspjelo. Provjeri vezu i pokušaj ponovno.';
        let cartLink = false;
        let failedPiece = null;
        if (error && error.message === 'unknown') {
          message =
            'Veza se prekinula, pa nije sigurno je li set dodan. Pokušaj ponovno – prvo ćemo provjeriti košaricu, pa set neće biti dodan dvaput.';
          cartLink = true;
        } else if (error && error.message === 'pending') {
          message =
            'Set iz prošlog pokušaja već je u košarici, ali ga nije bilo moguće zamijeniti ovim setom. Otvori košaricu i provjeri ga ili pokušaj ponovno.';
          cartLink = true;
        } else if (error && error.message === '422') {
          const { hit, candidates } = await this.findUnavailable(chosen, error.description);
          failedPiece = hit || null;
          if (hit) {
            const { slot, card } = hit;
            message = `Komad ${this.pieceName(hit)} više nije dostupan u traženoj količini – možda je već u košarici. Odaberi drugu veličinu ili drugi komad.`;
            this.errorPiece = { slotId: slot.id, handle: card.handle };
            this.setSlotMessage(slot.id, 'Ovaj komad više nije dostupan u traženoj količini – odaberi drugu veličinu ili drugi komad.');
          } else if (candidates.length > 1) {
            message = `Jedan od ovih komada već je u košarici i nema ga više na zalihi: ${candidates
              .map((piece) => this.pieceName(piece))
              .join(', ')}. Ukloni ga iz seta ili odaberi drugu veličinu.`;
            cartLink = true;
          } else {
            message =
              'Nažalost, neki komad više nije dostupan u traženoj količini – možda je već u košarici ili je upravo rasprodan. Odaberi drugu veličinu ili drugi komad.';
          }
        }
        this.trackAddFailure(error, failedPiece, chosen);
        // she must not think the earlier set is still waiting in the cart
        if (replaced && error && error.message !== 'unknown') {
          message += ' Set iz prošlog pokušaja uklonjen je iz košarice, da ne bude dvaput.';
          cartLink = true;
        }
        this.endBusy();
        this.showError(message, { cartLink });
      }
    }

    /** Praćenje grešaka: the failed add (codes: sold_out_on_add, variant_not_found, add_to_cart_network, add_to_cart_failed). */
    trackAddFailure(error, piece, chosen) {
      try {
        const reason = error && error.message ? String(error.message) : '';
        let code = 'add_to_cart_failed';
        if (reason === '422') code = 'sold_out_on_add';
        else if (reason === '404') code = 'variant_not_found';
        else if (reason === 'unknown' || reason === 'network') code = 'add_to_cart_network';
        const data = { pieces: chosen.length };
        if (piece && piece.slot) data.step = piece.slot.id;
        if (piece && piece.card) {
          data.product = piece.card.handle;
          if (piece.card.variant) data.variant = String(piece.card.variant.id);
        }
        const detail = error && error.description ? `${reason}: ${error.description}` : reason || String(error);
        tracker.error(code, detail, data);
        if (code === 'add_to_cart_failed') tracker.flush();
      } catch (trackError) {
        // never in the customer's way
      }
    }

    /**
     * The set of an unanswered add is in the cart (lines = its lines, items = the builder's set now, same set id).
     * - The same pieces: each line whose fields, gift or quantity differ gets the builder's (POST /cart/change.js
     *   by line key – another colour of the gift, a changed letter). Returns { lines } (the set's lines now).
     * - Other pieces (one added or removed): its lines are removed in one request (/cart/update.js by line key);
     *   returns { removed: true } and the builder's set is then added as a new set.
     * A lost answer here throws 'unknown' (the set stays pending, the next click checks again); a refusal throws
     * 'pending'.
     */
    async settlePendingSet(setId, lines, items) {
      const unused = [...lines];
      const pairs = items.map((item) => {
        const index = unused.findIndex((line) => String(line.variant_id) === String(item.id));
        return index === -1 ? null : { item, line: unused.splice(index, 1)[0] };
      });
      const send = async (route, fallback, body) => {
        let response;
        try {
          response = await fetch(this.cartJsUrl(route, fallback), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(body),
          });
        } catch (error) {
          throw new Error('unknown');
        }
        if (!response.ok) throw new Error('pending');
        try {
          return await response.json();
        } catch (error) {
          return null;
        }
      };
      if (pairs.some((pair) => !pair) || unused.length) {
        const updates = {};
        lines.forEach((line) => {
          updates[line.key] = 0;
        });
        await send(this.data.cartUpdateUrl, '/cart/update', { updates });
        return { removed: true };
      }
      let changed = false;
      let cart = null;
      for (const { item, line } of pairs) {
        if (Number(line.quantity) === item.quantity && sameProperties(line.properties, item.properties)) continue;
        // the other lines keep their keys when one line changes
        cart = await send(this.data.cartChangeUrl, '/cart/change', { id: line.key, quantity: item.quantity, properties: item.properties });
        changed = true;
      }
      if (!changed) return { lines };
      const now = cart && Array.isArray(cart.items) ? cart.items.filter((item) => item.properties && item.properties._babyset_id === setId) : null;
      if (now && now.length) return { lines: now };
      const fresh = await this.setLines(setId);
      if (!fresh) throw new Error('unknown');
      // gone meanwhile (changed in another tab): nothing of it is left, the set is added as usual
      return fresh.length ? { lines: fresh } : {};
    }

    /**
     * The set is in the cart. Prices can change while the page is open (a sale starts, a price is corrected): what
     * the cart charges decides the gift, so when it moved the gift across the threshold the customer is told here
     * instead of silently landing on a cart without (or with a surprise) gift:
     * - no longer above the threshold: the cart script removes the gift; the page says so;
     * - now above it: the gift is written onto the set ("Gumica – boja nije odabrana") and the colour picker opens –
     *   the chosen colour replaces it (writeLateGift).
     * Otherwise straight on to the cart.
     */
    async finishAdd(context, lines) {
      const { setId, chosen, gift, carrier, expected, giftReached } = context;
      this.pendingAdd = null;
      tracker.push('babyset_added_to_cart', { ...(this.origin || {}), pieces: chosen.length, totalCents: Math.round(Number(expected) || 0) });
      tracker.flush();
      // The set is in the cart: now the set coupon (<kod>), before the gift steps read the cart. It never
      // stands in the way of the set (applyCoupon only logs its failures).
      const coupon = await this.applyCoupon();
      this.couponResult = coupon;
      // a whole ready set: the ready sets' hidden code (the weekend's −20 %); only logs its failures too
      await this.applyPresetDeal(chosen);
      const setLines = (lines || []).filter((item) => item.properties && item.properties._babyset_id === setId);
      let actual = null;
      if (setLines.length && setLines.length === chosen.length) {
        actual = setLines.reduce((sum, item) => {
          const price = item.original_line_price != null ? item.original_line_price : item.line_price;
          return sum + (Number(price) || 0);
        }, 0);
      }
      if (actual != null && actual !== expected && this.gift.enabled) {
        this.applyCartPrices(chosen, setLines);
        const nowReached = actual > this.gift.threshold;
        if (giftReached !== nowReached) {
          const value = this.format(actual);
          await this.syncCart(gift);
          // a click on the button now goes to the cart (the same set is not added twice)
          this.pendingAdd = { setId, signature: JSON.stringify(this.cartItems(chosen, { setId: '', gift: null, carrier })) };
          if (nowReached && carrier) {
            this.lateGift = { setId, variantId: carrier.card.variant.id };
            // a colour picked earlier (while the set was above the threshold) still counts
            let pending = this.gift.name;
            if (this.giftRadios.length) pending = `${this.gift.name} – ${this.giftChoice || 'boja nije odabrana'}`;
            let written = false;
            try {
              await this.writeGiftToSet(this.lateGift, pending);
              await this.syncCart(pending);
              written = true;
            } catch (error) {
              // the colour step below writes it again
            }
            if (written && (!this.giftRadios.length || this.giftChoice)) {
              this.lateGift = null;
              this.pendingAdd = null;
              await this.goToCart(coupon);
              return;
            }
            this.endBusy();
            this.showError(
              `Set je dodan u košaricu. Cijena komada upravo se promijenila, pa set vrijedi ${value} i gumica za mamu je gratis – odaberi boju gumice.${this.couponSentence(coupon)}`,
              { cartLink: true }
            );
            this.focusGift();
            return;
          }
          this.endBusy();
          this.showError(
            `Set je dodan u košaricu, ali cijena komada upravo se promijenila: set vrijedi ${value}, pa gumica za mamu nije gratis (poklon je za set iznad ${this.formatShort(this.gift.threshold)}).${this.couponSentence(coupon)}`,
            { cartLink: true }
          );
          return;
        }
      }
      await this.syncCart(gift);
      await this.goToCart(coupon);
    }

    /** The prices the cart charged become the builder's prices (the page then shows the real set value). */
    applyCartPrices(chosen, setLines) {
      setLines.forEach((item) => {
        const piece = chosen.find(({ card }) => card.variant && String(card.variant.id) === String(item.variant_id));
        if (!piece) return;
        const price = Number(item.original_price != null ? item.original_price : item.price);
        if (Number.isFinite(price)) piece.card.variant.price = price;
      });
    }

    /** Write the gift onto the set's dekica line in the cart (by its line key). */
    async writeGiftToSet({ setId, variantId }, value) {
      const lines = await this.setLines(setId);
      const line = (lines || []).find((item) => String(item.variant_id) === String(variantId));
      if (!line) throw new Error('line');
      const properties = { ...(line.properties || {}), [this.gift.property]: value, _babyset_prag: String(this.gift.threshold) };
      const response = await fetch(this.cartJsUrl(this.data.cartChangeUrl, '/cart/change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
      });
      if (!response.ok) throw new Error(`change ${response.status}`);
    }

    /** The colour chosen after the price change: onto the set in the cart, then on to the cart. */
    async writeLateGift() {
      const late = this.lateGift;
      if (!late || this.busy || !this.giftChoice) return;
      const value = `${this.gift.name} – ${this.giftChoice}`;
      this.setBusy('Zapisujem boju…');
      try {
        await this.writeGiftToSet(late, value);
        this.lateGift = null;
        this.pendingAdd = null;
        await this.syncCart(value);
        await this.goToCart(this.couponResult);
      } catch (error) {
        this.endBusy();
        this.showError('Boju gumice nije bilo moguće zapisati uz set. Odaberi je ponovno ili otvori košaricu.', {
          cartLink: true,
        });
      }
    }

    /* ---------- set coupon (e.g. <kod>) ---------- */

    /**
     * "Kod <kod> (−15 %) – automatski na blagajni." – one line at the top of the set bar and above
     * the add button of the "koraci" summary and review step. Made here, because the bar and the summary are
     * snippets shared by every layout; the hero line comes from Liquid (data-coupon-offer). Nothing is made while the
     * coupon is off.
     */
    initCoupon() {
      this.couponOffer = this.querySelector('[data-coupon-offer]');
      this.couponLines = [];
      this.couponResult = false;
      if (!this.coupon.enabled) return;
      const places = [];
      if (this.bar) {
        const first = this.bar.querySelector(':scope > .babyset-bar__notice') || this.bar.querySelector(':scope > .babyset-bar__main');
        if (first) places.push({ before: first, modifier: 'babyset-coupon--bar' });
      }
      this.addButtons.forEach((button) => {
        if (this.bar && this.bar.contains(button)) return;
        places.push({ before: button, modifier: 'babyset-coupon--summary' });
      });
      places.forEach(({ before, modifier }) => {
        const line = el('p', `babyset-coupon ${modifier}`);
        const icon = el('span', 'babyset-coupon__icon');
        icon.setAttribute('aria-hidden', 'true');
        icon.append(this.iconClone('__tag'));
        const text = el('span', 'babyset-coupon__text');
        line.append(icon, text);
        before.before(line);
        this.couponLines.push({ line, text });
      });
      this.renderCoupon();
    }

    /** The coupon is still on: not past its last day by the customer's clock (a cached page can outlive it). */
    couponActive() {
      const { enabled, end, offset } = this.coupon;
      if (!enabled) return false;
      return !end || shopToday(offset) <= end;
    }

    /**
     * Text of the coupon line: [before, code (bold), after].
     * - info (short: one line on most phones; the hero line says it in full): "Kod <kod> (−15 %) – automatski
     *   na blagajni." – or, without automatic applying, "Upiši kod <kod> na blagajni (−15 % na set)."
     * - applied (the set and the code are in the cart): "Set je u košarici · kod <kod> bit će primijenjen"
     * - added (the code was not applied): with automatic applying Shopify refused it or did not answer (the code does
     *   not exist yet, is over, does not fit these pieces or another code on the cart wins) – "Set je u košarici ·
     *   kod <kod> trenutno nije moguće primijeniti", so the full price in the cart is not a surprise; without
     *   automatic applying "Set je u košarici · upiši kod <kod> na blagajni".
     */
    couponParts(state) {
      const { code, label, autoApply } = this.coupon;
      if (state === 'applied') return ['Set je u košarici · kod ', code, ' bit će primijenjen'];
      if (state === 'added') {
        return autoApply
          ? ['Set je u košarici · kod ', code, ' trenutno nije moguće primijeniti']
          : ['Set je u košarici · upiši kod ', code, ' na blagajni'];
      }
      if (autoApply) return ['Kod ', code, `${label ? ` (${label})` : ''} – automatski na blagajni.`];
      return ['Upiši kod ', code, label ? ` na blagajni (${label} na set).` : ' na blagajni.'];
    }

    /** Fill the coupon lines (state: info | applied | added); returns their text ('' when the coupon is over). */
    renderCoupon(state = 'info') {
      const active = this.couponActive();
      if (this.couponOffer) this.couponOffer.hidden = !active;
      if (!this.couponLines || !this.couponLines.length) return '';
      const parts = this.couponParts(state);
      const key = `${active}|${state}|${parts.join('|')}`;
      if (this.couponKey !== key) {
        this.couponKey = key;
        const [before, code, after] = parts;
        this.couponLines.forEach(({ line, text }) => {
          line.hidden = !active;
          line.classList.toggle('is-added', state !== 'info');
          text.textContent = '';
          text.append(before);
          if (code) text.append(el('strong', 'babyset-coupon__code', code));
          if (after) text.append(after);
        });
      }
      return active ? parts.join('') : '';
    }

    /** " Kod <kod> bit će primijenjen." for the messages that stay on the page after the set was added. */
    couponSentence(applied) {
      if (!this.coupon.enabled || !this.couponActive()) return '';
      if (applied) return ` Kod ${this.coupon.code} bit će primijenjen.`;
      return this.coupon.autoApply
        ? ` Kod ${this.coupon.code} trenutno nije moguće primijeniti.`
        : ` Na blagajni upiši kod ${this.coupon.code}.`;
    }

    /**
     * After a successful add: put the set's code on the cart with the Ajax Cart API – POST /cart/update.js with
     * { discount: 'CODE1,CODE2' } (shopify.dev/docs/api/ajax/reference/cart#update-discounts-in-the-cart). The
     * parameter REPLACES the cart's codes, so the codes already on the cart stay in the list. The answer (the cart
     * JSON) lists discount_codes [{ code, applicable }]: a code that does not exist, is over or does not fit these
     * pieces (applicable: false) is taken off again – the customer's other codes stay – so checkout never opens with
     * an error. Silent-safe: it never throws, only logs (console.debug), gives up after 8 s, and sends nothing when
     * the cart cannot be read. Returns true when the code is on the cart and applies.
     */
    async applyCoupon() {
      if (!this.coupon.enabled || !this.coupon.autoApply || !this.couponActive()) return false;
      return this.putCode(this.coupon.code);
    }

    /** One code onto the cart the way applyCoupon describes (the set coupon, the ready sets' hidden code). */
    async putCode(code) {
      const same = (value) => String(value || '').trim().toLowerCase() === code.toLowerCase();
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller ? window.setTimeout(() => controller.abort(), 8000) : null;
      const signal = controller ? controller.signal : undefined;
      const update = async (codes) => {
        const response = await fetch(this.cartJsUrl(this.data.cartUpdateUrl, '/cart/update'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: codes.join(',') }),
          signal,
        });
        if (!response.ok) throw new Error(`update ${response.status}`);
        return response.json();
      };
      try {
        const response = await fetch(this.cartJsUrl(this.data.cartUrl, '/cart'), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal,
        });
        if (!response.ok) throw new Error(`cart ${response.status}`);
        const codes = cartCodes(await response.json());
        const current = codes.find((entry) => same(entry.code));
        if (current && current.applicable) return true;
        const others = [];
        codes.forEach((entry) => {
          if (!same(entry.code) && !others.some((other) => other.toLowerCase() === entry.code.toLowerCase())) others.push(entry.code);
        });
        const entry = cartCodes(await update([...others, code])).find((item) => same(item.code));
        if (entry && entry.applicable) return true;
        // not applicable (or not taken): the cart keeps only the other codes
        await update(others);
        console.debug(`Babyset: kod ${code} nije primijenjen (ne postoji, istekao je ili ne vrijedi za ove komade)`, entry);
        return false;
      } catch (error) {
        console.debug(`Babyset: kod ${code} nije primijenjen`, error);
        return false;
      } finally {
        if (timer) window.clearTimeout(timer);
      }
    }

    /**
     * On to the cart. With the coupon on, the bar first says so for a moment – "Set je u košarici · kod <kod>
     * bit će primijenjen" (also for screen readers); a code that could not be applied stays a little longer, so it
     * can be read. Without the coupon the cart opens at once, as before.
     */
    async goToCart(applied) {
      if (this.couponLines && this.couponLines.length && this.couponActive()) {
        const text = this.renderCoupon(applied ? 'applied' : 'added');
        this.addButtons.forEach((button) => {
          button.textContent = 'Set je u košarici';
        });
        window.clearTimeout(this.liveTimer);
        if (this.live) this.live.textContent = text;
        await wait(!applied && this.coupon.autoApply ? 2200 : 600);
      }
      window.location.href = this.data.cartUrl;
    }

    /* ---------- theme editor ---------- */

    /**
     * Theme editor (V2-SPEC §2.18): a "Dio seta" block select shows its step (no focus, no history); a "Polje uz
     * proizvod" block select shows the step and the window of the first product with that field, in the edit look with
     * nothing added; a block deselect closes it; the section select shows the start screen.
     */
    initEditorEvents() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      this.editorHandler = (event) => this.onEditorBlockSelect(event);
      this.editorDeselectHandler = (event) => this.onEditorBlockDeselect(event);
      this.editorSectionHandler = (event) => {
        if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
        // a block select may follow at once: it wins
        window.clearTimeout(this.editorTimer);
        this.editorTimer = window.setTimeout(() => {
          if (this.sheet) this.cancelSheet({ focus: false });
          if (this.stepIndex !== 0) this.goToStep(0, { push: false, focus: false, scroll: false });
        }, 60);
      };
      document.addEventListener('shopify:block:select', this.editorHandler);
      document.addEventListener('shopify:block:deselect', this.editorDeselectHandler);
      document.addEventListener('shopify:section:select', this.editorSectionHandler);
    }

    onEditorBlockSelect(event) {
      if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
      window.clearTimeout(this.editorTimer);
      const { blockId } = event.detail;
      if (this.sheet) this.cancelSheet({ focus: false });
      const index = this.stepIndexOf(blockId);
      if (index !== -1) {
        this.goToStep(index, { push: false, focus: false, scroll: false });
        return;
      }
      // a "Polje uz proizvod" block: the first product that has the field
      const card = this.cards.find((item) => item.configEl && item.fields.some((field) => field.id === blockId));
      if (!card) return;
      const step = this.stepIndexOf(card.slotId);
      if (step !== -1) this.goToStep(step, { push: false, focus: false, scroll: false });
      this.openSheet(card, 'preview', card.addButton);
    }

    onEditorBlockDeselect(event) {
      if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
      if (this.sheet && this.sheet.mode === 'preview') this.cancelSheet({ focus: false });
    }
  }

  customElements.define('babyset-builder-v2', BabysetBuilder);
  // dev-test-19: the EasyFlow parser for tests (work/dev-test-19/qa/parser-test.js) and a look from the console
  window.BabysetEasyflow = { parse: parseEasyflow, hidden: efHiddenMap };
})();
